import React, { useState, useEffect, createContext, useContext, useCallback } from 'react';
import { supabase } from './supabase';

// ─────────────────────────────────────────────────────────
// THEME  (black / gold — CCR website style)
// ─────────────────────────────────────────────────────────
const C = {
  night:   '#0a0c10',
  void:    '#111318',
  surface: '#181b22',
  panel:   '#1f232d',
  border:  '#2a2f3d',
  borderB: '#363d52',
  text:    '#e8eaf0',
  muted:   '#8891aa',
  faint:   '#4a5168',
  gold:    '#c8973a',
  goldLt:  '#e2b054',
  goldDim: '#2a2010',
  amber:   '#e8730a',
  blue:    '#3a7bd5',
  blueLt:  '#5a9bf0',
  teal:    '#2ab8a5',
  red:     '#e84040',
  redDim:  '#3a1010',
  green:   '#40c870',
  purple:  '#8a5af0',
};

const STATUS_C = {
  'Lead':              { bg:'#1a1e2a', fg:C.muted   },
  'Estimate Sent':     { bg:'rgba(58,123,213,.18)', fg:C.blueLt  },
  'Contract Signed':   { bg:'rgba(138,90,240,.18)', fg:C.purple  },
  'Deposit Paid':      { bg:'rgba(200,151,58,.18)',  fg:C.gold    },
  'Scheduled':         { bg:'rgba(42,184,165,.18)',  fg:C.teal    },
  'In Progress':       { bg:'rgba(200,151,58,.18)',  fg:C.gold    },
  'Inspect Scheduled': { bg:'rgba(58,123,213,.18)',  fg:C.blueLt  },
  'Complete':          { bg:'rgba(64,200,112,.18)',  fg:C.green   },
  'Final Invoice Sent':{ bg:'rgba(58,123,213,.18)',  fg:C.blueLt  },
  'Paid - Closed':     { bg:'rgba(64,200,112,.18)',  fg:C.green   },
};

const LEAD_STATUS_C = {
  'New':                { bg:'rgba(58,123,213,.18)', fg:C.blueLt  },
  'Contacted':          { bg:'rgba(138,90,240,.18)', fg:C.purple  },
  'Inspect Scheduled':  { bg:'rgba(42,184,165,.18)', fg:C.teal    },
  'Estimate Scheduled': { bg:'rgba(200,151,58,.18)', fg:C.gold    },
  'Estimate Sent':      { bg:'rgba(42,184,165,.18)', fg:C.teal    },
  'Follow-Up':          { bg:'rgba(232,115,10,.18)', fg:C.amber   },
  'Won':                { bg:'rgba(64,200,112,.18)', fg:C.green   },
  'Non-Profitable':     { bg:'rgba(232,64,64,.18)',  fg:C.red     },
  'Lost':               { bg:'rgba(232,64,64,.18)',  fg:C.red     },
};

const LEAD_STATUSES = ['New','Contacted','Inspect Scheduled','Estimate Scheduled','Estimate Sent','Follow-Up','Won','Non-Profitable','Lost'];
const ROOF_TYPES    = ['Asphalt Shingle','Metal Roofing','Flat/TPO','Flat/EPDM','Tile','Slate','Cedar Shake'];
const JOB_STATUSES  = ['Lead','Estimate Sent','Contract Signed','Deposit Paid','Scheduled','In Progress','Inspect Scheduled','Complete','Final Invoice Sent','Paid - Closed'];

const MARKET_PRICES = {
  'Asphalt Shingle': { matCost:225, laborRate:51,  retail:460,  insurance:550  },
  'Metal Roofing':   { matCost:680, laborRate:280, retail:1800, insurance:2400 },
  'Flat/TPO':        { matCost:320, laborRate:120, retail:720,  insurance:1100 },
  'Flat/EPDM':       { matCost:280, laborRate:110, retail:650,  insurance:980  },
  'Tile':            { matCost:850, laborRate:380, retail:2200, insurance:3000 },
  'Slate':           { matCost:1200,laborRate:500, retail:3200, insurance:4200 },
  'Cedar Shake':     { matCost:620, laborRate:260, retail:1650, insurance:2200 },
};

// ─────────────────────────────────────────────────────────
// SEED DATA (used only when Supabase tables are empty)
// ─────────────────────────────────────────────────────────
const SEED_JOBS = [
  { id:'j1', num:'CCR-25-042', status:'In Progress',
    client:{ name:'Robert & Linda Thompson', email:'thompson@email.com', phone:'(770) 555-0182', address:'1420 Pine Ridge Dr, Kennesaw, GA 30152' },
    project:{ type:'Asphalt Shingle', sq:'28', pitch:'6/12', stories:'2', desc:'Full tear-off and replacement.', ins:true, claimNum:'CLM-2025-8842', jobType:'Insurance', insCo:'State Farm', adjName:'David Kim', adjEmail:'d.kim@statefarm.com', adjPhone:'(800) 555-0100' },
    est:14800, paid:7400, bal:7400, closed:false, crew:['Jose Ramirez','Dario Vega'], start:'2025-06-10', end:'2025-06-13',
    mats:[{ item:'GAF Timberline HDZ Shingles', qty:30, unit:'SQ', cost:210 },{ item:'Synthetic Underlayment 30lb', qty:30, unit:'Roll', cost:45 }],
    docs:{ estimate:true, contract:true, depositInvoice:true, finalInvoice:false, completeCert:false, lienRelease:false, warranty:false },
    notes:'Homeowner wants GAF Lifetime warranty.',
    timeline:[{ date:'2025-05-28', event:'Lead received' },{ date:'2025-06-03', event:'Deposit received — $7,400' }],
    photos:[], uploadedDocs:[], expenses:[], revenues:[], costEntries:[] },
  { id:'j2', num:'CCR-25-037', status:'Estimate Sent',
    client:{ name:'Greenfield HOA', email:'manager@greenfield.org', phone:'(404) 555-0299', address:'88 Greenfield Blvd, Marietta, GA 30064' },
    project:{ type:'Flat/TPO', sq:'120', pitch:'Flat', stories:'1', desc:'TPO flat roof replacement on clubhouse.', ins:false, claimNum:'', jobType:'Retail', insCo:'', adjName:'', adjEmail:'', adjPhone:'' },
    est:42500, paid:0, bal:42500, closed:false, crew:[], start:'', end:'', mats:[],
    docs:{ estimate:true, contract:false, depositInvoice:false, finalInvoice:false, completeCert:false, lienRelease:false, warranty:false },
    notes:'HOA board must vote to approve.',
    timeline:[{ date:'2025-06-05', event:'Estimate submitted — $42,500' }],
    photos:[], uploadedDocs:[], expenses:[], revenues:[], costEntries:[] },
];

const SEED_LEADS = [
  { id:'l1', createdAt:'2025-06-15', status:'New', source:'Referral', name:'Marcus & Diana Webb', email:'mwebb@email.com', phone:'(770) 555-0211', address:'832 Willow Creek Dr, Acworth, GA 30101', roofType:'Asphalt Shingle', notes:'Neighbor referral from Thompson job.', jobType:'Insurance', claimNum:'CLM-2025-9910', ins:true, insCo:'Allstate', adjName:'Sarah Chen', adjEmail:'s.chen@allstate.com', adjPhone:'(800) 555-0200', inspectionDone:false, contractSigned:false, photos:[], uploadedDocs:[] },
  { id:'l2', createdAt:'2025-06-18', status:'Contacted', source:'Website', name:'Patricia Nguyen', email:'p.nguyen@email.com', phone:'(404) 555-0388', address:'410 Oak Park Ln, Marietta, GA 30067', roofType:'Metal Roofing', notes:'Wants standing seam metal quote.', jobType:'Retail', claimNum:'', ins:false, insCo:'', adjName:'', adjEmail:'', adjPhone:'', inspectionDone:false, contractSigned:false, photos:[] },
];

const SEED_CONTRACTORS = [
  { id:'c1', name:'Jose Ramirez', phone:'(770) 555-0310', email:'jose@crew.com', ein:'12-3456789', coi:'COI-2025-JR-001', coiExpiry:'2026-01-01', payMethod:'ACH', bankLast4:'4421', specialty:'Shingle/Tearoff', active:true },
  { id:'c2', name:'Dario Vega',   phone:'(404) 555-0225', email:'dario@crew.com', ein:'98-7654321', coi:'COI-2025-DV-002', coiExpiry:'2025-12-01', payMethod:'ACH', bankLast4:'8810', specialty:'Flat/TPO', active:true },
  { id:'c3', name:'Tommy H.',     phone:'(678) 555-0188', email:'tommy@crew.com', ein:'55-1234567', coi:'COI-2025-TH-003', coiExpiry:'2026-03-01', payMethod:'ACH', bankLast4:'3390', specialty:'Metal', active:true },
];

const USERS = [
  { id:'u1', name:'Carlos Rivera', email:'admin@ccrooofing.com',  password:'Admin2025!',  role:'admin',  avatar:'CR', title:'CEO / Admin'       },
  { id:'u2', name:'Mike Torres',   email:'mike@ccrooofing.com',   password:'Member2025!', role:'member', avatar:'MT', title:'Project Manager'   },
  { id:'u4', name:'Ray Johnson',   email:'ray@rgjteam.com',       password:'Admin2025!',  role:'admin',  avatar:'RJ', title:'Administrator'     },
  { id:'u5', name:'Taressa Reed',  email:'taressareed@gmail.com', password:'Member2025!', role:'member', avatar:'TR', title:'Team Member'       },
];

// ─────────────────────────────────────────────────────────
// APP CONTEXT
// ─────────────────────────────────────────────────────────
const Ctx = createContext(null);
const useApp = () => useContext(Ctx);

function AppProvider({ children }) {
  const [user, setUser]               = useState(null);
  const [users, setUsers]             = useState([]);
  const [jobs, setJobs]               = useState([]);
  const [leads, setLeads]             = useState([]);
  const [contractors, setContractors] = useState([]);
  const [loading, setLoading]         = useState(true);
  const [dbReady, setDbReady]         = useState(false);
  const [toast, setToast]             = useState(null);

  const showToast = useCallback((msg, type='success') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3000);
  }, []);

  // ── Load from Supabase on mount ──────────────────────
  useEffect(() => {
    async function load() {
      setLoading(true);
      try {
        // Test connection
        const { error: testErr } = await supabase.from('jobs').select('id').limit(1);
        if (testErr) throw testErr;
        setDbReady(true);

        const [{ data: jData }, { data: lData }, { data: cData }, { data: uData }] = await Promise.all([
          supabase.from('jobs').select('*').order('created_at', { ascending: false }),
          supabase.from('leads').select('*').order('created_at', { ascending: false }),
          supabase.from('contractors').select('*').order('name'),
          supabase.from('users').select('*').order('name'),
        ]);

        // Seed if empty
        if (!jData?.length) {
          await supabase.from('jobs').insert(SEED_JOBS.map(j => ({ ...j, data: j })));
          setJobs(SEED_JOBS);
        } else {
          setJobs(jData.map(r => r.data || r));
        }
        if (!lData?.length) {
          await supabase.from('leads').insert(SEED_LEADS.map(l => ({ ...l, data: l })));
          setLeads(SEED_LEADS);
        } else {
          setLeads(lData.map(r => r.data || r));
        }
        if (!cData?.length) {
          await supabase.from('contractors').insert(SEED_CONTRACTORS.map(c => ({ ...c, data: c })));
          setContractors(SEED_CONTRACTORS);
        } else {
          setContractors(cData.map(r => r.data || r));
        }
        if (!uData?.length) {
          const seedUsers = USERS.map(u => ({ ...u }));
          await supabase.from('users').insert(seedUsers);
          setUsers(seedUsers);
        } else {
          setUsers(uData);
        }
      } catch (e) {
        console.warn('Supabase not connected — running in local mode:', e.message);
        setJobs(SEED_JOBS);
        setLeads(SEED_LEADS);
        setContractors(SEED_CONTRACTORS);
        setUsers(USERS);
        setDbReady(false);
      }
      setLoading(false);
    }
    load();
  }, []);

  // ── Real-time subscriptions ──────────────────────────
  useEffect(() => {
    if (!dbReady) return;
    const ch = supabase
      .channel('ccr-realtime')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'jobs' }, payload => {
        const extract = r => ({ ...(r.data || r), id: r.id });
        if (payload.eventType === 'INSERT') setJobs(j => {
          const nj = extract(payload.new);
          return j.find(x=>x.id===nj.id) ? j : [nj, ...j];
        });
        if (payload.eventType === 'UPDATE') setJobs(j => j.map(x => x.id === payload.new.id ? extract(payload.new) : x));
        if (payload.eventType === 'DELETE') setJobs(j => j.filter(x => x.id !== payload.old.id));
      })
      .on('postgres_changes', { event: '*', schema: 'public', table: 'leads' }, payload => {
        const extract = r => ({ ...(r.data || r), id: r.id });
        if (payload.eventType === 'INSERT') setLeads(l => {
          const nl = extract(payload.new);
          return l.find(x=>x.id===nl.id) ? l : [nl, ...l];
        });
        if (payload.eventType === 'UPDATE') setLeads(l => l.map(x => x.id === payload.new.id ? extract(payload.new) : x));
        if (payload.eventType === 'DELETE') setLeads(l => l.filter(x => x.id !== payload.old.id));
      })
      .on('postgres_changes', { event: '*', schema: 'public', table: 'contractors' }, payload => {
        const extract = r => ({ ...(r.data || r), id: r.id });
        if (payload.eventType === 'INSERT') setContractors(c => {
          const nc = extract(payload.new);
          return c.find(x=>x.id===nc.id) ? c : [...c, nc];
        });
        if (payload.eventType === 'UPDATE') setContractors(c => c.map(x => x.id === payload.new.id ? extract(payload.new) : x));
        if (payload.eventType === 'DELETE') setContractors(c => c.filter(x => x.id !== payload.old.id));
      })
      .on('postgres_changes', { event: '*', schema: 'public', table: 'users' }, payload => {
        if (payload.eventType === 'INSERT') setUsers(u => [...u, payload.new]);
        if (payload.eventType === 'UPDATE') setUsers(u => u.map(x => x.id === payload.new.id ? payload.new : x));
        if (payload.eventType === 'DELETE') setUsers(u => u.filter(x => x.id !== payload.old.id));
      })
      .subscribe();
    return () => supabase.removeChannel(ch);
  }, [dbReady]);

  // ── CRUD helpers ─────────────────────────────────────
  // Strip base64 file data before saving to Supabase (files stored in Storage, not DB)
  function stripFiles(obj) {
    if (!obj) return obj;
    const cleaned = { ...obj };
    if (cleaned.photos) {
      cleaned.photos = cleaned.photos.map(p => ({
        id: p.id, name: p.name, size: p.size, addedAt: p.addedAt,
        url: p.url || null,
        uri: p.url || null,
      }));
    }
    if (cleaned.uploadedDocs) {
      cleaned.uploadedDocs = cleaned.uploadedDocs.map(d => ({
        id: d.id, name: d.name, size: d.size, mimeType: d.mimeType,
        addedAt: d.addedAt, linkedTo: d.linkedTo || null,
        url: d.url || null,
        uri: d.url || null,
      }));
    }
    return cleaned;
  }

  async function persist(table, id, data) {
    if (!dbReady) return;
    const clean = (table === 'jobs' || table === 'leads') ? stripFiles(data) : data;
    const { error } = await supabase.from(table).upsert({ id, data: clean, updated_at: new Date().toISOString() });
    if (error) console.error('Persist error:', table, error.message);
  }

  async function uploadToStorage(bucket, path, file) {
    try {
      const { data, error } = await supabase.storage.from(bucket).upload(path, file, {
        upsert: true, contentType: file.type || 'application/octet-stream'
      });
      if (error) throw error;
      const { data: urlData } = supabase.storage.from(bucket).getPublicUrl(path);
      return urlData.publicUrl;
    } catch(e) {
      console.error('Storage upload failed:', e.message);
      return null;
    }
  }

  function autoTimeline(prev, next) {
    // Returns extra timeline entries to append for meaningful changes
    const entries = [...(next.timeline||[])];
    const addAuto = (event) => {
      const today = new Date().toISOString().slice(0,10);
      // Don't duplicate same event on same day
      if (!entries.some(e => e.date===today && e.event===event)) {
        entries.push({ date:today, event, auto:true });
      }
    };
    if (prev && next) {
      // Status change
      if (prev.status !== next.status)
        addAuto(`Status changed: ${prev.status} → ${next.status}`);
      // Crew assigned
      const newCrew = (next.crew||[]).filter(c=>!(prev.crew||[]).includes(c));
      newCrew.forEach(c => addAuto(`Crew assigned: ${c}`));
      // Schedule set
      if (!prev.start && next.start) addAuto(`Project scheduled: ${next.start}${next.end?' – '+next.end:''}`);
      // Docs completed
      if (next.docs) {
        const DNAMES = {estimate:'Estimate',contract:'Contract/Agreement',depositInvoice:'Deposit Invoice',
          finalInvoice:'Final Invoice',completeCert:'Completion Certificate',lienRelease:'Lien Release',warranty:'Warranty'};
        Object.entries(DNAMES).forEach(([k,name]) => {
          if (!prev.docs?.[k] && next.docs[k]) addAuto(`Document completed: ${name}`);
        });
      }
      // Revenue/payment
      const prevRevLen = (prev.revenues||[]).length;
      const nextRevLen = (next.revenues||[]).length;
      if (nextRevLen > prevRevLen) {
        const newRev = next.revenues[nextRevLen-1];
        if (newRev) addAuto(`Payment received: ${newRev.category} — $${parseFloat(newRev.amount||0).toLocaleString()}`);
      }
    }
    return { ...next, timeline:entries };
  }

  const updateJob = useCallback(async (j, prevOverride) => {
    const prev = prevOverride || jobs.find(x=>x.id===j.id);
    const enriched = autoTimeline(prev, j);
    setJobs(js => js.map(x => x.id === enriched.id ? enriched : x));
    await persist('jobs', enriched.id, enriched);
    showToast('Job saved');
  }, [dbReady, jobs]);

  const addJob = useCallback(async d => {
    const j = {
      id: 'j' + Date.now(),
      num: `CCR-${new Date().getFullYear().toString().slice(-2)}-${String(100+Math.floor(Math.random()*899))}`,
      status: 'Lead',
      client: { name:d.name||'', email:d.email||'', phone:d.phone||'', address:d.address||'' },
      project: { type:d.roofType||'', sq:d.sq||'', pitch:d.pitch||'', stories:d.stories||'1', desc:d.desc||'', ins:d.ins||false, claimNum:d.claimNum||'', jobType:d.jobType||'Retail', insCo:d.insCo||'', adjName:d.adjName||'', adjEmail:d.adjEmail||'', adjPhone:d.adjPhone||'' },
      est: parseFloat(d.est)||0, paid:0, bal:parseFloat(d.est)||0, closed:false,
      crew:[], start:'', end:'', mats:[],
      docs:{ estimate:false, contract:false, depositInvoice:false, finalInvoice:false, completeCert:false, lienRelease:false, warranty:false },
      notes:d.notes||'', timeline:[{ date:new Date().toISOString().slice(0,10), event:'Job created' }],
      photos:[], uploadedDocs:[], expenses:[], revenues:[], costEntries:[],
    };
    setJobs(js => [j, ...js]);
    if (dbReady) await supabase.from('jobs').insert({ id:j.id, data:j });
    showToast('Job created');
  }, [dbReady]);

  const addLead = useCallback(async d => {
    const l = {
      id: 'l' + Date.now(),
      createdAt: new Date().toISOString().slice(0,10),
      status:'New', source:d.source||'', name:d.name||'', email:d.email||'',
      phone:d.phone||'', address:d.address||'', roofType:d.roofType||'',
      notes:d.notes||'', jobType:d.jobType||'Retail', claimNum:d.claimNum||'',
      ins:d.ins||false, insCo:d.insCo||'', adjName:d.adjName||'',
      adjEmail:d.adjEmail||'', adjPhone:d.adjPhone||'',
      inspectionDone:false, contractSigned:false, photos:[], uploadedDocs:[],
    };
    setLeads(ls => [l, ...ls]);
    if (dbReady) await supabase.from('leads').insert({ id:l.id, data:l });
    showToast('Lead added');
  }, [dbReady]);

  const updateLead = useCallback(async l => {
    setLeads(ls => ls.map(x => x.id === l.id ? l : x));
    await persist('leads', l.id, l);
    showToast('Lead saved');
  }, [dbReady]);

  const deleteLead = useCallback(async id => {
    setLeads(ls => ls.filter(x => x.id !== id));
    if (dbReady) await supabase.from('leads').delete().eq('id', id);
    showToast('Lead deleted', 'info');
  }, [dbReady]);

  const convertLeadToJob = useCallback(async lead => {
    const j = {
      id: 'j' + Date.now(),
      num: `CCR-${new Date().getFullYear().toString().slice(-2)}-${String(100+Math.floor(Math.random()*899))}`,
      status: 'Estimate Sent',
      client: { name:lead.name, email:lead.email, phone:lead.phone, address:lead.address },
      project: { type:lead.roofType, sq:'', pitch:'', stories:'1', desc:lead.notes||'', ins:lead.ins||false, claimNum:lead.claimNum||'', jobType:lead.jobType||'Retail', insCo:lead.insCo||'', adjName:lead.adjName||'', adjEmail:lead.adjEmail||'', adjPhone:lead.adjPhone||'' },
      est:0, paid:0, bal:0, closed:false, crew:[], start:'', end:'', mats:[],
      docs:{ estimate:false, contract:false, depositInvoice:false, finalInvoice:false, completeCert:false, lienRelease:false, warranty:false },
      notes:lead.notes||'',
      timeline:[{ date:new Date().toISOString().slice(0,10), event:`Converted from lead — ${lead.name}` }],
      photos:[], uploadedDocs:[], expenses:[], revenues:[], costEntries:[],
    };
    setJobs(js => [j, ...js]);
    setLeads(ls => ls.filter(x => x.id !== lead.id));
    if (dbReady) {
      await supabase.from('jobs').insert({ id:j.id, data:j });
      await supabase.from('leads').delete().eq('id', lead.id);
    }
    showToast(`${lead.name} converted to job`);
    return j;
  }, [dbReady]);

  const addContractor = useCallback(async c => {
    const nc = { ...c, id:'c'+Date.now(), active:true };
    setContractors(cs => [nc, ...cs]);
    if (dbReady) await supabase.from('contractors').insert({ id:nc.id, data:nc, updated_at: new Date().toISOString() });
    showToast('Contractor added');
  }, [dbReady]);

  const updateContractor = useCallback(async c => {
    setContractors(cs => cs.map(x => x.id === c.id ? c : x));
    if (dbReady) await supabase.from('contractors').upsert({ id:c.id, data:c, updated_at: new Date().toISOString() });
    showToast('Contractor saved');
  }, [dbReady]);

  const deleteContractor = useCallback(async id => {
    setContractors(cs => cs.filter(x => x.id !== id));
    if (dbReady) await supabase.from('contractors').delete().eq('id', id);
  }, [dbReady]);

  const login = (email, pass) => {
    const list = users.length ? users : USERS;
    const u = list.find(x => x.email.toLowerCase() === email.trim().toLowerCase() && x.password === pass && x.active !== false);
    if (!u) throw new Error('Invalid email or password');
    setUser(u);
    return u;
  };

  const addUser = async d => {
    const nu = { id:'u'+Date.now(), name:d.name, email:d.email, password:d.password||'Welcome2025!',
                 role:d.role||'member', avatar:d.name.split(' ').map(w=>w[0]).join('').slice(0,2).toUpperCase(),
                 title:d.title||'', active:true };
    setUsers(us => [...us, nu]);
    if (dbReady) await supabase.from('users').insert(nu);
    showToast('User added — temporary password: ' + nu.password, 'success');
    return nu;
  };

  const updateUser = async u => {
    setUsers(us => us.map(x => x.id===u.id ? u : x));
    if (user?.id === u.id) setUser(u);
    if (dbReady) await supabase.from('users').upsert({ ...u, updated_at: new Date().toISOString() });
    showToast('User updated');
  };

  const deleteUser = async id => {
    setUsers(us => us.filter(x => x.id!==id));
    if (dbReady) await supabase.from('users').delete().eq('id', id);
    showToast('User removed', 'info');
  };
  const logout = () => setUser(null);

  return (
    <Ctx.Provider value={{ user, users, jobs, leads, contractors, loading, dbReady, toast, showToast,
      login, logout, addUser, updateUser, deleteUser, updateJob, addJob, addLead, updateLead, deleteLead, convertLeadToJob,
      addContractor, updateContractor, deleteContractor }}>
      {children}
    </Ctx.Provider>
  );
}

// ─────────────────────────────────────────────────────────
// SHARED UI COMPONENTS
// ─────────────────────────────────────────────────────────
const s = {
  // Layout
  page:    { minHeight:'100vh', background:C.night, color:C.text, fontFamily:'-apple-system,"Helvetica Neue",Arial,sans-serif' },
  card:    { background:C.surface, border:`1px solid ${C.border}`, borderRadius:14, padding:'18px 20px', marginBottom:14,
             boxShadow:'0 4px 20px rgba(0,0,0,.35)' },
  row:     { display:'flex', alignItems:'center', justifyContent:'space-between', gap:12 },
  col:     { display:'flex', flexDirection:'column', gap:8 },
  wrap:    { maxWidth:1200, margin:'0 auto', padding:'0 20px' },
  // Typography
  h1:      { fontSize:24, fontWeight:900, color:C.text, margin:0 },
  h2:      { fontSize:18, fontWeight:800, color:C.text, margin:0 },
  label:   { fontSize:11, fontWeight:700, color:C.muted, textTransform:'uppercase', letterSpacing:1, marginBottom:6, display:'block' },
  mono:    { fontFamily:'monospace', fontSize:11, color:C.gold },
  // Input
  input:   { background:C.panel, border:`1.5px solid ${C.border}`, borderRadius:10, padding:'10px 14px', color:C.text,
             fontSize:14, outline:'none', width:'100%', transition:'border-color .15s' },
  textarea:{ background:C.panel, border:`1.5px solid ${C.border}`, borderRadius:10, padding:'10px 14px', color:C.text,
             fontSize:14, outline:'none', width:'100%', resize:'vertical', minHeight:80, fontFamily:'inherit' },
  select:  { background:C.panel, border:`1.5px solid ${C.border}`, borderRadius:10, padding:'10px 14px', color:C.text,
             fontSize:14, outline:'none', width:'100%', cursor:'pointer' },
};

function Btn({ label, onClick, v='primary', size='md', disabled, style, fullWidth }) {
  const variants = {
    primary: { background:C.gold, color:C.night, border:'none' },
    solid:   { background:C.gold, color:C.night, border:'none' },
    outline: { background:'transparent', color:C.text, border:`1.5px solid ${C.borderB}` },
    ghost:   { background:'transparent', color:C.muted, border:'none' },
    danger:  { background:C.redDim, color:C.red, border:`1.5px solid rgba(232,64,64,.3)` },
    blue:    { background:'rgba(58,123,213,.15)', color:C.blueLt, border:`1.5px solid rgba(58,123,213,.3)` },
    teal:    { background:'rgba(42,184,165,.15)', color:C.teal, border:`1.5px solid rgba(42,184,165,.3)` },
  };
  const sizes = { sm:{ padding:'6px 14px', fontSize:12 }, md:{ padding:'10px 20px', fontSize:14 }, lg:{ padding:'13px 28px', fontSize:15 } };
  const v2 = variants[v] || variants.primary;
  const sz = sizes[size] || sizes.md;
  return (
    <button onClick={onClick} disabled={disabled}
      style={{ ...v2, ...sz, borderRadius:10, fontWeight:700, cursor:disabled?'not-allowed':'pointer',
               opacity:disabled?.5:1, transition:'all .15s', display:'inline-flex', alignItems:'center',
               justifyContent:'center', gap:8, width:fullWidth?'100%':undefined, ...style }}
      onMouseEnter={e => { if (!disabled) e.currentTarget.style.filter='brightness(1.12)'; }}
      onMouseLeave={e => { e.currentTarget.style.filter=''; }}>
      {label}
    </button>
  );
}

function Card({ children, style, onClick }) {
  return (
    <div style={{ ...s.card, ...style, cursor:onClick?'pointer':undefined }}
      onClick={onClick}
      onMouseEnter={e => { if (onClick) e.currentTarget.style.borderColor=C.borderB; }}
      onMouseLeave={e => { if (onClick) e.currentTarget.style.borderColor=C.border; }}>
      {children}
    </div>
  );
}

function Badge({ status, colors=STATUS_C }) {
  const sc = colors[status] || { bg:C.panel, fg:C.muted };
  return (
    <span style={{ background:sc.bg, color:sc.fg, borderRadius:999, padding:'4px 12px',
                   fontSize:11, fontWeight:700, whiteSpace:'nowrap' }}>
      {status}
    </span>
  );
}

function Field({ label, value, onChange, type='text', placeholder, multi, options, required }) {
  const baseStyle = { ...s.input, borderColor:C.border };
  return (
    <div style={{ marginBottom:16 }}>
      <label style={s.label}>{label}{required&&<span style={{ color:C.red }}> *</span>}</label>
      {options ? (
        <select value={value} onChange={e=>onChange(e.target.value)} style={s.select}>
          <option value="">— Select —</option>
          {options.map(o => <option key={o} value={o}>{o}</option>)}
        </select>
      ) : multi ? (
        <textarea value={value} onChange={e=>onChange(e.target.value)} placeholder={placeholder} style={s.textarea} />
      ) : (
        <input type={type} value={value||''} onChange={e=>onChange(e.target.value)}
          placeholder={placeholder} style={baseStyle} required={required} />
      )}
    </div>
  );
}

function Toggle({ label, checked, onChange }) {
  return (
    <label style={{ display:'flex', alignItems:'center', gap:12, cursor:'pointer', padding:'12px 14px',
                    background:checked?'rgba(200,151,58,.1)':C.panel, borderRadius:10,
                    border:`1.5px solid ${checked?C.gold:C.border}`, transition:'all .15s', marginBottom:12 }}>
      <div style={{ position:'relative', width:40, height:22, flexShrink:0 }}>
        <div style={{ position:'absolute', inset:0, background:checked?C.gold:C.border, borderRadius:11, transition:'background .15s' }} />
        <div style={{ position:'absolute', top:3, left:checked?20:3, width:16, height:16, background:'white', borderRadius:8, transition:'left .15s', boxShadow:'0 1px 3px rgba(0,0,0,.3)' }} />
        <input type="checkbox" checked={checked} onChange={e=>onChange(e.target.checked)} style={{ opacity:0, position:'absolute', inset:0, cursor:'pointer' }} />
      </div>
      <span style={{ fontWeight:600, color:checked?C.gold:C.text, fontSize:14 }}>{label}</span>
    </label>
  );
}

function Bar({ val, color=C.gold, h=6 }) {
  return (
    <div style={{ background:C.panel, borderRadius:h, height:h, overflow:'hidden' }}>
      <div style={{ width:`${Math.min(val,100)}%`, height:'100%', background:color, borderRadius:h, transition:'width .4s' }} />
    </div>
  );
}

function Stat({ label, value, color=C.gold }) {
  return (
    <div style={{ flex:1, background:C.surface, borderRadius:12, padding:'16px 18px',
                  border:`1px solid ${C.border}`, borderTop:`3px solid ${color}`, boxShadow:'0 2px 12px rgba(0,0,0,.25)' }}>
      <div style={{ fontSize:11, fontWeight:700, color:C.muted, textTransform:'uppercase', letterSpacing:.8, marginBottom:8 }}>{label}</div>
      <div style={{ fontSize:24, fontWeight:900, color }}>{value}</div>
    </div>
  );
}

function Modal({ open, onClose, title, children, width=560 }) {
  if (!open) return null;
  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,.75)', zIndex:1000,
                  display:'flex', alignItems:'center', justifyContent:'center', padding:20 }}
      onClick={e => { if (e.target===e.currentTarget) onClose(); }}>
      <div style={{ background:C.surface, borderRadius:18, padding:'28px 32px', width:'100%', maxWidth:width,
                    border:`1px solid ${C.borderB}`, maxHeight:'90vh', overflowY:'auto',
                    boxShadow:'0 32px 80px rgba(0,0,0,.6)' }}>
        <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:24 }}>
          <h2 style={{ fontSize:22, fontWeight:900, color:C.gold }}>{title}</h2>
          <button onClick={onClose} style={{ background:'none', border:'none', color:C.muted, fontSize:22, cursor:'pointer', lineHeight:1 }}>✕</button>
        </div>
        {children}
      </div>
    </div>
  );
}

function Toast({ toast }) {
  if (!toast) return null;
  const bg = toast.type==='error' ? C.red : toast.type==='info' ? C.blueLt : C.green;
  return (
    <div style={{ position:'fixed', bottom:28, right:28, background:bg, color:'white', padding:'12px 22px',
                  borderRadius:12, fontWeight:700, fontSize:14, zIndex:9999, boxShadow:'0 8px 32px rgba(0,0,0,.4)',
                  animation:'slideUp .25s ease' }}>
      {toast.msg}
    </div>
  );
}

function Confirm({ open, title, message, onConfirm, onCancel, danger }) {
  if (!open) return null;
  return (
    <Modal open title={title} onClose={onCancel} width={420}>
      <p style={{ color:C.muted, marginBottom:24, lineHeight:1.6 }}>{message}</p>
      <div style={{ display:'flex', gap:12, justifyContent:'flex-end' }}>
        <Btn label="Cancel" v="ghost" onClick={onCancel} />
        <Btn label="Confirm" v={danger?'danger':'primary'} onClick={onConfirm} />
      </div>
    </Modal>
  );
}

// Chip selector (multi button toggle)
function Chips({ options, value, onChange, multi }) {
  return (
    <div style={{ display:'flex', flexWrap:'wrap', gap:8, marginBottom:16 }}>
      {options.map(o => {
        const sel = multi ? (value||[]).includes(o) : value===o;
        return (
          <button key={o} onClick={() => {
            if (multi) {
              const cur = value||[];
              onChange(sel ? cur.filter(x=>x!==o) : [...cur, o]);
            } else onChange(o);
          }} style={{ background:sel?'rgba(200,151,58,.18)':C.panel, color:sel?C.gold:C.muted,
                      border:`1.5px solid ${sel?C.gold:C.border}`, borderRadius:8, padding:'7px 14px',
                      fontSize:12, fontWeight:700, cursor:'pointer', transition:'all .15s' }}>
            {o}
          </button>
        );
      })}
    </div>
  );
}

// ─────────────────────────────────────────────────────────
// SIDEBAR NAV
// ─────────────────────────────────────────────────────────
function Sidebar({ active, setActive, push }) {
  const { user, logout, dbReady } = useApp();
  const isAdmin = user?.role === 'admin';

  const navItems = [
    { key:'dash',        label:'Dashboard',          icon:'📊' },
    { key:'jobs',        label:'Jobs',               icon:'🏠' },
    { key:'leads',       label:'Leads',              icon:'🎯' },
    { key:'sched',       label:'Schedule',           icon:'📅' },
    { key:'pa',          label:'Property Analysis',  icon:'📋' },
    ...(isAdmin ? [{ key:'pay',   label:'Payments',  icon:'💳' }] : []),
    ...(isAdmin ? [{ key:'crew',  label:'Crew',      icon:'👷' }] : []),
    ...(isAdmin ? [{ key:'users', label:'Users',     icon:'👥' }] : []),
    { key:'prof',        label:'Profile',            icon:'👤' },
  ];

  return (
    <div style={{ width:220, minHeight:'100vh', background:C.void, borderRight:`1px solid ${C.border}`,
                  display:'flex', flexDirection:'column', position:'sticky', top:0, flexShrink:0 }}>
      {/* Logo */}
      <div style={{ padding:'20px 16px 18px', borderBottom:`1px solid ${C.border}` }}>
        <div style={{ display:'flex', alignItems:'center', justifyContent:'center' }}>
          <img src="/logo.png" alt="Capricorn Capital Roofing"
            style={{ width:120, height:'auto', filter:'drop-shadow(0 2px 8px rgba(200,151,58,.25))' }} />
        </div>
        {/* DB status */}
        <div style={{ marginTop:12, display:'flex', alignItems:'center', gap:6 }}>
          <div style={{ width:6, height:6, borderRadius:3, background:dbReady?C.green:C.amber }} />
          <span style={{ fontSize:10, color:dbReady?C.green:C.amber, fontWeight:600 }}>
            {dbReady ? 'Live · Supabase' : 'Local mode'}
          </span>
        </div>
      </div>

      {/* Nav */}
      <nav style={{ flex:1, padding:'12px 10px' }}>
        {navItems.map(item => (
          <button key={item.key} onClick={() => setActive(item.key)}
            style={{ display:'flex', alignItems:'center', gap:12, width:'100%', padding:'10px 12px',
                     borderRadius:10, border:'none', cursor:'pointer', marginBottom:2, textAlign:'left',
                     background:active===item.key?`rgba(200,151,58,.12)`:'transparent',
                     color:active===item.key?C.gold:C.muted, fontWeight:active===item.key?700:500,
                     fontSize:13, transition:'all .15s' }}
            onMouseEnter={e => { if (active!==item.key) e.currentTarget.style.background='rgba(255,255,255,.04)'; }}
            onMouseLeave={e => { if (active!==item.key) e.currentTarget.style.background='transparent'; }}>
            <span style={{ fontSize:16 }}>{item.icon}</span>
            {item.label}
            {active===item.key && <div style={{ marginLeft:'auto', width:3, height:16, background:C.gold, borderRadius:2 }} />}
          </button>
        ))}

        {/* E&A button */}
        {isAdmin && (
          <button onClick={() => push('ea')}
            style={{ display:'flex', alignItems:'center', gap:12, width:'100%', padding:'10px 12px',
                     borderRadius:10, border:`1px solid rgba(200,151,58,.25)`, cursor:'pointer', marginTop:12,
                     background:'rgba(200,151,58,.08)', color:C.gold, fontWeight:700, fontSize:13 }}>
            <span>📊</span> E&amp;A Tool
          </button>
        )}
      </nav>

      {/* User */}
      <div style={{ padding:'16px 20px', borderTop:`1px solid ${C.border}` }}>
        <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:12 }}>
          <div style={{ width:34, height:34, borderRadius:17, background:`linear-gradient(135deg,${C.gold},${C.amber})`,
                        display:'flex', alignItems:'center', justifyContent:'center', fontWeight:800, fontSize:12, color:C.night }}>
            {user?.avatar}
          </div>
          <div>
            <div style={{ fontWeight:700, fontSize:13, color:C.text }}>{user?.name}</div>
            <div style={{ fontSize:10, color:C.muted, textTransform:'capitalize' }}>{user?.role}</div>
          </div>
        </div>
        <Btn label="Sign Out" v="ghost" onClick={logout} size="sm" style={{ width:'100%', justifyContent:'center' }} />
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────
// DASHBOARD
// ─────────────────────────────────────────────────────────
function DashScreen({ push }) {
  const { user, jobs, leads } = useApp();
  const isAdmin = user?.role==='admin';
  const activeJobs = jobs.filter(j => !j.closed);
  const totRev  = jobs.reduce((a,j) => a+j.paid, 0);
  const totBal  = jobs.reduce((a,j) => a+j.bal, 0);
  const inProg  = jobs.filter(j => j.status==='In Progress').length;
  const actLeads = leads.filter(l => !['Won','Lost'].includes(l.status)).length;

  const recentJobs  = activeJobs.slice(0,5);
  const recentLeads = leads.filter(l=>!['Won','Lost'].includes(l.status)).slice(0,4);

  return (
    <div style={{ padding:'32px 0' }}>
      <div style={{ marginBottom:28 }}>
        <div style={{ fontSize:12, color:C.muted, marginBottom:4 }}>Welcome back,</div>
        <h1 style={{ ...s.h1, fontSize:28 }}>{user?.name}</h1>
      </div>

      {/* Stats */}
      {(() => {
        const totalMatCost  = jobs.reduce((a,j)=>(j.costEntries||[]).filter(e=>['Materials','Equipment','Permit','Disposal'].includes(e.type)).reduce((b,e)=>b+parseFloat(e.amount||0),a),0);
        const totalLabCost  = jobs.reduce((a,j)=>(j.costEntries||[]).filter(e=>['Labor','Subcontractor','Overhead'].includes(e.type)).reduce((b,e)=>b+parseFloat(e.amount||0),a),0);
        const totalJobCost  = totalMatCost + totalLabCost;
        const grossProfit   = totRev - totalJobCost;
        const grossMargin   = totRev > 0 ? (grossProfit/totRev*100).toFixed(1) : '0';
        return (
          <>
            <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:12, marginBottom:12 }}>
              {isAdmin && <Stat label="Revenue YTD"   value={`$${(totRev/1000).toFixed(1)}K`}  color={C.gold} />}
              {isAdmin && <Stat label="Open Pipeline" value={`$${(totBal/1000).toFixed(1)}K`}  color={C.blueLt} />}
              <Stat label="In Progress"  value={inProg}    color={C.teal} />
              <Stat label="Active Leads" value={actLeads}  color={C.purple} />
            </div>
            {isAdmin && (
              <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:12, marginBottom:28 }}>
                <Stat label="Material Cost"  value={`$${(totalMatCost/1000).toFixed(1)}K`} color={C.amber} />
                <Stat label="Labor Cost"     value={`$${(totalLabCost/1000).toFixed(1)}K`} color={C.muted} />
                <Stat label="Gross Profit"   value={`$${(grossProfit/1000).toFixed(1)}K`}  color={grossProfit>=0?C.green:C.red} />
                <Stat label="Gross Margin"   value={`${grossMargin}%`} color={parseFloat(grossMargin)>=40?C.green:parseFloat(grossMargin)>=20?C.gold:C.red} />
              </div>
            )}
          </>
        );
      })()}

      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:20 }}>
        {/* Recent Jobs */}
        <div>
          <div style={{ ...s.row, marginBottom:14 }}>
            <h2 style={s.h2}>Recent Jobs</h2>
            <Btn label="View All →" v="ghost" size="sm" onClick={() => push('jobs')} />
          </div>
          {recentJobs.map(job => (
            <Card key={job.id} onClick={() => push('jobDetail', { jobId:job.id })} style={{ marginBottom:10 }}>
              <div style={s.row}>
                <div>
                  <div style={s.mono}>{job.num}</div>
                  <div style={{ fontWeight:700, marginTop:4 }}>{job.client.name}</div>
                  <div style={{ fontSize:12, color:C.muted, marginTop:2 }}>{job.project.type} · {job.project.sq} SQ</div>
                </div>
                <div style={{ textAlign:'right' }}>
                  <Badge status={job.status} />
                  {isAdmin && <div style={{ fontWeight:800, color:C.gold, marginTop:8 }}>${job.est.toLocaleString()}</div>}
                </div>
              </div>
            </Card>
          ))}
        </div>

        {/* Lead Pipeline */}
        <div>
          <div style={{ ...s.row, marginBottom:14 }}>
            <h2 style={s.h2}>Lead Pipeline</h2>
            <Btn label="View All →" v="ghost" size="sm" onClick={() => push('leads')} />
          </div>
          {recentLeads.map(lead => {
            const sc = LEAD_STATUS_C[lead.status]||{ bg:C.panel, fg:C.muted };
            return (
              <Card key={lead.id} onClick={() => push('leadDetail', { leadId:lead.id })} style={{ marginBottom:10 }}>
                <div style={s.row}>
                  <div>
                    <div style={{ fontWeight:700 }}>{lead.name}</div>
                    <div style={{ fontSize:12, color:C.muted, marginTop:2 }}>{lead.roofType} · {lead.source}</div>
                    <div style={{ fontSize:11, color:C.faint, marginTop:3 }}>📍 {lead.address}</div>
                  </div>
                  <div style={{ textAlign:'right' }}>
                    <span style={{ background:sc.bg, color:sc.fg, borderRadius:999, padding:'3px 10px', fontSize:11, fontWeight:700 }}>{lead.status}</span>
                    <div style={{ fontSize:11, color:lead.jobType==='Insurance'?C.blueLt:C.gold, marginTop:6, fontWeight:700 }}>
                      {lead.jobType==='Insurance'?'🏛 Insurance':'🛒 Retail'}
                    </div>
                  </div>
                </div>
              </Card>
            );
          })}
          {isAdmin && (
            <button onClick={() => push('ea')}
              style={{ width:'100%', marginTop:8, background:'rgba(200,151,58,.08)', border:`1px solid rgba(200,151,58,.25)`,
                       borderRadius:12, padding:'14px 20px', cursor:'pointer', display:'flex', alignItems:'center', gap:12, color:C.gold }}>
              <span style={{ fontSize:22 }}>📊</span>
              <div style={{ textAlign:'left' }}>
                <div style={{ fontWeight:800, fontSize:13 }}>Estimate &amp; Analysis Tool</div>
                <div style={{ fontSize:11, color:C.muted }}>Market pricing · Profitability · PDF export</div>
              </div>
              <span style={{ marginLeft:'auto', fontSize:18 }}>›</span>
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────
// JOBS SCREEN
// ─────────────────────────────────────────────────────────
function JobsScreen({ push }) {
  const { user, jobs } = useApp();
  const isAdmin = user?.role==='admin';
  const [q, setQ]   = useState('');
  const [fil, setFil] = useState('all');

  const shown = jobs
    .filter(j => !j.closed || fil==='Paid - Closed')
    .filter(j => fil==='all' || j.status===fil)
    .filter(j => !q || j.client.name.toLowerCase().includes(q.toLowerCase()) || j.num.toLowerCase().includes(q.toLowerCase()));

  return (
    <div style={{ padding:'32px 0' }}>
      <div style={{ ...s.row, marginBottom:24 }}>
        <h1 style={s.h1}>Jobs <span style={{ fontSize:16, color:C.muted, fontWeight:400 }}>({shown.length})</span></h1>
      </div>

      {/* Filters */}
      <div style={{ display:'flex', gap:12, marginBottom:20, flexWrap:'wrap', alignItems:'center' }}>
        <input value={q} onChange={e=>setQ(e.target.value)} placeholder="Search jobs…"
          style={{ ...s.input, maxWidth:260 }} />
        <div style={{ display:'flex', gap:8, flexWrap:'wrap' }}>
          {['all','In Progress','Estimate Sent','Deposit Paid','Scheduled','Paid - Closed'].map(f => (
            <button key={f} onClick={()=>setFil(f)}
              style={{ background:fil===f?C.gold:C.panel, color:fil===f?C.night:C.muted,
                       border:`1px solid ${fil===f?C.gold:C.border}`, borderRadius:999, padding:'6px 16px',
                       fontSize:12, fontWeight:700, cursor:'pointer' }}>
              {f==='all'?'All':f}
            </button>
          ))}
        </div>
      </div>

      {shown.length===0 && (
        <div style={{ textAlign:'center', padding:'60px 0', color:C.muted }}>
          <div style={{ fontSize:48, marginBottom:12 }}>🏠</div>
          <div>No jobs found</div>
        </div>
      )}

      <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(420px,1fr))', gap:14 }}>
        {shown.map(job => {
          const dp = Object.values(job.docs).filter(Boolean).length / 7 * 100;
          return (
            <Card key={job.id} onClick={() => push('jobDetail',{ jobId:job.id })}>
              <div style={{ ...s.row, marginBottom:10 }}>
                <div>
                  <div style={s.mono}>{job.num}</div>
                  <div style={{ fontWeight:800, fontSize:15, marginTop:4 }}>{job.client.name}</div>
                  <div style={{ fontSize:12, color:C.muted, marginTop:2 }}>{job.project.type} · {job.project.sq} SQ</div>
                </div>
                <Badge status={job.status} />
              </div>
              <div style={{ fontSize:12, color:C.faint, marginBottom:12 }}>📍 {job.client.address}</div>
              {isAdmin && (
                <div style={{ display:'flex', gap:20, marginBottom:14 }}>
                  {[['Est',`$${job.est.toLocaleString()}`,C.gold],['Paid',`$${job.paid.toLocaleString()}`,C.green],['Bal',`$${job.bal.toLocaleString()}`,job.bal>0?C.red:C.green]].map(([l,v,col]) => (
                    <div key={l}>
                      <div style={{ fontSize:10, fontWeight:700, color:C.muted, textTransform:'uppercase' }}>{l}</div>
                      <div style={{ fontWeight:800, color:col, fontSize:15, marginTop:3 }}>{v}</div>
                    </div>
                  ))}
                </div>
              )}
              <div style={{ display:'flex', alignItems:'center', gap:10 }}>
                <div style={{ flex:1 }}><Bar val={dp} color={dp===100?C.green:C.gold} /></div>
                <span style={{ fontSize:11, color:C.muted }}>Docs {Math.round(dp)}%</span>
              </div>
              {job.project.ins && (
                <div style={{ marginTop:10, background:'rgba(58,123,213,.1)', borderRadius:8, padding:'7px 12px',
                              border:'1px solid rgba(58,123,213,.25)', fontSize:11, color:C.blueLt, fontWeight:700 }}>
                  🏛 Insurance · {job.project.claimNum}
                </div>
              )}
            </Card>
          );
        })}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────
// JOB DETAIL
// ─────────────────────────────────────────────────────────
const DOCS_LIST = [
  { key:'estimate',       name:'Estimate',         icon:'📋' },
  { key:'contract',       name:'Contract / Agreement', icon:'✍️' },
  { key:'depositInvoice', name:'Deposit Invoice',  icon:'🧾' },
  { key:'finalInvoice',   name:'Final Invoice',    icon:'🧾' },
  { key:'completeCert',   name:'Completion Cert.', icon:'📜' },
  { key:'lienRelease',    name:'Lien Release',     icon:'🔓' },
  { key:'warranty',       name:'Warranty',         icon:'🛡️' },
];

// ─────────────────────────────────────────────────────────
// ESTIMATE GENERATOR
// ─────────────────────────────────────────────────────────
function generateEstimateHTML(data, format, logo) {
  const {
    num, clientName, clientAddress, clientPhone, clientEmail,
    roofType, sq, pitch, stories, jobType,
    insCo, claimNum, adjName, adjEmail, adjPhone,
    scopeNotes, total, matCost, laborCost, addlCost,
    issuedDate, expiryDate,
  } = data;

  const fmt = v => '$' + parseFloat(v||0).toLocaleString('en-US', { minimumFractionDigits:2, maximumFractionDigits:2 });
  const dep  = Math.round(total * 0.5);
  const prog = Math.round(total * 0.25);
  const fin  = total - dep - prog;
  const isIns = jobType === 'Insurance';
  const isItemized = format === 'itemized';

  // Default line items based on SQ
  const sqNum = parseFloat(sq) || 20;
  const shingles    = Math.round(sqNum * 125);
  const underlay    = Math.round(sqNum * 28);
  const iceWater    = Math.round(4 * 85);
  const ridge       = Math.round(3 * 65);
  const dripEdge    = 320;
  const flashings   = Math.round(4 * 45);
  const misc        = 143;
  const tearoff     = Math.round(sqNum * 35);
  const install     = Math.round(sqNum * 45);
  const flashLab    = 480;
  const dumpster    = 450;
  const permit      = 275;
  const op          = isIns ? Math.round((shingles+underlay+iceWater+ridge+dripEdge+flashings+misc+tearoff+install+flashLab+dumpster+permit) * 0.2) : 0;

  const scopeItems = scopeNotes
    ? scopeNotes.split('\n').filter(Boolean)
    : [
        'Complete tear-off and disposal of existing roofing materials',
        'Full inspection of roof deck — replace damaged sheathing if required (billed separately)',
        'Install synthetic underlayment, ice & water shield in valleys, eaves, and penetrations',
        `Install ${roofType} system — per specifications`,
        'Replace all pipe boots, flashings, ridge cap, and ridge ventilation',
        'Full debris cleanup, daily magnetic nail sweep, and photo documentation upon completion',
      ];

  return `<!DOCTYPE html>
<html lang="en"><head><meta charset="UTF-8"/>
<meta name="viewport" content="width=device-width,initial-scale=1.0"/>
<title>Estimate ${num}</title>
<style>
*{margin:0;padding:0;box-sizing:border-box;}
body{font-family:-apple-system,'Helvetica Neue',Arial,sans-serif;background:#fff;}
.page{max-width:820px;margin:0 auto;background:#fff;}
.header{background:#0a0c10;padding:32px 44px 26px;display:flex;justify-content:space-between;align-items:flex-start;}
.header img{height:80px;width:auto;}
.header-sub{font-size:11px;color:#6b7280;margin-top:7px;line-height:1.6;}
.doc-right{text-align:right;}
.doc-label{font-size:10px;font-weight:700;letter-spacing:2px;text-transform:uppercase;color:#c8973a;opacity:.8;margin-bottom:5px;}
.doc-num{font-family:monospace;font-size:21px;font-weight:900;color:#c8973a;}
.doc-meta{font-size:11px;color:#6b7280;margin-top:5px;line-height:1.7;}
.ins-badge{display:inline-block;background:#1e3a5f;color:#93c5fd;border-radius:999px;padding:2px 10px;font-size:10px;font-weight:800;margin-left:8px;vertical-align:middle;}
.stripe{height:4px;background:linear-gradient(90deg,#c8973a,#e2b054,#c8973a);}
.body{padding:36px 44px;}
.parties{display:grid;grid-template-columns:1fr 1fr;gap:20px;margin-bottom:32px;}
.party-box{background:#f8f9fc;border-radius:11px;padding:16px 18px;border:1px solid #eaecf4;}
.plbl{font-size:10px;font-weight:800;color:#9ca3af;text-transform:uppercase;letter-spacing:1.2px;margin-bottom:8px;}
.pname{font-size:15px;font-weight:800;color:#1a1a2e;margin-bottom:3px;}
.pdet{font-size:12px;color:#6b7280;line-height:1.7;}
.stitle{font-size:10px;font-weight:800;color:#9ca3af;text-transform:uppercase;letter-spacing:1.5px;margin-bottom:14px;display:flex;align-items:center;gap:8px;}
.stitle::after{content:'';flex:1;height:1px;background:#eaecf4;}
.pgrid{display:grid;grid-template-columns:repeat(4,1fr);gap:10px;margin-bottom:32px;}
.pbox{background:#f8f9fc;border-radius:10px;padding:13px 14px;border:1px solid #eaecf4;text-align:center;}
.pbox .l{font-size:10px;color:#9ca3af;text-transform:uppercase;letter-spacing:.7px;margin-bottom:5px;}
.pbox .v{font-size:14px;font-weight:800;color:#1a1a2e;}
.scope-box{background:#fffbf2;border:1px solid rgba(200,151,58,.25);border-radius:11px;padding:18px 20px;margin-bottom:32px;}
.scope-box h4{font-size:13px;font-weight:800;color:#92400e;margin-bottom:10px;}
.si{display:flex;gap:9px;font-size:12.5px;color:#374151;line-height:1.5;margin-bottom:7px;}
.sd{width:6px;height:6px;border-radius:3px;background:#c8973a;flex-shrink:0;margin-top:5px;}
/* simple */
.srow{display:flex;justify-content:space-between;padding:12px 18px;border-radius:10px;margin-bottom:8px;font-size:13px;}
.srow.mat{background:#eff6ff;border:1px solid #bfdbfe;}
.srow.lab{background:#f0fdf4;border:1px solid #bbf7d0;}
.srow.add{background:#fef9c3;border:1px solid #fde68a;}
.srow .rl{font-weight:700;color:#374151;}
.srow .rv{font-weight:800;color:#1a1a2e;}
.snote{font-size:11.5px;color:#9ca3af;font-style:italic;margin-top:8px;padding:0 4px;}
/* table */
table{width:100%;border-collapse:collapse;margin-bottom:20px;border-radius:12px;overflow:hidden;}
thead tr{background:#0a0c10;}
thead th{padding:11px 14px;font-size:10px;font-weight:700;color:#c8973a;text-align:left;text-transform:uppercase;letter-spacing:.8px;}
thead th:last-child{text-align:right;}
tbody tr{border-bottom:1px solid #f3f4f6;}
tbody td{padding:11px 14px;font-size:12.5px;color:#1a1a2e;}
tbody td:last-child{text-align:right;font-weight:700;}
.cr td{background:#f8f9fc;font-size:10px;font-weight:800;color:#9ca3af;text-transform:uppercase;letter-spacing:1px;padding:7px 14px;}
/* totals */
.tot-wrap{display:flex;justify-content:flex-end;margin-bottom:32px;}
.tot-box{width:300px;}
.trow{display:flex;justify-content:space-between;padding:9px 14px;font-size:13px;color:#374151;border-bottom:1px solid #f3f4f6;}
.trow.grand{background:#0a0c10;border-radius:10px;padding:14px 16px;margin-top:8px;border:none;}
.trow.grand span:first-child{font-weight:800;font-size:14px;color:#c8973a;}
.trow.grand span:last-child{font-weight:900;font-size:20px;color:#c8973a;}
/* payments */
.pgmt{display:grid;grid-template-columns:repeat(3,1fr);gap:10px;margin-bottom:32px;}
.pmt{border-radius:11px;padding:16px 14px;text-align:center;border:1px solid;}
.pmt .pn{font-size:20px;font-weight:900;margin-bottom:3px;}
.pmt .pl{font-size:10px;font-weight:800;text-transform:uppercase;letter-spacing:.7px;margin-bottom:5px;}
.pmt .pw{font-size:11px;}
.dep{background:#fffbf2;border-color:rgba(200,151,58,.3);} .dep .pn,.dep .pl{color:#92400e;} .dep .pw{color:#a16207;}
.prg{background:#eff6ff;border-color:rgba(58,123,213,.25);} .prg .pn,.prg .pl{color:#1e40af;} .prg .pw{color:#1d4ed8;}
.fin{background:#f0fdf4;border-color:rgba(64,200,112,.25);} .fin .pn,.fin .pl{color:#065f46;} .fin .pw{color:#16a34a;}
/* acceptance */
.acc{background:linear-gradient(135deg,#0a0c10,#151a28);border-radius:13px;padding:22px 26px;margin-bottom:32px;display:flex;align-items:center;justify-content:space-between;gap:16px;}
.acc h3{font-size:14px;font-weight:800;color:#c8973a;margin-bottom:5px;}
.acc p{font-size:12px;color:#9ca3af;line-height:1.6;max-width:380px;}
.acc-badge{background:rgba(200,151,58,.15);border:1px solid rgba(200,151,58,.35);border-radius:10px;padding:12px 18px;text-align:center;flex-shrink:0;}
.acc-badge .bl{font-size:10px;color:#c8973a;font-weight:700;letter-spacing:1px;text-transform:uppercase;}
.acc-badge .bv{font-size:20px;font-weight:900;color:#c8973a;margin-top:3px;}
/* sig */
.sig{display:grid;grid-template-columns:1fr 1fr;gap:36px;margin-bottom:32px;}
.sl{font-size:10px;font-weight:700;color:#9ca3af;text-transform:uppercase;letter-spacing:.8px;margin-bottom:36px;}
.sln{border-bottom:1.5px solid #d1d5db;margin-bottom:5px;}
.sn{font-size:11px;color:#6b7280;}
.terms{background:#f8f9fc;border-radius:11px;padding:18px 20px;margin-bottom:32px;border:1px solid #eaecf4;}
.terms li{font-size:11.5px;color:#6b7280;line-height:1.9;margin-left:16px;}
.footer{background:#0a0c10;padding:18px 44px;display:flex;justify-content:space-between;align-items:center;}
.fb{font-size:12px;font-weight:800;color:#c8973a;}
.fd{font-size:11px;color:#4b5563;margin-top:2px;line-height:1.6;}
.fr{text-align:right;font-size:11px;color:#4b5563;line-height:1.8;}
@media print{body{background:#fff;}.page{max-width:100%;}}
</style>
</head><body><div class="page">
<div class="header">
  <div>
    ${logo ? `<img src="data:image/png;base64,${logo}" alt="CCR"/>` : '<div style="color:#c8973a;font-size:22px;font-weight:900;">CCRoofing</div>'}
    <div class="header-sub">info@capricorncapitalroofing.com<br/>capricorncapitalroofing.com</div>
  </div>
  <div class="doc-right">
    <div class="doc-label">Roofing Estimate ${isIns ? '<span class="ins-badge">🏛 Insurance</span>' : ''}</div>
    <div class="doc-num">${num}</div>
    <div class="doc-meta">Issued: ${issuedDate}<br/>Valid through: ${expiryDate}</div>
  </div>
</div>
<div class="stripe"></div>
<div class="body">

<div class="parties">
  <div class="party-box">
    <div class="plbl">Prepared For</div>
    <div class="pname">${clientName}</div>
    <div class="pdet">${clientAddress}<br/>${clientPhone}${clientEmail ? '<br/>'+clientEmail : ''}</div>
  </div>
  <div class="party-box">
    <div class="plbl">${isIns ? 'Insurance / Adjuster' : 'Prepared By'}</div>
    ${isIns ? `
    <div class="pname">${insCo || 'Insurance Company'}</div>
    <div class="pdet">Claim #: ${claimNum || '—'}<br/>${adjName || ''}${adjEmail ? '<br/>'+adjEmail : ''}${adjPhone ? '<br/>'+adjPhone : ''}</div>
    ` : `
    <div class="pname">Capricorn Capital Roofing LLC</div>
    <div class="pdet">Atlanta, GA Metro Area<br/>info@capricorncapitalroofing.com<br/>License #: GA-RC-2024-00891</div>
    `}
  </div>
</div>

<div class="stitle">Project Overview</div>
<div class="pgrid">
  <div class="pbox"><div class="l">Roof Type</div><div class="v">${roofType || 'Asphalt Shingle'}</div></div>
  <div class="pbox"><div class="l">Squares</div><div class="v">${sq} SQ</div></div>
  <div class="pbox"><div class="l">Pitch</div><div class="v">${pitch || '—'}</div></div>
  <div class="pbox"><div class="l">Stories</div><div class="v">${stories || '1'}</div></div>
</div>

<div class="stitle">Scope of Work</div>
<div class="scope-box">
  <h4>🏗 ${roofType || 'Roofing'} System — Full Replacement</h4>
  ${scopeItems.map(i => `<div class="si"><div class="sd"></div>${i}</div>`).join('')}
</div>

${!isItemized ? `
<div class="stitle">Estimate Summary</div>
<div class="srow mat"><span class="rl">Materials</span><span class="rv">${fmt(matCost * sqNum)}</span></div>
<div class="srow lab"><span class="rl">Labor &amp; Installation</span><span class="rv">${fmt(laborCost * sqNum)}</span></div>
<div class="srow add"><span class="rl">Permit, Disposal &amp; Additional</span><span class="rv">${fmt(addlCost)}</span></div>
<div class="snote">* Detailed breakdown available upon request. Any additional deck repairs will be communicated before proceeding.</div>
<div style="margin-bottom:32px;"></div>
` : `
<div class="stitle">Detailed Estimate${isIns ? ' &nbsp;·&nbsp; <span style="font-size:10px;color:#1d4ed8;font-weight:700;">Xactimate-aligned pricing</span>' : ''}</div>
<table>
  <thead><tr><th>Item / Description</th><th>Qty</th><th>Unit</th><th>Rate</th><th>Amount</th></tr></thead>
  <tbody>
    <tr class="cr"><td colspan="5">Materials</td></tr>
    <tr><td>${roofType} Shingles / Material</td><td>${sqNum+2}</td><td>SQ</td><td>${fmt(125)}</td><td>${fmt(shingles)}</td></tr>
    <tr><td>Synthetic Underlayment</td><td>${sqNum}</td><td>SQ</td><td>${fmt(28)}</td><td>${fmt(underlay)}</td></tr>
    <tr><td>Ice &amp; Water Shield</td><td>4</td><td>SQ</td><td>${fmt(85)}</td><td>${fmt(iceWater)}</td></tr>
    <tr><td>Ridge Cap Shingles</td><td>3</td><td>Bundle</td><td>${fmt(65)}</td><td>${fmt(ridge)}</td></tr>
    <tr><td>Drip Edge / Starter Strip</td><td>1</td><td>Lot</td><td>${fmt(320)}</td><td>${fmt(dripEdge)}</td></tr>
    <tr><td>Pipe Flashings &amp; Boots</td><td>4</td><td>Each</td><td>${fmt(45)}</td><td>${fmt(flashings)}</td></tr>
    <tr><td>Fasteners, Nails &amp; Misc.</td><td>1</td><td>Lot</td><td>${fmt(143)}</td><td>${fmt(misc)}</td></tr>
    <tr class="cr"><td colspan="5">Labor</td></tr>
    <tr><td>Tear-Off &amp; Disposal</td><td>${sqNum}</td><td>SQ</td><td>${fmt(35)}</td><td>${fmt(tearoff)}</td></tr>
    <tr><td>Installation</td><td>${sqNum}</td><td>SQ</td><td>${fmt(45)}</td><td>${fmt(install)}</td></tr>
    <tr><td>Flashing &amp; Detail Work</td><td>1</td><td>Lot</td><td>${fmt(480)}</td><td>${fmt(flashLab)}</td></tr>
    <tr class="cr"><td colspan="5">Additional Services</td></tr>
    <tr><td>Dumpster / Debris Hauling</td><td>1</td><td>Lot</td><td>${fmt(450)}</td><td>${fmt(dumpster)}</td></tr>
    <tr><td>Permit</td><td>1</td><td>Lot</td><td>${fmt(275)}</td><td>${fmt(permit)}</td></tr>
    ${isIns ? `<tr><td>Overhead &amp; Profit (O&P) — 20%</td><td>1</td><td>Lot</td><td>—</td><td>${fmt(op)}</td></tr>` : ''}
    <tr><td>Final Inspection &amp; Documentation</td><td>1</td><td>Lot</td><td>—</td><td>Included</td></tr>
  </tbody>
</table>
`}

<div class="tot-wrap"><div class="tot-box">
  <div class="trow"><span>Subtotal</span><span>${fmt(total)}</span></div>
  <div class="trow" style="color:#6b7280;"><span>Tax (0%)</span><span>$0.00</span></div>
  <div class="trow grand"><span>Total Estimate</span><span>${fmt(total)}</span></div>
</div></div>

<div class="stitle">Payment Schedule</div>
<div class="pgmt">
  <div class="pmt dep"><div class="pl">Deposit</div><div class="pn">${fmt(dep)}</div><div class="pw">Due at contract signing (50%)</div></div>
  <div class="pmt prg"><div class="pl">Progress</div><div class="pn">${fmt(prog)}</div><div class="pw">Due at project midpoint (25%)</div></div>
  <div class="pmt fin"><div class="pl">Final</div><div class="pn">${fmt(fin)}</div><div class="pw">Due upon completion (25%)</div></div>
</div>

<div class="acc">
  <div>
    <h3>Ready to Get Started?</h3>
    <p>Accept this estimate by signing below or replying to this message. A 50% deposit of ${fmt(dep)} confirms your project and secures your place on our schedule.</p>
  </div>
  <div class="acc-badge"><div class="bl">Project Total</div><div class="bv">${fmt(total)}</div></div>
</div>

<div class="stitle">Authorization</div>
<div class="sig">
  <div><div class="sl">Client Signature</div><div class="sln"></div><div class="sn">${clientName} &nbsp;&nbsp; Date: ___________</div></div>
  <div><div class="sl">CCR Representative</div><div class="sln"></div><div class="sn">Capricorn Capital Roofing LLC &nbsp;&nbsp; Date: ___________</div></div>
</div>

<div class="stitle">Terms &amp; Conditions</div>
<div class="terms"><ul>
  <li>This estimate is valid for <strong>30 days</strong> from the date of issue (expires ${expiryDate}).</li>
  <li>Additional deck repairs discovered during tear-off will be communicated before proceeding — billed at $85/sheet.</li>
  <li>All work performed per local building codes. Permits pulled by CCR on homeowner's behalf.</li>
  <li>Manufacturer warranty registered in homeowner's name upon project completion.</li>
  <li>CCR provides a <strong>5-year workmanship warranty</strong> on all labor.</li>
  <li>Capricorn Capital Roofing LLC is fully licensed, bonded, and insured. COI available upon request.</li>
</ul></div>

</div>
<div class="footer">
  <div><div class="fb">Capricorn Capital Roofing LLC</div><div class="fd">Atlanta, GA Metro Area · info@capricorncapitalroofing.com<br/>capricorncapitalroofing.com · License #GA-RC-2024-00891</div></div>
  <div class="fr">Estimate ${num}<br/>Issued ${issuedDate} · Valid 30 days</div>
</div>
</div></body></html>`;
}

function EstimateModal({ open, onClose, source, onSave }) {
  const isJob  = source && source.client !== undefined;
  const [format,    setFormat]    = useState('simple');
  const [jobType,   setJobType]   = useState('Retail');
  const [sqInput,   setSqInput]   = useState('');
  const [total,     setTotal]     = useState('');
  const [matCostSq, setMatCostSq] = useState('225');
  const [labCostSq, setLabCostSq] = useState('51');
  const [addlCost,  setAddlCost]  = useState('725');
  const [saved,     setSaved]     = useState(false);
  const [generated, setGenerated] = useState(false);
  const [logoB64,   setLogoB64]   = useState(null);

  React.useEffect(() => {
    if (!open || logoB64) return;
    fetch('/logo.png').then(r=>r.blob())
      .then(blob=>new Promise(res=>{
        const rd=new FileReader(); rd.onload=e=>res(e.target.result.split(',')[1]); rd.readAsDataURL(blob);
      })).then(b64=>setLogoB64(b64)).catch(()=>setLogoB64(null));
  }, [open]);

  React.useEffect(() => {
    if (!open || !source) return;
    const jt = isJob ? (source.project?.jobType||'Retail') : (source.jobType||'Retail');
    setJobType(jt);
    setFormat(jt==='Insurance'?'itemized':'simple');
    setSqInput(String(isJob?(source.project?.sq||'20'):(source.sq||'20')));
    const est = isJob ? source.est : (source.estimatedAmt||'');
    setTotal(est?String(est):'');
    setSaved(false); setGenerated(false);
  }, [open, source?.id]);

  if (!open || !source) return null;

  const isIns     = jobType==='Insurance';
  const sqNum     = parseFloat(sqInput)||20;
  const matC      = parseFloat(matCostSq)||225;
  const labC      = parseFloat(labCostSq)||51;
  const addl      = parseFloat(addlCost)||725;
  const autoTotal = Math.round((matC+labC)*sqNum+addl);
  const finalTotal= parseFloat(total)||autoTotal;
  const deposit   = Math.round(finalTotal*0.5);

  const estimateData = isJob ? {
    num:source.num, clientName:source.client.name, clientAddress:source.client.address,
    clientPhone:source.client.phone, clientEmail:source.client.email,
    roofType:source.project.type, sq:sqInput, pitch:source.project.pitch,
    stories:source.project.stories, jobType, insCo:source.project.insCo,
    claimNum:source.project.claimNum, adjName:source.project.adjName,
    adjEmail:source.project.adjEmail, adjPhone:source.project.adjPhone,
    scopeNotes:source.project.desc, total:finalTotal, matCost:matC, laborCost:labC, addlCost:addl,
    issuedDate:new Date().toLocaleDateString('en-US',{month:'long',day:'numeric',year:'numeric'}),
    expiryDate:new Date(Date.now()+30*864e5).toLocaleDateString('en-US',{month:'long',day:'numeric',year:'numeric'}),
  } : {
    num:'EST-'+Date.now().toString().slice(-6), clientName:source.name,
    clientAddress:source.address, clientPhone:source.phone, clientEmail:source.email,
    roofType:source.roofType, sq:sqInput, pitch:source.pitch||'', stories:source.stories||'1',
    jobType, insCo:source.insCo, claimNum:source.claimNum, adjName:source.adjName,
    adjEmail:source.adjEmail, adjPhone:source.adjPhone, scopeNotes:source.notes,
    total:finalTotal, matCost:matC, laborCost:labC, addlCost:addl,
    issuedDate:new Date().toLocaleDateString('en-US',{month:'long',day:'numeric',year:'numeric'}),
    expiryDate:new Date(Date.now()+30*864e5).toLocaleDateString('en-US',{month:'long',day:'numeric',year:'numeric'}),
  };

  function saveEstimate() {
    if (onSave) onSave({ total:finalTotal, jobType, format, sq:sqInput, matCost:matC, laborCost:labC });
    setSaved(true); setGenerated(false);
  }

  function generateAndSend() {
    if (onSave) onSave({ total:finalTotal, jobType, format, sq:sqInput, matCost:matC, laborCost:labC });
    setSaved(true);
    const html = generateEstimateHTML(estimateData, format, logoB64);
    const win  = window.open('', '_blank');
    if (win) { win.document.write(html); win.document.close(); setTimeout(()=>win.print(),600); }
    setGenerated(true);
  }

  const fmtC = v => '$'+parseFloat(v||0).toLocaleString('en-US',{minimumFractionDigits:2,maximumFractionDigits:2});
  const chg  = cb => v => { cb(v); setSaved(false); };

  return (
    <Modal open title="Estimate" onClose={onClose} width={580}>
      <label style={s.label}>Job Type</label>
      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10, marginBottom:20 }}>
        {['Retail','Insurance'].map(jt=>(
          <button key={jt} onClick={()=>{ setJobType(jt); setFormat(jt==='Insurance'?'itemized':'simple'); setSaved(false); }}
            style={{ padding:'12px 0', borderRadius:10,
                     border:`2px solid ${jobType===jt?(jt==='Insurance'?C.blueLt:C.gold):C.border}`,
                     background:jobType===jt?(jt==='Insurance'?'rgba(58,123,213,.15)':'rgba(200,151,58,.15)'):'rgba(255,255,255,.04)',
                     cursor:'pointer', fontWeight:800, fontSize:14,
                     color:jobType===jt?(jt==='Insurance'?C.blueLt:C.gold):C.muted }}>
            {jt==='Insurance'?'🏛':'🛒'} {jt}
          </button>
        ))}
      </div>

      <label style={s.label}>Estimate Format</label>
      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10, marginBottom:isIns?8:20 }}>
        {[['simple','📄 Simple','Retail — totals only'],['itemized','📋 Itemized','Insurance / full line items']].map(([fv,lbl,sub])=>(
          <button key={fv} onClick={()=>{ setFormat(fv); setSaved(false); }}
            style={{ padding:'12px 14px', borderRadius:10, textAlign:'left',
                     border:`2px solid ${format===fv?C.gold:C.border}`,
                     background:format===fv?'rgba(200,151,58,.1)':'rgba(255,255,255,.04)', cursor:'pointer' }}>
            <div style={{ fontWeight:800, fontSize:13, color:format===fv?C.gold:C.text }}>{lbl}</div>
            <div style={{ fontSize:11, color:C.faint, marginTop:3 }}>{sub}</div>
          </button>
        ))}
      </div>
      {isIns && <div style={{ fontSize:11, color:C.blueLt, marginBottom:16, fontWeight:600 }}>🏛 Insurance — itemized with O&P line</div>}

      <Field label="Squares (SQ)" value={sqInput} onChange={chg(setSqInput)} type="number" placeholder="20" />
      <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:12 }}>
        <Field label="Materials ($/SQ)" value={matCostSq} onChange={chg(setMatCostSq)} type="number" placeholder="225" />
        <Field label="Labor ($/SQ)"     value={labCostSq} onChange={chg(setLabCostSq)} type="number" placeholder="51" />
        <Field label="Addl. Costs ($)"  value={addlCost}  onChange={chg(setAddlCost)}  type="number" placeholder="725" />
      </div>
      <div style={{ marginBottom:16 }}>
        <label style={s.label}>Total Estimate — leave blank to auto-calculate</label>
        <input type="number" value={total} onChange={e=>{ setTotal(e.target.value); setSaved(false); }}
          placeholder={`Auto: ${fmtC(autoTotal)}`} style={{ ...s.input }} />
      </div>

      {/* Summary strip */}
      <div style={{ background:C.panel, borderRadius:12, padding:'14px 16px', marginBottom:16,
                    display:'grid', gridTemplateColumns:'repeat(4,1fr)', textAlign:'center', gap:8,
                    border:`1px solid ${C.border}` }}>
        {[['Total',fmtC(finalTotal),C.gold],['Deposit',fmtC(deposit),C.amber],
          ['Balance',fmtC(finalTotal-deposit),C.teal],['Format',format==='simple'?'Simple':'Itemized',C.muted]].map(([l,v,col])=>(
          <div key={l}>
            <div style={{ fontSize:10, color:C.faint, textTransform:'uppercase', fontWeight:700, marginBottom:4 }}>{l}</div>
            <div style={{ fontWeight:800, fontSize:13, color:col }}>{v}</div>
          </div>
        ))}
      </div>

      {saved && (
        <div style={{ background:'rgba(64,200,112,.1)', borderRadius:10, padding:'10px 14px', marginBottom:10,
                      border:`1px solid rgba(64,200,112,.3)`, fontSize:13, color:C.green }}>
          💾 Saved — {fmtC(finalTotal)} · {jobType} · {format==='simple'?'Simple':'Itemized'}
        </div>
      )}
      {generated && (
        <div style={{ background:'rgba(58,123,213,.1)', borderRadius:10, padding:'10px 14px', marginBottom:10,
                      border:`1px solid rgba(58,123,213,.3)`, fontSize:13, color:C.blueLt }}>
          📄 Opened in new tab — Print → Save as PDF → share via email or AirDrop
        </div>
      )}

      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10, marginBottom:10 }}>
        <Btn label="💾 Save Estimate" v="outline" onClick={saveEstimate} />
        <Btn label="📄 Generate & Send" onClick={generateAndSend} />
      </div>
      <Btn label="✓ Done" v="ghost" onClick={onClose} fullWidth />
    </Modal>
  );
}

function DocsTab({ job, updateJob, toggleDoc, dp }) {
  const uploadRefs = React.useRef({});
  const addMoreRef = React.useRef();
  const [delConfirm, setDelConfirm] = useState(null);
  const uploadedDocs = job.uploadedDocs || [];

  function fileIcon(mime='') {
    if (mime.includes('pdf'))   return '📄';
    if (mime.includes('image')) return '🖼️';
    if (mime.includes('word') || mime.includes('document')) return '📝';
    if (mime.includes('sheet') || mime.includes('excel'))   return '📊';
    if (mime.includes('presentation') || mime.includes('powerpoint')) return '📊';
    return '📎';
  }

  function formatSize(bytes) {
    if (!bytes) return '';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1048576) return `${(bytes/1024).toFixed(1)} KB`;
    return `${(bytes/1048576).toFixed(1)} MB`;
  }

  async function uploadDocToStorage(file, linkedTo=null) {
    const id = 'doc' + Date.now() + Math.random();
    const safeName = file.name.replace(/[^a-zA-Z0-9._-]/g,'_');
    const path = `jobs/${job.id}/docs/${id}_${safeName}`;
    // Show immediately with object URL (local preview)
    const preview = {
      id, name: file.name, uri: URL.createObjectURL(file),
      mimeType: file.type, size: file.size,
      addedAt: new Date().toISOString(), linkedTo, uploading: true,
    };
    // Try Supabase Storage upload
    try {
      const { error } = await supabase.storage.from('job-files').upload(path, file, { upsert:true, contentType:file.type });
      if (!error) {
        const { data: urlData } = supabase.storage.from('job-files').getPublicUrl(path);
        return { ...preview, uri: urlData.publicUrl, url: urlData.publicUrl, uploading: false };
      }
    } catch(e) { console.warn('Doc storage upload failed:', e.message); }
    return { ...preview, uploading: false };
  }

  async function handleUploadForDoc(key, files) {
    if (!files?.length) return;
    const newDoc = await uploadDocToStorage(files[0], key);
    updateJob({
      ...job,
      docs: { ...job.docs, [key]: true },
      uploadedDocs: [...uploadedDocs, newDoc],
    });
  }

  async function handleAddMore(files) {
    if (!files?.length) return;
    const newDocs = await Promise.all(Array.from(files).map(f => uploadDocToStorage(f, null)));
    updateJob({ ...job, uploadedDocs: [...uploadedDocs, ...newDocs] });
  }

  function shareDoc(doc) {
    const url = doc.url || doc.uri;
    if (navigator.share && url) {
      // If it's a public URL, share the link directly
      if (url.startsWith('http')) {
        navigator.share({ title: doc.name, url })
          .catch(() => downloadDoc(doc));
      } else {
        // Local blob — fetch and share as file
        fetch(url).then(r => r.blob()).then(blob => {
          const file = new File([blob], doc.name, { type: doc.mimeType || 'application/octet-stream' });
          if (navigator.canShare?.({ files:[file] })) {
            navigator.share({ title: doc.name, files: [file] }).catch(() => downloadDoc(doc));
          } else {
            navigator.share({ title: doc.name, url }).catch(() => downloadDoc(doc));
          }
        }).catch(() => downloadDoc(doc));
      }
    } else {
      downloadDoc(doc);
    }
  }

  function downloadDoc(doc) {
    const url = doc.url || doc.uri;
    if (!url) return;
    const a = document.createElement('a');
    a.href = url;
    a.download = doc.name;
    a.target = '_blank';
    a.click();
  }

  function deleteDoc(id) {
    updateJob({ ...job, uploadedDocs: uploadedDocs.filter(d => d.id !== id) });
    setDelConfirm(null);
  }

  // Group uploaded docs by which required doc they're linked to
  const linkedDocs = key => uploadedDocs.filter(d => d.linkedTo === key);
  const unlinkedDocs = uploadedDocs.filter(d => !d.linkedTo);

  return (
    <div>
      {/* Progress */}
      <div style={{ display:'flex', justifyContent:'space-between', marginBottom:8 }}>
        <div style={{ fontSize:13, color:C.muted, fontWeight:600 }}>Required Documents</div>
        <div style={{ fontWeight:800, color:dp===100?C.green:C.gold }}>{Math.round(dp)}% Complete</div>
      </div>
      <Bar val={dp} color={dp===100?C.green:C.gold} h={8} />

      {/* Required docs checklist */}
      <div style={{ marginTop:20, display:'flex', flexDirection:'column', gap:10, marginBottom:32 }}>
        {DOCS_LIST.map(d => {
          const linked = linkedDocs(d.key);
          return (
            <Card key={d.key} style={{
              border:`1px solid ${job.docs[d.key]?'rgba(64,200,112,.3)':C.border}`,
              background:job.docs[d.key]?'rgba(64,200,112,.04)':C.surface,
            }}>
              {/* Doc header row */}
              <div style={{ display:'flex', alignItems:'center', gap:14 }}>
                {/* Checkbox */}
                <div onClick={()=>toggleDoc(d.key)}
                  style={{ width:26, height:26, borderRadius:7, border:`2px solid ${job.docs[d.key]?C.green:C.border}`,
                           background:job.docs[d.key]?C.green:'transparent', display:'flex', alignItems:'center',
                           justifyContent:'center', cursor:'pointer', flexShrink:0, transition:'all .15s' }}>
                  {job.docs[d.key] && <span style={{ color:C.night, fontSize:14, fontWeight:900 }}>✓</span>}
                </div>

                {/* Name */}
                <div style={{ flex:1 }}>
                  <div style={{ fontWeight:700, fontSize:14 }}>{d.icon} {d.name}</div>
                  {linked.length > 0 && (
                    <div style={{ fontSize:11, color:C.green, marginTop:3 }}>
                      {linked.length} file{linked.length>1?'s':''} attached
                    </div>
                  )}
                </div>

                {/* Status badge */}
                <span style={{ background:job.docs[d.key]?'rgba(64,200,112,.15)':'rgba(200,151,58,.1)',
                               color:job.docs[d.key]?C.green:C.gold, borderRadius:999,
                               padding:'4px 12px', fontSize:11, fontWeight:700, flexShrink:0 }}>
                  {job.docs[d.key]?'DONE':'PENDING'}
                </span>

                {/* Upload button for this doc */}
                <input ref={el => uploadRefs.current[d.key] = el} type="file" style={{ display:'none' }}
                  onChange={e => { handleUploadForDoc(d.key, e.target.files); e.target.value=''; }} />
                <button onClick={()=>uploadRefs.current[d.key]?.click()}
                  style={{ background:'rgba(200,151,58,.12)', border:`1px solid rgba(200,151,58,.3)`,
                           borderRadius:8, padding:'6px 12px', color:C.gold, fontSize:12,
                           fontWeight:700, cursor:'pointer', flexShrink:0, whiteSpace:'nowrap' }}>
                  📎 Upload
                </button>
              </div>

              {/* Attached files for this required doc */}
              {linked.length > 0 && (
                <div style={{ marginTop:12, display:'flex', flexDirection:'column', gap:8 }}>
                  {linked.map(doc => (
                    <div key={doc.id} style={{ background:C.panel, borderRadius:10, padding:'10px 14px',
                                               display:'flex', alignItems:'center', gap:12,
                                               border:`1px solid ${C.border}` }}>
                      <div style={{ width:36, height:36, borderRadius:8, background:'rgba(200,151,58,.1)',
                                    display:'flex', alignItems:'center', justifyContent:'center',
                                    fontSize:20, flexShrink:0, border:`1px solid rgba(200,151,58,.2)` }}>
                        {fileIcon(doc.mimeType)}
                      </div>
                      <div style={{ flex:1, minWidth:0 }}>
                        <div style={{ fontWeight:600, fontSize:13, color:C.text,
                                      overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                          {doc.name}
                        </div>
                        <div style={{ fontSize:11, color:C.faint, marginTop:2 }}>
                          {formatSize(doc.size)} · {new Date(doc.addedAt).toLocaleDateString('en-US',{month:'short',day:'numeric',year:'numeric'})}
                        </div>
                      </div>
                      <div style={{ display:'flex', gap:8, flexShrink:0 }}>
                        <button onClick={()=>shareDoc(doc)}
                          style={{ background:'rgba(58,123,213,.15)', border:`1px solid rgba(58,123,213,.3)`,
                                   borderRadius:8, padding:'6px 12px', color:C.blueLt,
                                   fontSize:12, fontWeight:700, cursor:'pointer' }}>
                          📤 Share
                        </button>
                        <button onClick={()=>downloadDoc(doc)}
                          style={{ background:'rgba(200,151,58,.1)', border:`1px solid rgba(200,151,58,.25)`,
                                   borderRadius:8, padding:'6px 12px', color:C.gold,
                                   fontSize:12, fontWeight:700, cursor:'pointer' }}>
                          ⬇
                        </button>
                        <button onClick={()=>setDelConfirm(doc)}
                          style={{ background:C.redDim, border:`1px solid rgba(232,64,64,.3)`,
                                   borderRadius:8, padding:'6px 10px', color:C.red,
                                   fontSize:12, cursor:'pointer' }}>
                          🗑
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </Card>
          );
        })}
      </div>

      {/* Additional Files section */}
      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:14 }}>
        <div>
          <div style={{ fontWeight:700, fontSize:15, color:C.text }}>
            Additional Files {unlinkedDocs.length > 0 && <span style={{ color:C.muted, fontSize:13 }}>({unlinkedDocs.length})</span>}
          </div>
          <div style={{ fontSize:12, color:C.faint, marginTop:3 }}>Contracts, photos, quotes, misc. documents</div>
        </div>
        <div style={{ display:'flex', gap:10 }}>
          <input ref={addMoreRef} type="file" multiple style={{ display:'none' }}
            onChange={e => { handleAddMore(e.target.files); e.target.value=''; }} />
          <Btn label="📎 Upload Files" onClick={()=>addMoreRef.current.click()} />
        </div>
      </div>

      {/* Drop zone for additional files */}
      <div
        onDragOver={e=>{ e.preventDefault(); e.currentTarget.style.borderColor=C.gold; e.currentTarget.style.background='rgba(200,151,58,.06)'; }}
        onDragLeave={e=>{ e.currentTarget.style.borderColor=C.border; e.currentTarget.style.background=unlinkedDocs.length?'transparent':C.panel; }}
        onDrop={e=>{ e.preventDefault(); e.currentTarget.style.borderColor=C.border; e.currentTarget.style.background='transparent'; handleAddMore(e.dataTransfer.files); }}
        style={{ border:`2px dashed ${C.border}`, borderRadius:14,
                 padding: unlinkedDocs.length ? '16px' : '36px 20px',
                 background: unlinkedDocs.length ? 'transparent' : C.panel,
                 transition:'all .2s', marginBottom:16 }}>
        {unlinkedDocs.length === 0 ? (
          <div style={{ textAlign:'center' }}>
            <div style={{ fontSize:36, marginBottom:10 }}>📁</div>
            <div style={{ fontWeight:600, color:C.muted, marginBottom:6 }}>No additional files uploaded</div>
            <div style={{ fontSize:13, color:C.faint }}>Drag &amp; drop files here, or click Upload Files above</div>
            <div style={{ fontSize:11, color:C.faint, marginTop:4 }}>PDF, Word, Excel, images, and more</div>
          </div>
        ) : (
          <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
            {unlinkedDocs.map(doc => (
              <div key={doc.id} style={{ background:C.panel, borderRadius:10, padding:'12px 16px',
                                         display:'flex', alignItems:'center', gap:14,
                                         border:`1px solid ${C.border}` }}>
                <div style={{ width:40, height:40, borderRadius:10, background:'rgba(58,123,213,.1)',
                              display:'flex', alignItems:'center', justifyContent:'center',
                              fontSize:22, flexShrink:0, border:`1px solid rgba(58,123,213,.2)` }}>
                  {fileIcon(doc.mimeType)}
                </div>
                <div style={{ flex:1, minWidth:0 }}>
                  <div style={{ fontWeight:600, fontSize:14, color:C.text,
                                overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                    {doc.name}
                  </div>
                  <div style={{ fontSize:11, color:C.faint, marginTop:3 }}>
                    {formatSize(doc.size)} · {new Date(doc.addedAt).toLocaleDateString('en-US',{month:'short',day:'numeric',year:'numeric'})}
                  </div>
                </div>
                <div style={{ display:'flex', gap:8, flexShrink:0 }}>
                  <button onClick={()=>shareDoc(doc)}
                    style={{ background:'rgba(58,123,213,.15)', border:`1px solid rgba(58,123,213,.3)`,
                             borderRadius:8, padding:'8px 14px', color:C.blueLt,
                             fontSize:12, fontWeight:700, cursor:'pointer' }}>
                    📤 Share
                  </button>
                  <button onClick={()=>downloadDoc(doc)}
                    style={{ background:'rgba(200,151,58,.1)', border:`1px solid rgba(200,151,58,.25)`,
                             borderRadius:8, padding:'8px 14px', color:C.gold,
                             fontSize:12, fontWeight:700, cursor:'pointer' }}>
                    ⬇ Download
                  </button>
                  <button onClick={()=>setDelConfirm(doc)}
                    style={{ background:C.redDim, border:`1px solid rgba(232,64,64,.3)`,
                             borderRadius:8, padding:'8px 10px', color:C.red,
                             fontSize:13, cursor:'pointer' }}>
                    🗑
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <Confirm open={!!delConfirm}
        title="Delete Document"
        message={`Remove "${delConfirm?.name}"? This cannot be undone.`}
        danger
        onConfirm={() => deleteDoc(delConfirm.id)}
        onCancel={() => setDelConfirm(null)} />
    </div>
  );
}

// ─────────────────────────────────────────────────────────
// FINANCIAL TAB
// ─────────────────────────────────────────────────────────
const EXPENSE_CATS = ['Labor','Materials','Subcontractor','Equipment','Permit','Disposal','Overhead','Other'];
const REVENUE_CATS = ['Deposit','Progress Payment','Final Payment','Insurance Payment','Change Order','Other'];

// ─────────────────────────────────────────────────────────
// MATERIAL / LABOR TAB
// ─────────────────────────────────────────────────────────
const COST_CATS = ['Materials','Labor','Subcontractor','Equipment','Permit','Disposal','Overhead','Other'];

function MaterialLaborTab({ job, updateJob }) {
  const [modal,      setModal]      = useState(false);
  const [editEntry,  setEditEntry]  = useState(null);
  const [delConfirm, setDelConfirm] = useState(null);
  const [filter,     setFilter]     = useState('All'); // All | Materials | Labor
  const fileRef = React.useRef();
  const cameraRef = React.useRef();

  const entries = job.costEntries || [];

  const fmtC = v => '$'+parseFloat(v||0).toLocaleString('en-US',{minimumFractionDigits:2,maximumFractionDigits:2});

  function openNew(method='manual') {
    setEditEntry({
      id:'ce'+Date.now(), type:'Materials', vendor:'', date:new Date().toISOString().slice(0,10),
      invoiceNum:'', description:'', amount:'', payStatus:'Unpaid', payDate:'',
      notes:'', docUri:null, docName:null, entryMethod: method,
    });
    setModal(true);
  }

  function save(entry) {
    const existing = entries.find(e=>e.id===entry.id);
    let updated;
    if (existing) {
      updated = entries.map(e=>e.id===entry.id?entry:e);
    } else {
      updated = [...entries, entry];
    }
    // Auto-sync to expenses for FinancialTab
    const newExpense = {
      id:   entry.id,
      date: entry.date,
      description: `${entry.vendor ? entry.vendor+' — ' : ''}${entry.description}`,
      category: entry.type,
      amount: parseFloat(entry.amount)||0,
      addedAt: new Date().toISOString(),
      fromCostEntry: true,
    };
    const otherExpenses = (job.expenses||[]).filter(e=>e.id!==entry.id && !e.fromCostEntry || (e.fromCostEntry && e.id!==entry.id));
    // Auto-add timeline entry
    const tlEntry = { date:entry.date||new Date().toISOString().slice(0,10),
      event:`${entry.type} cost recorded — ${entry.vendor||entry.description||'entry'} · ${fmtC(entry.amount)}` };
    const newTimeline = [...(job.timeline||[]), tlEntry];

    updateJob({ ...job, costEntries:updated, expenses:[...otherExpenses, newExpense], timeline:newTimeline });
    setModal(false); setEditEntry(null);
  }

  function deleteEntry(id) {
    const updated = entries.filter(e=>e.id!==id);
    const newExpenses = (job.expenses||[]).filter(e=>e.id!==id);
    updateJob({ ...job, costEntries:updated, expenses:newExpenses });
    setDelConfirm(null);
  }

  async function handleDocUpload(file, entry) {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = e => {
      setEditEntry(x=>({...x, docUri:e.target.result, docName:file.name}));
    };
    reader.readAsDataURL(file);
  }

  const filtered = filter==='All' ? entries :
    filter==='Materials' ? entries.filter(e=>['Materials','Equipment','Permit','Disposal'].includes(e.type)) :
    entries.filter(e=>['Labor','Subcontractor','Overhead'].includes(e.type));

  const totalMat = entries.filter(e=>['Materials','Equipment','Permit','Disposal'].includes(e.type))
                          .reduce((a,e)=>a+parseFloat(e.amount||0),0);
  const totalLab = entries.filter(e=>['Labor','Subcontractor','Overhead'].includes(e.type))
                          .reduce((a,e)=>a+parseFloat(e.amount||0),0);
  const totalAll = totalMat + totalLab;

  return (
    <div>
      {/* Header */}
      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:16 }}>
        <div>
          <h3 style={{ fontWeight:800, color:C.text, margin:0 }}>Material &amp; Labor Costs</h3>
          <div style={{ fontSize:12, color:C.muted, marginTop:4 }}>
            Receipts, invoices, and labor costs · Auto-syncs to Payments
          </div>
        </div>
        <div style={{ display:'flex', gap:8 }}>
          <input ref={fileRef} type="file" accept="image/*,application/pdf" style={{ display:'none' }}
            onChange={e=>{ if(e.target.files[0]) { openNew('upload'); handleDocUpload(e.target.files[0], editEntry); } e.target.value=''; }} />
          <input ref={cameraRef} type="file" accept="image/*" capture="environment" style={{ display:'none' }}
            onChange={e=>{ if(e.target.files[0]) { openNew('camera'); handleDocUpload(e.target.files[0], editEntry); } e.target.value=''; }} />
          <Btn label="📷 Scan" size="sm" v="outline" onClick={()=>cameraRef.current.click()} />
          <Btn label="📎 Upload" size="sm" v="outline" onClick={()=>fileRef.current.click()} />
          <Btn label="+ Manual Entry" size="sm" onClick={()=>openNew('manual')} />
        </div>
      </div>

      {/* Summary strip */}
      <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:10, marginBottom:20 }}>
        {[['Material Cost', fmtC(totalMat), C.amber],['Labor Cost', fmtC(totalLab), C.blueLt],['Total Job Cost', fmtC(totalAll), C.red]].map(([l,v,col])=>(
          <div key={l} style={{ background:C.panel, borderRadius:10, padding:'13px 16px', textAlign:'center', border:`1px solid ${C.border}` }}>
            <div style={{ fontSize:10, color:C.faint, textTransform:'uppercase', letterSpacing:.8, marginBottom:4 }}>{l}</div>
            <div style={{ fontWeight:900, fontSize:18, color:col }}>{v}</div>
          </div>
        ))}
      </div>

      {/* Filter tabs */}
      <div style={{ display:'flex', gap:8, marginBottom:16 }}>
        {['All','Materials','Labor'].map(f=>(
          <button key={f} onClick={()=>setFilter(f)}
            style={{ padding:'6px 16px', borderRadius:999, border:`1px solid ${filter===f?C.gold:C.border}`,
                     background:filter===f?'rgba(200,151,58,.12)':'transparent',
                     color:filter===f?C.gold:C.muted, fontWeight:700, fontSize:12, cursor:'pointer' }}>
            {f}
          </button>
        ))}
      </div>

      {/* Entries */}
      {filtered.length === 0 ? (
        <div style={{ textAlign:'center', padding:'48px 0', color:C.muted, background:C.panel,
                      borderRadius:12, border:`2px dashed ${C.border}` }}>
          <div style={{ fontSize:40, marginBottom:12 }}>🧾</div>
          <div style={{ fontWeight:700, marginBottom:6 }}>No cost entries yet</div>
          <div style={{ fontSize:13, color:C.faint }}>
            📷 Scan a receipt · 📎 Upload an invoice · or + Manual Entry
          </div>
        </div>
      ) : (
        <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
          {filtered.map(entry=>(
            <Card key={entry.id} style={{ border:`1px solid ${['Labor','Subcontractor'].includes(entry.type)?'rgba(58,123,213,.25)':'rgba(200,151,58,.2)'}` }}>
              <div style={{ display:'flex', gap:14, alignItems:'flex-start' }}>
                {/* Type icon */}
                <div style={{ width:42, height:42, borderRadius:10, flexShrink:0,
                              background:['Labor','Subcontractor'].includes(entry.type)?'rgba(58,123,213,.12)':'rgba(200,151,58,.1)',
                              display:'flex', alignItems:'center', justifyContent:'center', fontSize:20,
                              border:`1px solid ${['Labor','Subcontractor'].includes(entry.type)?'rgba(58,123,213,.25)':'rgba(200,151,58,.2)'}` }}>
                  {['Labor','Subcontractor'].includes(entry.type)?'👷':'📦'}
                </div>
                <div style={{ flex:1 }}>
                  <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start' }}>
                    <div>
                      <div style={{ fontWeight:700, fontSize:14 }}>{entry.vendor||entry.description||'—'}</div>
                      <div style={{ fontSize:12, color:C.muted, marginTop:2 }}>
                        {entry.type} · {entry.date}
                        {entry.invoiceNum && ` · #${entry.invoiceNum}`}
                      </div>
                      {entry.description && entry.vendor && (
                        <div style={{ fontSize:12, color:C.faint, marginTop:2 }}>{entry.description}</div>
                      )}
                    </div>
                    <div style={{ textAlign:'right', flexShrink:0, marginLeft:16 }}>
                      <div style={{ fontWeight:900, fontSize:16, color:['Labor','Subcontractor'].includes(entry.type)?C.blueLt:C.amber }}>
                        {fmtC(entry.amount)}
                      </div>
                      <span style={{ fontSize:10, fontWeight:700, borderRadius:999, padding:'3px 8px',
                                     background:entry.payStatus==='Paid'?'rgba(64,200,112,.15)':'rgba(232,64,64,.12)',
                                     color:entry.payStatus==='Paid'?C.green:C.red }}>
                        {entry.payStatus||'Unpaid'}
                      </span>
                    </div>
                  </div>
                  {(entry.notes||entry.docName) && (
                    <div style={{ marginTop:8, display:'flex', gap:8, flexWrap:'wrap' }}>
                      {entry.docName && (
                        <span style={{ fontSize:11, color:C.muted, background:C.panel, borderRadius:6,
                                       padding:'3px 10px', border:`1px solid ${C.border}` }}>
                          📄 {entry.docName}
                        </span>
                      )}
                      {entry.notes && (
                        <span style={{ fontSize:11, color:C.faint }}>{entry.notes}</span>
                      )}
                    </div>
                  )}
                </div>
              </div>
              <div style={{ display:'flex', gap:8, marginTop:12, justifyContent:'flex-end' }}>
                <Btn label="✏️ Edit" v="outline" size="sm" onClick={()=>{ setEditEntry({...entry}); setModal(true); }} />
                <Btn label="🗑" v="danger" size="sm" onClick={()=>setDelConfirm(entry)} />
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* Entry Modal */}
      <Modal open={modal} onClose={()=>{ setModal(false); setEditEntry(null); }}
        title={editEntry?.id && entries.find(e=>e.id===editEntry?.id) ? 'Edit Cost Entry' : 'Add Cost Entry'} width={540}>
        {editEntry && (
          <div>
            {/* Type */}
            <div style={{ marginBottom:16 }}>
              <label style={s.label}>Category</label>
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:8, marginBottom:8 }}>
                {[['Materials','📦 Material Cost'],['Labor','👷 Labor / Sub']].map(([v,lbl])=>(
                  <button key={v}
                    onClick={()=>setEditEntry(x=>({...x,type:v}))}
                    style={{ padding:'10px 0', borderRadius:10, border:`2px solid ${editEntry.type===v?C.gold:C.border}`,
                             background:editEntry.type===v?'rgba(200,151,58,.12)':'rgba(255,255,255,.04)',
                             color:editEntry.type===v?C.gold:C.muted, fontWeight:800, cursor:'pointer' }}>
                    {lbl}
                  </button>
                ))}
              </div>
              <select value={editEntry.type} onChange={e=>setEditEntry(x=>({...x,type:e.target.value}))}
                style={{ ...s.input }}>
                {COST_CATS.map(c=><option key={c}>{c}</option>)}
              </select>
            </div>

            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
              <div>
                <label style={s.label}>Vendor / Subcontractor</label>
                <input value={editEntry.vendor} onChange={e=>setEditEntry(x=>({...x,vendor:e.target.value}))}
                  placeholder="Company or person name" style={{ ...s.input }} />
              </div>
              <div>
                <label style={s.label}>Date</label>
                <input type="date" value={editEntry.date} onChange={e=>setEditEntry(x=>({...x,date:e.target.value}))}
                  style={{ ...s.input }} />
              </div>
              <div>
                <label style={s.label}>Invoice / Receipt #</label>
                <input value={editEntry.invoiceNum} onChange={e=>setEditEntry(x=>({...x,invoiceNum:e.target.value}))}
                  placeholder="Optional" style={{ ...s.input }} />
              </div>
              <div>
                <label style={s.label}>Amount ($)</label>
                <input type="number" value={editEntry.amount} onChange={e=>setEditEntry(x=>({...x,amount:e.target.value}))}
                  placeholder="0.00" style={{ ...s.input }} />
              </div>
            </div>
            <div>
              <label style={s.label}>Description</label>
              <input value={editEntry.description} onChange={e=>setEditEntry(x=>({...x,description:e.target.value}))}
                placeholder="What was purchased or performed" style={{ ...s.input, marginBottom:12 }} />
            </div>
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
              <div>
                <label style={s.label}>Payment Status</label>
                <select value={editEntry.payStatus} onChange={e=>setEditEntry(x=>({...x,payStatus:e.target.value}))}
                  style={{ ...s.input }}>
                  {['Unpaid','Paid','Partial','Pending'].map(o=><option key={o}>{o}</option>)}
                </select>
              </div>
              <div>
                <label style={s.label}>Payment Date</label>
                <input type="date" value={editEntry.payDate||''} onChange={e=>setEditEntry(x=>({...x,payDate:e.target.value}))}
                  style={{ ...s.input }} />
              </div>
            </div>
            <div>
              <label style={s.label}>Notes</label>
              <input value={editEntry.notes||''} onChange={e=>setEditEntry(x=>({...x,notes:e.target.value}))}
                placeholder="Optional notes" style={{ ...s.input, marginBottom:12 }} />
            </div>

            {/* Document */}
            <div style={{ background:C.panel, borderRadius:10, padding:'12px 14px', marginBottom:16, border:`1px solid ${C.border}` }}>
              <div style={{ fontSize:12, fontWeight:700, color:C.muted, marginBottom:10 }}>Source Document</div>
              {editEntry.docUri ? (
                <div style={{ display:'flex', alignItems:'center', gap:10 }}>
                  <span style={{ fontSize:13, color:C.text, flex:1 }}>📄 {editEntry.docName}</span>
                  <Btn label="Remove" v="danger" size="sm" onClick={()=>setEditEntry(x=>({...x,docUri:null,docName:null}))} />
                </div>
              ) : (
                <div style={{ display:'flex', gap:8 }}>
                  <Btn label="📷 Camera" size="sm" v="outline" onClick={()=>{
                    const i=document.createElement('input'); i.type='file'; i.accept='image/*'; i.capture='environment';
                    i.onchange=e=>{ if(e.target.files[0]) handleDocUpload(e.target.files[0], editEntry); };
                    i.click();
                  }} />
                  <Btn label="📎 Upload" size="sm" v="outline" onClick={()=>{
                    const i=document.createElement('input'); i.type='file'; i.accept='image/*,application/pdf';
                    i.onchange=e=>{ if(e.target.files[0]) handleDocUpload(e.target.files[0], editEntry); };
                    i.click();
                  }} />
                  <span style={{ fontSize:11, color:C.faint, alignSelf:'center' }}>Photo, scan, or PDF</span>
                </div>
              )}
            </div>

            <div style={{ display:'flex', gap:10 }}>
              <Btn label="Cancel" v="ghost" onClick={()=>{ setModal(false); setEditEntry(null); }} />
              <Btn label="💾 Save Entry" onClick={()=>save(editEntry)} style={{ flex:1 }} />
            </div>
          </div>
        )}
      </Modal>

      <Confirm open={!!delConfirm}
        title="Delete Cost Entry"
        message={`Remove "${delConfirm?.vendor||delConfirm?.description}"? This will also remove it from the expense ledger.`}
        danger
        onConfirm={()=>deleteEntry(delConfirm.id)}
        onCancel={()=>setDelConfirm(null)} />
    </div>
  );
}

function FinancialTab({ job, updateJob }) {
  const [addExp,  setAddExp]  = useState(false);
  const [addRev,  setAddRev]  = useState(false);
  const [delConf, setDelConf] = useState(null);
  const [eForm,   setEForm]   = useState({ date:'', description:'', category:'Labor', amount:'' });
  const [rForm,   setRForm]   = useState({ date:'', description:'', category:'Deposit', amount:'', method:'Check' });

  const expenses = job.expenses || [];
  const revenues = job.revenues || [];

  const totalRev  = revenues.reduce((a,r) => a + parseFloat(r.amount||0), 0);
  const totalExp  = expenses.reduce((a,e) => a + parseFloat(e.amount||0), 0);
  const grossProfit = totalRev - totalExp;
  const margin    = totalRev > 0 ? (grossProfit / totalRev * 100) : 0;

  function addExpense() {
    if (!eForm.amount || !eForm.description) return;
    const entry = { id:'exp'+Date.now(), ...eForm, amount:parseFloat(eForm.amount), addedAt:new Date().toISOString() };
    const updated = { ...job, expenses:[...expenses, entry],
                      paid: job.paid, bal: job.bal };
    updateJob(updated);
    setEForm({ date:'', description:'', category:'Labor', amount:'' });
    setAddExp(false);
  }

  function addRevenue() {
    if (!rForm.amount || !rForm.description) return;
    const entry = { id:'rev'+Date.now(), ...rForm, amount:parseFloat(rForm.amount), addedAt:new Date().toISOString() };
    const newRevenues = [...revenues, entry];
    const newPaid = newRevenues.reduce((a,r)=>a+parseFloat(r.amount||0),0);
    const updated = { ...job, revenues:newRevenues, paid:newPaid, bal:Math.max(0, job.est - newPaid) };
    updateJob(updated);
    setRForm({ date:'', description:'', category:'Deposit', amount:'', method:'Check' });
    setAddRev(false);
  }

  function deleteEntry(type, id) {
    if (type === 'expense') {
      updateJob({ ...job, expenses: expenses.filter(e=>e.id!==id) });
    } else {
      const newRevs  = revenues.filter(r=>r.id!==id);
      const newPaid  = newRevs.reduce((a,r)=>a+parseFloat(r.amount||0),0);
      updateJob({ ...job, revenues:newRevs, paid:newPaid, bal:Math.max(0, job.est-newPaid) });
    }
    setDelConf(null);
  }

  function sendReceipt(r) {
    const receiptNum = `RCP-${job.num}-${r.id.slice(-4).toUpperCase()}`;
    const today = new Date().toLocaleDateString('en-US',{year:'numeric',month:'long',day:'numeric'});
    const paid   = revenues.reduce((a,x)=>a+parseFloat(x.amount||0),0);
    const bal    = Math.max(0, job.est - paid);
    const html = `<!DOCTYPE html>
<html><head><meta charset="UTF-8"/>
<style>
  *{margin:0;padding:0;box-sizing:border-box;}
  body{font-family:-apple-system,'Helvetica Neue',Arial,sans-serif;background:#fff;color:#1a1a2e;padding:48px 40px;}
  .header{display:flex;justify-content:space-between;align-items:flex-start;margin-bottom:40px;padding-bottom:24px;border-bottom:2px solid #c8973a;}
  .logo-area h1{font-size:22px;font-weight:900;color:#c8973a;letter-spacing:.5px;}
  .logo-area p{font-size:12px;color:#6b7280;margin-top:4px;}
  .receipt-badge{background:#0a0c10;color:#c8973a;padding:10px 20px;border-radius:10px;text-align:right;}
  .receipt-badge .label{font-size:10px;font-weight:700;letter-spacing:1.5px;text-transform:uppercase;opacity:.7;}
  .receipt-badge .num{font-size:18px;font-weight:900;margin-top:4px;font-family:monospace;}
  .section{margin-bottom:28px;}
  .section-title{font-size:10px;font-weight:800;color:#9ca3af;text-transform:uppercase;letter-spacing:1.2px;margin-bottom:12px;}
  .info-grid{display:grid;grid-template-columns:1fr 1fr;gap:16px;}
  .info-box{background:#f8f9fc;border-radius:10px;padding:14px 16px;border:1px solid #eaecf4;}
  .info-box .lbl{font-size:10px;color:#9ca3af;text-transform:uppercase;letter-spacing:.8px;margin-bottom:4px;}
  .info-box .val{font-size:14px;font-weight:700;color:#1a1a2e;}
  .payment-box{background:#f0fdf4;border:1px solid #86efac;border-radius:12px;padding:20px 24px;margin-bottom:24px;}
  .payment-amount{font-size:42px;font-weight:900;color:#15803d;}
  .payment-label{font-size:13px;color:#16a34a;margin-top:4px;font-weight:600;}
  table{width:100%;border-collapse:collapse;margin-bottom:24px;}
  thead tr{background:#0a0c10;}
  thead th{padding:10px 14px;font-size:11px;font-weight:700;color:#c8973a;text-align:left;text-transform:uppercase;letter-spacing:.8px;}
  tbody td{padding:10px 14px;font-size:13px;border-bottom:1px solid #eaecf4;}
  .total-row td{font-weight:800;background:#fffbf2;border-top:2px solid #c8973a;font-size:14px;}
  .status-paid{display:inline-block;background:#dcfce7;color:#15803d;padding:4px 14px;border-radius:999px;font-size:12px;font-weight:800;}
  .status-bal{display:inline-block;background:#fef2f2;color:#dc2626;padding:4px 14px;border-radius:999px;font-size:12px;font-weight:800;}
  .footer{margin-top:40px;padding-top:20px;border-top:1px solid #eaecf4;display:flex;justify-content:space-between;align-items:center;}
  .footer p{font-size:11px;color:#9ca3af;}
  .thank-you{text-align:center;padding:24px;background:#fffbf2;border-radius:12px;border:1px solid rgba(200,151,58,.2);margin-bottom:28px;}
  .thank-you h2{font-size:18px;font-weight:800;color:#c8973a;}
  .thank-you p{font-size:13px;color:#6b7280;margin-top:6px;}
</style>
</head><body>
  <div class="header">
    <div class="logo-area">
      <h1>Capricorn Capital Roofing LLC</h1>
      <p>Protecting What Matters Most.</p>
      <p style="margin-top:8px;font-size:12px;color:#9ca3af;">info@capricorncapitalroofing.com</p>
    </div>
    <div class="receipt-badge">
      <div class="label">Payment Receipt</div>
      <div class="num">${receiptNum}</div>
      <div style="font-size:11px;margin-top:6px;opacity:.7;">${today}</div>
    </div>
  </div>

  <div class="payment-box">
    <div class="payment-amount">${fmtCur(r.amount)}</div>
    <div class="payment-label">Payment Received — ${r.category}</div>
  </div>

  <div class="section">
    <div class="section-title">Client & Project</div>
    <div class="info-grid">
      <div class="info-box"><div class="lbl">Client</div><div class="val">${job.client.name}</div></div>
      <div class="info-box"><div class="lbl">Job Number</div><div class="val">${job.num}</div></div>
      <div class="info-box"><div class="lbl">Property</div><div class="val">${job.client.address}</div></div>
      <div class="info-box"><div class="lbl">Roof System</div><div class="val">${job.project.type}</div></div>
    </div>
  </div>

  <div class="section">
    <div class="section-title">Payment Details</div>
    <table>
      <thead><tr><th>Description</th><th>Category</th><th>Method</th><th>Date</th><th>Amount</th></tr></thead>
      <tbody>
        <tr>
          <td>${r.description}</td>
          <td>${r.category}</td>
          <td>${r.method||'—'}</td>
          <td>${r.date||today}</td>
          <td style="font-weight:800;color:#15803d;">${fmtCur(r.amount)}</td>
        </tr>
      </tbody>
    </table>
  </div>

  <div class="section">
    <div class="section-title">Account Summary</div>
    <table>
      <tbody>
        <tr><td>Project Estimate</td><td style="text-align:right;font-weight:700;">${fmtCur(job.est)}</td></tr>
        <tr><td>Total Collected</td><td style="text-align:right;font-weight:700;color:#15803d;">${fmtCur(paid)}</td></tr>
        <tr class="total-row">
          <td>Balance Remaining</td>
          <td style="text-align:right;color:${bal>0?'#dc2626':'#15803d'};">${fmtCur(bal)}</td>
        </tr>
      </tbody>
    </table>
    ${bal===0?'<span class="status-paid">✓ PAID IN FULL</span>':'<span class="status-bal">Balance Due: '+fmtCur(bal)+'</span>'}
  </div>

  <div class="thank-you">
    <h2>Thank You for Your Business!</h2>
    <p>We appreciate your trust in Capricorn Capital Roofing. Please keep this receipt for your records.</p>
  </div>

  <div class="footer">
    <p>Capricorn Capital Roofing LLC · Atlanta, GA Metro Area<br/>info@capricorncapitalroofing.com · capricorncapitalroofing.com</p>
    <p>Receipt ${receiptNum} · Generated ${today}</p>
  </div>
</body></html>`;

    // Open in new window for printing/saving
    const win = window.open('', '_blank');
    if (win) {
      win.document.write(html);
      win.document.close();
      setTimeout(() => win.print(), 500);
    }
  }

  const fmtCur = v => `$${parseFloat(v||0).toLocaleString('en-US',{minimumFractionDigits:2,maximumFractionDigits:2})}`;

  return (
    <div>
      {/* Summary cards */}
      <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:12, marginBottom:28 }}>
        <Stat label="Total Revenue"   value={fmtCur(totalRev)}    color={C.green} />
        <Stat label="Total Expenses"  value={fmtCur(totalExp)}    color={C.red}   />
        <Stat label="Gross Profit"    value={fmtCur(grossProfit)} color={grossProfit>=0?C.teal:C.red} />
        <Stat label="Margin"          value={`${margin.toFixed(1)}%`} color={margin>=40?C.green:margin>=20?C.gold:C.red} />
      </div>

      {/* Progress bar */}
      <Card style={{ marginBottom:20 }}>
        <div style={{ display:'flex', justifyContent:'space-between', marginBottom:8 }}>
          <span style={{ fontSize:13, color:C.muted }}>Collected vs Estimate</span>
          <span style={{ fontWeight:700, color:C.gold }}>{fmtCur(totalRev)} / {fmtCur(job.est)}</span>
        </div>
        <Bar val={job.est>0?totalRev/job.est*100:0} color={totalRev>=job.est?C.green:C.gold} h={10} />
        <div style={{ display:'flex', justifyContent:'space-between', marginTop:8 }}>
          <span style={{ fontSize:12, color:C.muted }}>{Math.round(job.est>0?totalRev/job.est*100:0)}% collected</span>
          <span style={{ fontSize:12, color:job.bal>0?C.red:C.green, fontWeight:700 }}>
            {job.bal>0?`${fmtCur(job.bal)} remaining`:'Paid in full ✓'}
          </span>
        </div>
      </Card>

      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:20 }}>

        {/* ── REVENUE ── */}
        <div>
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:12 }}>
            <h3 style={{ fontWeight:800, color:C.text, margin:0 }}>💰 Revenue</h3>
            <Btn label="+ Add" size="sm" onClick={()=>setAddRev(true)} />
          </div>

          {addRev && (
            <Card style={{ marginBottom:12, border:`1px solid rgba(64,200,112,.3)` }}>
              <Field label="Date"        value={rForm.date}        onChange={v=>setRForm(x=>({...x,date:v}))}        type="date" />
              <Field label="Description" value={rForm.description} onChange={v=>setRForm(x=>({...x,description:v}))} placeholder="e.g. Deposit payment received" />
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
                <Field label="Category" value={rForm.category} onChange={v=>setRForm(x=>({...x,category:v}))} options={REVENUE_CATS} />
                <Field label="Method"   value={rForm.method}   onChange={v=>setRForm(x=>({...x,method:v}))}
                  options={['Check','ACH','Zelle','Cash','Credit Card','Insurance Check','Other']} />
              </div>
              <Field label="Amount ($)" value={rForm.amount} onChange={v=>setRForm(x=>({...x,amount:v}))} type="number" placeholder="0.00" />
              <div style={{ display:'flex', gap:10 }}>
                <Btn label="Cancel" v="ghost" size="sm" onClick={()=>setAddRev(false)} />
                <Btn label="✓ Add Revenue" size="sm" onClick={addRevenue} />
              </div>
            </Card>
          )}

          {revenues.length === 0 && !addRev && (
            <div style={{ textAlign:'center', padding:'28px 0', color:C.faint, background:C.panel, borderRadius:12, border:`1px dashed ${C.border}` }}>
              <div style={{ fontSize:28, marginBottom:8 }}>💳</div>
              <div style={{ fontSize:13 }}>No payments recorded yet</div>
            </div>
          )}

          <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
            {revenues.map(r => (
              <div key={r.id} style={{ background:C.panel, borderRadius:10, padding:'12px 14px',
                                       border:`1px solid rgba(64,200,112,.2)` }}>
                <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start' }}>
                  <div style={{ flex:1 }}>
                    <div style={{ fontWeight:700, fontSize:13, color:C.text }}>{r.description}</div>
                    <div style={{ fontSize:11, color:C.faint, marginTop:3 }}>
                      {r.category} · {r.method} {r.date?`· ${r.date}`:''}
                    </div>
                  </div>
                  <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                    <span style={{ fontWeight:800, fontSize:15, color:C.green }}>{fmtCur(r.amount)}</span>
                    <button onClick={()=>sendReceipt(r)}
                      style={{ background:'rgba(200,151,58,.12)', border:`1px solid rgba(200,151,58,.3)`,
                               borderRadius:6, padding:'5px 10px', color:C.gold,
                               fontSize:12, fontWeight:700, cursor:'pointer', whiteSpace:'nowrap' }}>
                      🧾 Receipt
                    </button>
                    <button onClick={()=>setDelConf({type:'revenue',id:r.id,name:r.description})}
                      style={{ background:C.redDim, border:`1px solid rgba(232,64,64,.3)`, borderRadius:6,
                               padding:'5px 8px', color:C.red, fontSize:12, cursor:'pointer' }}>🗑</button>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Revenue total */}
          {revenues.length > 0 && (
            <div style={{ background:'rgba(64,200,112,.08)', border:`1px solid rgba(64,200,112,.25)`,
                          borderRadius:10, padding:'12px 14px', marginTop:10,
                          display:'flex', justifyContent:'space-between', alignItems:'center' }}>
              <span style={{ fontWeight:700, color:C.green }}>Total Revenue</span>
              <span style={{ fontWeight:900, fontSize:18, color:C.green }}>{fmtCur(totalRev)}</span>
            </div>
          )}
        </div>

        {/* ── EXPENSES ── */}
        <div>
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:12 }}>
            <h3 style={{ fontWeight:800, color:C.text, margin:0 }}>📋 Expenses</h3>
            <Btn label="+ Add" size="sm" onClick={()=>setAddExp(true)} />
          </div>

          {addExp && (
            <Card style={{ marginBottom:12, border:`1px solid rgba(232,64,64,.3)` }}>
              <Field label="Date"        value={eForm.date}        onChange={v=>setEForm(x=>({...x,date:v}))}        type="date" />
              <Field label="Description" value={eForm.description} onChange={v=>setEForm(x=>({...x,description:v}))} placeholder="e.g. Jose Ramirez — labor day 1" />
              <Field label="Category"    value={eForm.category}    onChange={v=>setEForm(x=>({...x,category:v}))}    options={EXPENSE_CATS} />
              <Field label="Amount ($)"  value={eForm.amount}      onChange={v=>setEForm(x=>({...x,amount:v}))}      type="number" placeholder="0.00" />
              <div style={{ display:'flex', gap:10 }}>
                <Btn label="Cancel" v="ghost" size="sm" onClick={()=>setAddExp(false)} />
                <Btn label="✓ Add Expense" size="sm" onClick={addExpense} />
              </div>
            </Card>
          )}

          {expenses.length === 0 && !addExp && (
            <div style={{ textAlign:'center', padding:'28px 0', color:C.faint, background:C.panel, borderRadius:12, border:`1px dashed ${C.border}` }}>
              <div style={{ fontSize:28, marginBottom:8 }}>🧾</div>
              <div style={{ fontSize:13 }}>No expenses recorded yet</div>
            </div>
          )}

          {expenses.some(e=>e.fromCostEntry) && (
            <div style={{ background:'rgba(58,123,213,.08)', border:`1px solid rgba(58,123,213,.2)`,
                          borderRadius:8, padding:'9px 14px', marginBottom:12, fontSize:12, color:C.blueLt }}>
              🔗 Expenses marked <strong>M/L</strong> are synced from Material/Labor tab — edit them there.
            </div>
          )}
          <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
            {EXPENSE_CATS.filter(cat => expenses.some(e=>e.category===cat)).map(cat => {
              const catExps = expenses.filter(e=>e.category===cat);
              const catTotal = catExps.reduce((a,e)=>a+parseFloat(e.amount||0),0);
              return (
                <div key={cat}>
                  <div style={{ fontSize:11, fontWeight:800, color:C.muted, textTransform:'uppercase',
                                letterSpacing:.8, marginBottom:6, marginTop:4 }}>
                    {cat} <span style={{ color:C.red }}>{fmtCur(catTotal)}</span>
                  </div>
                  {catExps.map(e => (
                    <div key={e.id} style={{ background:C.panel, borderRadius:10, padding:'10px 14px',
                                             border:`1px solid ${e.fromCostEntry?'rgba(58,123,213,.2)':'rgba(232,64,64,.15)'}`,
                                             marginBottom:6 }}>
                      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start' }}>
                        <div style={{ flex:1 }}>
                          <div style={{ display:'flex', alignItems:'center', gap:6 }}>
                            <div style={{ fontWeight:600, fontSize:13, color:C.text }}>{e.description}</div>
                            {e.fromCostEntry && (
                              <span style={{ fontSize:9, fontWeight:800, background:'rgba(58,123,213,.15)',
                                             color:C.blueLt, borderRadius:4, padding:'1px 5px' }}>M/L</span>
                            )}
                          </div>
                          <div style={{ fontSize:11, color:C.faint, marginTop:2 }}>{e.date||'—'}</div>
                        </div>
                        <div style={{ display:'flex', alignItems:'center', gap:10 }}>
                          <span style={{ fontWeight:800, fontSize:14, color:C.red }}>{fmtCur(e.amount)}</span>
                          {!e.fromCostEntry && (
                            <button onClick={()=>setDelConf({type:'expense',id:e.id,name:e.description})}
                              style={{ background:C.redDim, border:`1px solid rgba(232,64,64,.3)`, borderRadius:6,
                                       padding:'4px 8px', color:C.red, fontSize:12, cursor:'pointer' }}>🗑</button>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              );
            })}
          </div>

          {expenses.length > 0 && (
            <div style={{ background:'rgba(232,64,64,.08)', border:`1px solid rgba(232,64,64,.25)`,
                          borderRadius:10, padding:'12px 14px', marginTop:10,
                          display:'flex', justifyContent:'space-between', alignItems:'center' }}>
              <span style={{ fontWeight:700, color:C.red }}>Total Expenses</span>
              <span style={{ fontWeight:900, fontSize:18, color:C.red }}>{fmtCur(totalExp)}</span>
            </div>
          )}
        </div>
      </div>

      {/* Profit summary */}
      {(revenues.length > 0 || expenses.length > 0) && (
        <Card style={{ marginTop:20, border:`1px solid ${grossProfit>=0?'rgba(42,184,165,.3)':'rgba(232,64,64,.3)'}`,
                       background:grossProfit>=0?'rgba(42,184,165,.05)':'rgba(232,64,64,.05)' }}>
          <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:20, textAlign:'center' }}>
            {[
              ['Revenue',fmtCur(totalRev),C.green],
              ['Expenses',fmtCur(totalExp),C.red],
              ['Gross Profit',fmtCur(grossProfit),grossProfit>=0?C.teal:C.red],
            ].map(([l,v,col])=>(
              <div key={l}>
                <div style={{ fontSize:11, fontWeight:700, color:C.muted, textTransform:'uppercase', marginBottom:6 }}>{l}</div>
                <div style={{ fontSize:22, fontWeight:900, color:col }}>{v}</div>
              </div>
            ))}
          </div>
          <div style={{ height:1, background:C.border, margin:'16px 0' }} />
          <div style={{ display:'flex', justifyContent:'center', alignItems:'center', gap:12 }}>
            <span style={{ fontSize:13, color:C.muted }}>Profit Margin</span>
            <span style={{ fontSize:24, fontWeight:900, color:margin>=40?C.green:margin>=20?C.gold:C.red }}>
              {margin.toFixed(1)}%
            </span>
            <span style={{ fontSize:12, color:margin>=40?C.green:C.gold, fontWeight:700 }}>
              {margin>=40?'✓ Target Met':margin>=20?'Below 40% Target':'Below Break-even'}
            </span>
          </div>
        </Card>
      )}

      <Confirm open={!!delConf}
        title={`Delete ${delConf?.type==='expense'?'Expense':'Revenue'}`}
        message={`Remove "${delConf?.name}"?`}
        danger
        onConfirm={()=>deleteEntry(delConf.type,delConf.id)}
        onCancel={()=>setDelConf(null)} />
    </div>
  );
}

// ─────────────────────────────────────────────────────────
// PHOTOS TAB
// ─────────────────────────────────────────────────────────
function PhotosTab({ job, updateJob }) {
  const [dragging, setDragging]   = useState(false);
  const [lightbox, setLightbox]   = useState(null);
  const [delConfirm, setDelConfirm] = useState(null);
  const fileRef = React.useRef();
  const photos  = job.photos || [];

  // updateJob needs to handle functional updates — wrap it
  async function addPhotos(files) {
    const imageFiles = Array.from(files).filter(f => f.type.startsWith('image/'));
    if (!imageFiles.length) return;
    // Show local preview immediately while uploading
    const previews = imageFiles.map(file => ({
      id:      'ph' + Date.now() + Math.random(),
      uri:     URL.createObjectURL(file),
      name:    file.name,
      size:    file.size,
      addedAt: new Date().toISOString(),
      uploading: true,
    }));
    const merged = [...photos, ...previews];
    updateJob({ ...job, photos: merged });
    // Upload each to Supabase Storage
    const uploads = await Promise.all(imageFiles.map(async (file, i) => {
      const path = `jobs/${job.id}/photos/${previews[i].id}_${file.name.replace(/[^a-zA-Z0-9._-]/g,'_')}`;
      try {
        const { data, error } = await supabase.storage.from('job-files').upload(path, file, { upsert:true, contentType:file.type });
        if (error) throw error;
        const { data: urlData } = supabase.storage.from('job-files').getPublicUrl(path);
        return { ...previews[i], uri: urlData.publicUrl, url: urlData.publicUrl, uploading: false };
      } catch(e) {
        console.warn('Photo upload failed, keeping local:', e.message);
        return { ...previews[i], uploading: false };
      }
    }));
    // Replace previews with final URLs
    const finalPhotos = [...photos, ...uploads];
    updateJob({ ...job, photos: finalPhotos });
  }

  function deletePhoto(id) {
    updateJob({ ...job, photos: photos.filter(p => p.id !== id) });
    setDelConfirm(null);
    if (lightbox?.id === id) setLightbox(null);
  }

  function formatSize(bytes) {
    if (!bytes) return '';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1048576) return `${(bytes/1024).toFixed(1)} KB`;
    return `${(bytes/1048576).toFixed(1)} MB`;
  }

  function navigate(dir) {
    const idx = photos.findIndex(p => p.id === lightbox.id);
    const next = photos[idx + dir];
    if (next) setLightbox(next);
  }

  return (
    <div>
      {/* Header */}
      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:20 }}>
        <div>
          <h3 style={{ fontWeight:700, color:C.text, margin:0 }}>Job Photos</h3>
          <div style={{ fontSize:12, color:C.muted, marginTop:4 }}>{photos.length} photo{photos.length!==1?'s':''} · Click to enlarge · Drag &amp; drop to upload</div>
        </div>
        <div style={{ display:'flex', gap:10 }}>
          <input ref={fileRef} type="file" accept="image/*" multiple style={{ display:'none' }}
            onChange={e => { addPhotos(e.target.files); e.target.value=''; }} />
          <Btn label="📷 Upload Photos" onClick={()=>fileRef.current.click()} />
        </div>
      </div>

      {/* Drop zone */}
      <div
        onDragOver={e => { e.preventDefault(); setDragging(true); }}
        onDragLeave={() => setDragging(false)}
        onDrop={e => { e.preventDefault(); setDragging(false); addPhotos(e.dataTransfer.files); }}
        onClick={()=>{ if(!photos.length) fileRef.current.click(); }}
        style={{
          border:`2px dashed ${dragging?C.gold:C.border}`,
          borderRadius:14, padding: photos.length?'16px':'48px 20px',
          background: dragging?'rgba(200,151,58,.06)':photos.length?'transparent':C.panel,
          textAlign:'center', transition:'all .2s', marginBottom:20,
          cursor: photos.length?'default':'pointer',
        }}>
        {photos.length === 0 ? (
          <>
            <div style={{ fontSize:48, marginBottom:12 }}>📸</div>
            <div style={{ fontWeight:700, color:C.muted, marginBottom:6 }}>
              {dragging ? 'Drop photos here' : 'No photos yet'}
            </div>
            <div style={{ fontSize:13, color:C.faint }}>
              Drag &amp; drop images here, or click to browse
            </div>
            <div style={{ fontSize:11, color:C.faint, marginTop:6 }}>
              Supports JPG, PNG, HEIC, WebP
            </div>
          </>
        ) : (
          /* Photo grid */
          <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(180px,1fr))', gap:12 }}>
            {photos.map(photo => (
              <div key={photo.id} style={{ position:'relative', borderRadius:10, overflow:'hidden',
                                           aspectRatio:'4/3', background:C.panel, cursor:'pointer',
                                           boxShadow:'0 2px 12px rgba(0,0,0,.4)', transition:'transform .15s' }}
                onMouseEnter={e => e.currentTarget.style.transform='scale(1.02)'}
                onMouseLeave={e => e.currentTarget.style.transform='scale(1)'}>
                <img src={photo.uri} alt={photo.name}
                  style={{ width:'100%', height:'100%', objectFit:'cover', display:'block' }}
                  onClick={() => setLightbox(photo)} />
                {/* Overlay on hover */}
                <div style={{ position:'absolute', inset:0, background:'rgba(0,0,0,0)',
                              display:'flex', alignItems:'flex-end', transition:'background .2s' }}
                  onMouseEnter={e => e.currentTarget.style.background='rgba(0,0,0,.45)'}
                  onMouseLeave={e => e.currentTarget.style.background='rgba(0,0,0,0)'}>
                  <div style={{ padding:'8px 10px', width:'100%', display:'flex', justifyContent:'space-between', alignItems:'flex-end' }}>
                    <div>
                      <div style={{ fontSize:11, color:'white', fontWeight:600, textShadow:'0 1px 3px rgba(0,0,0,.8)' }} onClick={()=>setLightbox(photo)}>
                        {photo.name?.length>22 ? photo.name.slice(0,20)+'…' : photo.name}
                      </div>
                      <div style={{ fontSize:10, color:'rgba(255,255,255,.7)', textShadow:'0 1px 3px rgba(0,0,0,.8)' }}>
                        {formatSize(photo.size)}
                      </div>
                    </div>
                    <button onClick={e=>{ e.stopPropagation(); setDelConfirm(photo); }}
                      style={{ background:'rgba(232,64,64,.85)', border:'none', borderRadius:6, padding:'4px 8px',
                               color:'white', fontSize:12, cursor:'pointer', flexShrink:0 }}>
                      🗑
                    </button>
                  </div>
                </div>
              </div>
            ))}
            {/* Add more tile */}
            <div onClick={()=>fileRef.current.click()}
              style={{ borderRadius:10, aspectRatio:'4/3', background:C.panel, border:`2px dashed ${C.border}`,
                       display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center',
                       cursor:'pointer', transition:'all .15s', gap:8 }}
              onMouseEnter={e=>{ e.currentTarget.style.borderColor=C.gold; e.currentTarget.style.background='rgba(200,151,58,.06)'; }}
              onMouseLeave={e=>{ e.currentTarget.style.borderColor=C.border; e.currentTarget.style.background=C.panel; }}>
              <div style={{ fontSize:28, color:C.faint }}>+</div>
              <div style={{ fontSize:12, color:C.faint, fontWeight:600 }}>Add photos</div>
            </div>
          </div>
        )}
      </div>

      {/* Photo count summary */}
      {photos.length > 0 && (
        <div style={{ display:'flex', gap:8, flexWrap:'wrap' }}>
          {photos.slice(-3).reverse().map(p => (
            <div key={p.id} style={{ fontSize:11, color:C.faint }}>
              📷 {p.name?.length>30?p.name.slice(0,28)+'…':p.name} · {formatSize(p.size)} · {new Date(p.addedAt).toLocaleDateString('en-US',{month:'short',day:'numeric'})}
            </div>
          ))}
          {photos.length > 3 && <div style={{ fontSize:11, color:C.faint }}>+{photos.length-3} more</div>}
        </div>
      )}

      {/* Lightbox */}
      {lightbox && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,.92)', zIndex:2000,
                      display:'flex', alignItems:'center', justifyContent:'center', flexDirection:'column' }}
          onClick={()=>setLightbox(null)}>
          {/* Top bar */}
          <div style={{ position:'absolute', top:0, left:0, right:0, padding:'16px 20px',
                        display:'flex', justifyContent:'space-between', alignItems:'center',
                        background:'linear-gradient(to bottom,rgba(0,0,0,.7),transparent)' }}
            onClick={e=>e.stopPropagation()}>
            <div>
              <div style={{ fontWeight:700, color:'white', fontSize:14 }}>{lightbox.name}</div>
              <div style={{ fontSize:11, color:'rgba(255,255,255,.6)', marginTop:2 }}>
                {formatSize(lightbox.size)} · {new Date(lightbox.addedAt).toLocaleDateString('en-US',{month:'long',day:'numeric',year:'numeric'})}
                · {photos.findIndex(p=>p.id===lightbox.id)+1} of {photos.length}
              </div>
            </div>
            <div style={{ display:'flex', gap:10 }}>
              <a href={lightbox.uri} download={lightbox.name}
                style={{ background:'rgba(200,151,58,.2)', border:`1px solid ${C.gold}`, borderRadius:8,
                         padding:'7px 14px', color:C.gold, fontSize:12, fontWeight:700, textDecoration:'none' }}
                onClick={e=>e.stopPropagation()}>
                ⬇ Download
              </a>
              <button onClick={e=>{ e.stopPropagation(); setDelConfirm(lightbox); setLightbox(null); }}
                style={{ background:'rgba(232,64,64,.2)', border:'1px solid rgba(232,64,64,.4)', borderRadius:8,
                         padding:'7px 14px', color:C.red, fontSize:12, fontWeight:700, cursor:'pointer' }}>
                🗑 Delete
              </button>
              <button onClick={()=>setLightbox(null)}
                style={{ background:'rgba(255,255,255,.1)', border:'1px solid rgba(255,255,255,.2)', borderRadius:8,
                         padding:'7px 14px', color:'white', fontSize:14, cursor:'pointer' }}>
                ✕
              </button>
            </div>
          </div>

          {/* Image */}
          <img src={lightbox.uri} alt={lightbox.name}
            style={{ maxWidth:'90vw', maxHeight:'80vh', objectFit:'contain', borderRadius:8,
                     boxShadow:'0 24px 80px rgba(0,0,0,.8)' }}
            onClick={e=>e.stopPropagation()} />

          {/* Prev / Next */}
          {photos.findIndex(p=>p.id===lightbox.id) > 0 && (
            <button onClick={e=>{ e.stopPropagation(); navigate(-1); }}
              style={{ position:'absolute', left:20, top:'50%', transform:'translateY(-50%)',
                       background:'rgba(255,255,255,.1)', border:'1px solid rgba(255,255,255,.2)',
                       borderRadius:10, padding:'12px 16px', color:'white', fontSize:22,
                       cursor:'pointer', backdropFilter:'blur(4px)' }}>
              ‹
            </button>
          )}
          {photos.findIndex(p=>p.id===lightbox.id) < photos.length-1 && (
            <button onClick={e=>{ e.stopPropagation(); navigate(1); }}
              style={{ position:'absolute', right:20, top:'50%', transform:'translateY(-50%)',
                       background:'rgba(255,255,255,.1)', border:'1px solid rgba(255,255,255,.2)',
                       borderRadius:10, padding:'12px 16px', color:'white', fontSize:22,
                       cursor:'pointer', backdropFilter:'blur(4px)' }}>
              ›
            </button>
          )}

          {/* Thumbnail strip */}
          {photos.length > 1 && (
            <div style={{ position:'absolute', bottom:0, left:0, right:0, padding:'12px 20px',
                          background:'linear-gradient(to top,rgba(0,0,0,.7),transparent)',
                          display:'flex', gap:8, justifyContent:'center', overflowX:'auto' }}
              onClick={e=>e.stopPropagation()}>
              {photos.map(p => (
                <img key={p.id} src={p.uri} alt={p.name} onClick={()=>setLightbox(p)}
                  style={{ width:56, height:42, objectFit:'cover', borderRadius:6, cursor:'pointer',
                           border:`2px solid ${p.id===lightbox.id?C.gold:'transparent'}`,
                           opacity:p.id===lightbox.id?1:.6, transition:'all .15s' }} />
              ))}
            </div>
          )}
        </div>
      )}

      {/* Delete confirm */}
      <Confirm open={!!delConfirm}
        title="Delete Photo"
        message={`Remove "${delConfirm?.name}" from this job? This cannot be undone.`}
        danger
        onConfirm={() => deletePhoto(delConfirm.id)}
        onCancel={() => setDelConfirm(null)} />
    </div>
  );
}

function JobDetailScreen({ params, onBack }) {
  const { jobs, updateJob, user, contractors } = useApp();
  const isAdmin = user?.role==='admin';
  const [tab, setTab] = useState('Overview');
  const [editClient, setEditClient]   = useState(false);
  const [editProject, setEditProject] = useState(false);
  const [editCrew, setEditCrew]       = useState(false);
  const [editStatus, setEditStatus]   = useState(false);
  const [showEstimate, setShowEstimate] = useState(false);
  const [cd, setCd] = useState(null);
  const [pd, setPd] = useState(null);
  const [confirm, setConfirm] = useState(null);

  const job = jobs.find(j => j.id === params.jobId);
  if (!job) return <div style={{ padding:40, color:C.muted }}>Job not found.</div>;

  const dp = DOCS_LIST.filter(d => job.docs[d.key]).length / DOCS_LIST.length * 100;
  const tabs = isAdmin
    ? ['Overview','Photos','Docs','Material/Labor','Schedule','Payments','Timeline']
    : ['Overview','Photos','Docs','Material/Labor','Schedule','Timeline'];

  const toggleDoc = key => updateJob({ ...job, docs:{ ...job.docs, [key]:!job.docs[key] } });
  const toggleCrew = name => {
    const crew = job.crew.includes(name) ? job.crew.filter(x=>x!==name) : [...job.crew,name];
    updateJob({ ...job, crew });
  };

  const tabBtn = (t) => (
    <button key={t} onClick={()=>setTab(t)}
      style={{ padding:'10px 18px', border:'none', borderBottom:`2.5px solid ${tab===t?C.gold:'transparent'}`,
               background:'transparent', color:tab===t?C.gold:C.muted, fontWeight:tab===t?700:500,
               fontSize:13, cursor:'pointer', whiteSpace:'nowrap' }}>
      {t}
    </button>
  );

  return (
    <div style={{ padding:'28px 0' }}>
      {/* Header */}
      <div style={{ ...s.row, marginBottom:24 }}>
        <div style={{ display:'flex', alignItems:'center', gap:14 }}>
          <button onClick={onBack} style={{ background:'none', border:'none', color:C.gold, fontSize:15, fontWeight:700, cursor:'pointer' }}>‹ Back</button>
          <div>
            <div style={s.mono}>{job.num}</div>
            <h1 style={{ ...s.h1, fontSize:22, marginTop:4 }}>{job.client.name}</h1>
          </div>
        </div>
        <div style={{ display:'flex', alignItems:'center', gap:12 }}>
          <Badge status={job.status} />
          <Btn label="📄 Estimate" v="outline" size="sm" onClick={()=>setShowEstimate(true)} />
          {isAdmin && <Btn label="Change Status" v="outline" size="sm" onClick={()=>setEditStatus(true)} />}
        </div>
      </div>

      {/* Financial snapshot — admin */}
      {isAdmin && (() => {
        const totalCostAmt = [...(job.expenses||[]), ...(job.costEntries||[]).filter(e=>!job.expenses?.some(x=>x.id===e.id))].reduce((a,e)=>a+parseFloat(e.amount||0),0);
        const grossProfit  = job.paid - totalCostAmt;
        return (
          <div style={{ marginBottom:24 }}>
            <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:10, marginBottom:10 }}>
              <Stat label="Estimate / Contract" value={`$${job.est.toLocaleString()}`} color={C.gold} />
              <Stat label="Paid" value={`$${job.paid.toLocaleString()}`} color={C.green} />
              <Stat label="Balance" value={`$${job.bal.toLocaleString()}`} color={job.bal>0?C.red:C.green} />
            </div>
            <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:10 }}>
              <Stat label="Total Cost" value={`$${Math.round(totalCostAmt).toLocaleString()}`} color={C.red} />
              <Stat label="Gross Profit" value={`$${Math.round(grossProfit).toLocaleString()}`} color={grossProfit>=0?C.teal:C.red} />
              <Stat label="Docs" value={`${Math.round(dp)}%`} color={dp===100?C.green:C.teal} />
            </div>
          </div>
        );
      })()}

      {/* Tabs */}
      <div style={{ display:'flex', borderBottom:`1px solid ${C.border}`, marginBottom:24, overflowX:'auto' }}>
        {tabs.map(tabBtn)}
      </div>

      {/* ── OVERVIEW ── */}
      {tab==='Overview' && (
        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:20 }}>
          {/* Client */}
          <Card>
            <div style={{ ...s.row, marginBottom:16 }}>
              <h3 style={{ fontWeight:700, color:C.text }}>Client</h3>
              <Btn label="✏️ Edit" v="outline" size="sm" onClick={()=>{ setCd({...job.client}); setEditClient(true); }} />
            </div>
            {editClient && cd ? (
              <div>
                <Field label="Name"    value={cd.name}    onChange={v=>setCd(x=>({...x,name:v}))} />
                <Field label="Email"   value={cd.email}   onChange={v=>setCd(x=>({...x,email:v}))} type="email" />
                <Field label="Phone"   value={cd.phone}   onChange={v=>setCd(x=>({...x,phone:v}))} />
                <Field label="Address" value={cd.address} onChange={v=>setCd(x=>({...x,address:v}))} />
                <div style={{ display:'flex', gap:10, marginTop:8 }}>
                  <Btn label="Cancel" v="ghost" onClick={()=>setEditClient(false)} />
                  <Btn label="Save" onClick={()=>{ updateJob({...job,client:cd}); setEditClient(false); }} />
                </div>
              </div>
            ) : (
              <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
                {[['Name',job.client.name],['Email',job.client.email],['Phone',job.client.phone],['Address',job.client.address]].map(([l,v])=>(
                  <div key={l}><span style={{ fontSize:11, color:C.muted, textTransform:'uppercase', fontWeight:700 }}>{l}</span>
                  <div style={{ marginTop:3, color:C.text, fontSize:13 }}>{v||'—'}</div></div>
                ))}
              </div>
            )}
          </Card>

          {/* Project */}
          <Card>
            <div style={{ ...s.row, marginBottom:16 }}>
              <h3 style={{ fontWeight:700, color:C.text }}>Project</h3>
              <div style={{ display:'flex', gap:8, alignItems:'center' }}>
                <span style={{ fontSize:12, fontWeight:700, color:job.project.jobType==='Insurance'?C.blueLt:C.gold,
                               background:job.project.jobType==='Insurance'?'rgba(58,123,213,.15)':'rgba(200,151,58,.12)',
                               borderRadius:999, padding:'3px 10px', cursor:'pointer' }}
                  onClick={()=>{ const next=job.project.jobType==='Insurance'?'Retail':'Insurance'; updateJob({...job,project:{...job.project,jobType:next}}); }}>
                  {job.project.jobType==='Insurance'?'🏛':'🛒'} {job.project.jobType||'Retail'}
                </span>
                <Btn label="✏️ Edit" v="outline" size="sm" onClick={()=>{ setPd({...job.project}); setEditProject(true); }} />
              </div>
            </div>
            {editProject && pd ? (
              <div>
                <Field label="Roof Type" value={pd.type} onChange={v=>setPd(x=>({...x,type:v}))} options={ROOF_TYPES} />
                <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
                  <Field label="Squares" value={pd.sq} onChange={v=>setPd(x=>({...x,sq:v}))} type="number" />
                  <Field label="Pitch"   value={pd.pitch} onChange={v=>setPd(x=>({...x,pitch:v}))} />
                </div>
                <Field label="Scope of Work" value={pd.desc} onChange={v=>setPd(x=>({...x,desc:v}))} multi />
                {pd.ins && <>
                  <Field label="Insurance Co" value={pd.insCo} onChange={v=>setPd(x=>({...x,insCo:v}))} />
                  <Field label="Claim #" value={pd.claimNum} onChange={v=>setPd(x=>({...x,claimNum:v}))} />
                  <Field label="Adjuster Name"  value={pd.adjName}  onChange={v=>setPd(x=>({...x,adjName:v}))} />
                  <Field label="Adjuster Email" value={pd.adjEmail} onChange={v=>setPd(x=>({...x,adjEmail:v}))} type="email" />
                  <Field label="Adjuster Phone" value={pd.adjPhone} onChange={v=>setPd(x=>({...x,adjPhone:v}))} />
                </>}
                <div style={{ display:'flex', gap:10, marginTop:8 }}>
                  <Btn label="Cancel" v="ghost" onClick={()=>setEditProject(false)} />
                  <Btn label="Save" onClick={()=>{ updateJob({...job,project:pd}); setEditProject(false); }} />
                </div>
              </div>
            ) : (
              <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
                {[['Type',job.project.type],['Squares',job.project.sq],['Pitch',job.project.pitch],['Stories',job.project.stories],['Scope',job.project.desc]].map(([l,v])=>(
                  <div key={l}><span style={{ fontSize:11, color:C.muted, textTransform:'uppercase', fontWeight:700 }}>{l}</span>
                  <div style={{ marginTop:3, color:C.text, fontSize:13 }}>{v||'—'}</div></div>
                ))}
                {job.project.ins && <>
                  <div style={{ height:1, background:C.border, margin:'8px 0' }} />
                  <div style={{ fontSize:12, fontWeight:700, color:C.blueLt }}>🏛 Insurance Details</div>
                  {[['Co',job.project.insCo],['Claim',job.project.claimNum],['Adjuster',job.project.adjName],['Adj. Email',job.project.adjEmail],['Adj. Phone',job.project.adjPhone]].map(([l,v])=>(
                    v ? <div key={l}><span style={{ fontSize:11, color:C.muted, textTransform:'uppercase', fontWeight:700 }}>{l}</span>
                    <div style={{ marginTop:2, color:C.text, fontSize:13 }}>{v}</div></div> : null
                  ))}
                </>}
              </div>
            )}
          </Card>

          {/* Crew — admin only */}
          {isAdmin && (
            <Card style={{ gridColumn:'1/-1' }}>
              <div style={{ ...s.row, marginBottom:16 }}>
                <h3 style={{ fontWeight:700, color:C.text }}>Crew Assignment</h3>
                <Btn label={editCrew?'✓ Done':'✏️ Edit'} v="outline" size="sm" onClick={()=>setEditCrew(!editCrew)} />
              </div>
              {editCrew ? (
                <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(200px,1fr))', gap:10 }}>
                  {(contractors||[]).filter(c=>c.active).map(c => {
                    const on = job.crew.includes(c.name);
                    const coiExp = c.coiExpiry && new Date(c.coiExpiry) < new Date();
                    return (
                      <div key={c.id} onClick={()=>toggleCrew(c.name)}
                        style={{ background:on?'rgba(200,151,58,.12)':C.panel, borderRadius:10, padding:'12px 14px',
                                 border:`1.5px solid ${on?C.gold:C.border}`, cursor:'pointer', display:'flex', gap:12, alignItems:'center' }}>
                        <div style={{ width:36, height:36, borderRadius:18, background:on?C.goldDim:C.panel,
                                      display:'flex', alignItems:'center', justifyContent:'center', fontWeight:800, color:on?C.gold:C.muted, border:`1px solid ${on?C.gold:C.border}` }}>
                          {c.name[0]}
                        </div>
                        <div>
                          <div style={{ fontWeight:700, color:on?C.gold:C.text, fontSize:13 }}>{c.name}</div>
                          <div style={{ fontSize:11, color:C.faint }}>{c.specialty}</div>
                          {coiExp && <div style={{ fontSize:10, color:C.red, fontWeight:700 }}>⚠️ COI Expired</div>}
                        </div>
                        {on && <span style={{ marginLeft:'auto', color:C.gold }}>✓</span>}
                      </div>
                    );
                  })}
                </div>
              ) : job.crew.length > 0 ? (
                <div style={{ display:'flex', flexWrap:'wrap', gap:8 }}>
                  {job.crew.map(c => (
                    <span key={c} style={{ background:'rgba(200,151,58,.12)', color:C.gold, borderRadius:999,
                                           padding:'6px 14px', fontSize:12, fontWeight:700, border:`1px solid rgba(200,151,58,.3)` }}>
                      👷 {c}
                    </span>
                  ))}
                </div>
              ) : (
                <div style={{ color:C.faint, fontSize:13, fontStyle:'italic' }}>No crew assigned. Tap Edit to assign crew.</div>
              )}
            </Card>
          )}
        </div>
      )}

      {/* ── DOCS ── */}
      {tab==='Docs' && (
        <DocsTab job={job} updateJob={updateJob} toggleDoc={toggleDoc} dp={dp} />
      )}

      {/* ── PHOTOS ── */}
      {tab==='Photos' && (
        <PhotosTab job={job} updateJob={updateJob} />
      )}

      {/* ── MATERIAL/LABOR ── */}
      {tab==='Material/Labor' && (
        <MaterialLaborTab job={job} updateJob={updateJob} />
      )}

      {/* ── SCHEDULE ── */}
      {tab==='Schedule' && (
        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:20 }}>
          <Card>
            <h3 style={{ fontWeight:700, marginBottom:16 }}>Project Dates</h3>
            <Field label="Start Date" value={job.start} onChange={v=>updateJob({...job,start:v})} type="date" />
            <Field label="End Date"   value={job.end}   onChange={v=>updateJob({...job,end:v})}   type="date" />
          </Card>
          <Card>
            <h3 style={{ fontWeight:700, marginBottom:16 }}>Assigned Crew</h3>
            {job.crew.length > 0 ? (
              <div style={{ display:'flex', flexWrap:'wrap', gap:8 }}>
                {job.crew.map(c => (
                  <span key={c} style={{ background:'rgba(200,151,58,.12)', color:C.gold, borderRadius:999, padding:'6px 14px', fontSize:12, fontWeight:700 }}>👷 {c}</span>
                ))}
              </div>
            ) : <div style={{ color:C.faint, fontSize:13 }}>No crew assigned</div>}
          </Card>
        </div>
      )}

      {/* ── PAYMENTS ── */}
      {tab==='Payments' && isAdmin && (
        <FinancialTab job={job} updateJob={updateJob} />
      )}

      {/* ── TIMELINE ── */}
      {tab==='Timeline' && (
        <Card style={{ maxWidth:560 }}>
          <h3 style={{ fontWeight:700, marginBottom:20 }}>Job Timeline</h3>
          <div style={{ paddingLeft:24, borderLeft:`2px solid ${C.border}` }}>
            {job.timeline.map((t,i) => (
              <div key={i} style={{ marginBottom:20, position:'relative' }}>
                <div style={{ position:'absolute', left:-30, top:3, width:12, height:12, borderRadius:6,
                              background:i===job.timeline.length-1?C.gold:C.borderB, border:`2px solid ${C.night}` }} />
                <div style={{ fontSize:11, color:C.muted, fontFamily:'monospace', marginBottom:4 }}>{t.date}</div>
                <div style={{ fontSize:13, color:C.text }}>{t.event}</div>
              </div>
            ))}
          </div>
          <div style={{ marginTop:20 }}>
            <AddTimelineEvent job={job} />
          </div>
        </Card>
      )}

      {/* Status change modal */}
      <Modal open={editStatus} onClose={()=>setEditStatus(false)} title="Update Job Status" width={440}>
        <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
          {JOB_STATUSES.map(s2 => {
            const sc = STATUS_C[s2]||{ bg:C.panel, fg:C.muted };
            const isCur = job.status===s2;
            return (
              <button key={s2} onClick={()=>{ updateJob({...job,status:s2,closed:s2==='Paid - Closed'}); setEditStatus(false); }}
                style={{ background:isCur?sc.bg:C.panel, border:`1.5px solid ${isCur?sc.fg:C.border}`,
                         borderRadius:10, padding:'12px 16px', cursor:'pointer', display:'flex', alignItems:'center', gap:12 }}>
                <div style={{ width:10, height:10, borderRadius:5, background:sc.fg }} />
                <span style={{ fontWeight:700, color:isCur?sc.fg:C.text, flex:1, textAlign:'left' }}>{s2}</span>
                {isCur && <span style={{ fontSize:11, color:sc.fg }}>● Current</span>}
              </button>
            );
          })}
        </div>
      </Modal>

      <Confirm {...(confirm||{})} open={!!confirm} onCancel={()=>setConfirm(null)} />
      <EstimateModal open={showEstimate} onClose={()=>setShowEstimate(false)} source={job}
        onSave={d=>updateJob({ ...job, est:d.total, estimateData:d, bal:Math.max(0, d.total - (job.paid||0)) })} />
    </div>
  );
}

function AddTimelineEvent({ job }) {
  const { updateJob } = useApp();
  const [val, setVal] = useState('');
  const add = () => {
    if (!val.trim()) return;
    const entry = { date:new Date().toISOString().slice(0,10), event:val.trim() };
    updateJob({ ...job, timeline:[...job.timeline, entry] });
    setVal('');
  };
  return (
    <div style={{ display:'flex', gap:10 }}>
      <input value={val} onChange={e=>setVal(e.target.value)} placeholder="Add timeline entry…"
        style={{ ...s.input, flex:1 }} onKeyDown={e=>{ if(e.key==='Enter') add(); }} />
      <Btn label="Add" onClick={add} size="sm" />
    </div>
  );
}

// ─────────────────────────────────────────────────────────
// LEADS SCREEN
// ─────────────────────────────────────────────────────────
function LeadsScreen({ push }) {
  const { leads, addLead } = useApp();
  const [q, setQ]   = useState('');
  const [fil, setFil] = useState('all');
  const [addModal, setAddModal] = useState(false);
  const [newLead, setNewLead] = useState({ name:'',phone:'',email:'',address:'',roofType:'',jobType:'Retail',source:'',notes:'',ins:false,claimNum:'',insCo:'',adjName:'',adjEmail:'',adjPhone:'' });
  const nl = (k,v) => setNewLead(x=>({...x,[k]:v}));

  const shown = leads.filter(l =>
    (fil==='all' || l.status===fil) &&
    (!q || l.name.toLowerCase().includes(q.toLowerCase()))
  );
  const pipeCounts = LEAD_STATUSES.reduce((a,s)=>({...a,[s]:leads.filter(l=>l.status===s).length}),{});

  function submit() {
    if (!newLead.name.trim()) return;
    addLead(newLead);
    setAddModal(false);
    setNewLead({ name:'',phone:'',email:'',address:'',roofType:'',jobType:'Retail',source:'',notes:'',ins:false,claimNum:'',insCo:'',adjName:'',adjEmail:'',adjPhone:'' });
  }

  return (
    <div style={{ padding:'32px 0' }}>
      <div style={{ ...s.row, marginBottom:20 }}>
        <h1 style={s.h1}>Leads <span style={{ fontSize:16, color:C.muted, fontWeight:400 }}>({leads.length})</span></h1>
        <Btn label="+ New Lead" onClick={()=>setAddModal(true)} />
      </div>

      {/* Pipeline bar */}
      <div style={{ display:'flex', gap:10, overflowX:'auto', paddingBottom:8, marginBottom:16 }}>
        {LEAD_STATUSES.map(st => {
          const sc = LEAD_STATUS_C[st]||{ bg:C.panel, fg:C.muted };
          return (
            <button key={st} onClick={()=>setFil(fil===st?'all':st)}
              style={{ background:fil===st?sc.bg:'rgba(255,255,255,.04)', border:`1.5px solid ${fil===st?sc.fg:C.border}`,
                       borderRadius:10, padding:'8px 16px', cursor:'pointer', textAlign:'center', flexShrink:0, minWidth:90 }}>
              <div style={{ fontSize:20, fontWeight:900, color:sc.fg }}>{pipeCounts[st]||0}</div>
              <div style={{ fontSize:10, fontWeight:700, color:fil===st?sc.fg:C.faint, marginTop:2 }}>{st}</div>
            </button>
          );
        })}
      </div>

      <input value={q} onChange={e=>setQ(e.target.value)} placeholder="Search leads…"
        style={{ ...s.input, maxWidth:320, marginBottom:16 }} />

      <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(380px,1fr))', gap:14 }}>
        {shown.map(lead => {
          const sc = LEAD_STATUS_C[lead.status]||{ bg:C.panel, fg:C.muted };
          return (
            <Card key={lead.id} onClick={()=>push('leadDetail',{ leadId:lead.id })}>
              <div style={{ ...s.row, marginBottom:10 }}>
                <div>
                  <div style={{ fontWeight:800, fontSize:15 }}>{lead.name}</div>
                  <div style={{ fontSize:12, color:C.muted, marginTop:2 }}>{lead.roofType} · {lead.source}</div>
                </div>
                <span style={{ background:sc.bg, color:sc.fg, borderRadius:999, padding:'4px 12px', fontSize:11, fontWeight:700 }}>{lead.status}</span>
              </div>
              <div style={{ fontSize:12, color:C.faint, marginBottom:10 }}>📍 {lead.address}</div>
              <div style={{ display:'flex', gap:8, flexWrap:'wrap' }}>
                <span style={{ background:lead.jobType==='Insurance'?'rgba(58,123,213,.15)':'rgba(200,151,58,.12)',
                               color:lead.jobType==='Insurance'?C.blueLt:C.gold,
                               border:`1px solid ${lead.jobType==='Insurance'?'rgba(58,123,213,.3)':'rgba(200,151,58,.3)'}`,
                               borderRadius:999, padding:'3px 10px', fontSize:10, fontWeight:700 }}>
                  {lead.jobType==='Insurance'?'🏛 Insurance':'🛒 Retail'}
                </span>
                <span style={{ background:lead.inspectionDone?'rgba(64,200,112,.15)':'rgba(255,255,255,.04)',
                               color:lead.inspectionDone?C.green:C.faint, borderRadius:999, padding:'3px 10px', fontSize:10, fontWeight:700 }}>
                  {lead.inspectionDone?'✓ Inspected':'Inspection Pending'}
                </span>
                <span style={{ background:lead.contractSigned?'rgba(64,200,112,.15)':'rgba(255,255,255,.04)',
                               color:lead.contractSigned?C.green:C.faint, borderRadius:999, padding:'3px 10px', fontSize:10, fontWeight:700 }}>
                  {lead.contractSigned?'✓ Signed':'Contract Pending'}
                </span>
              </div>
            </Card>
          );
        })}
        {shown.length===0 && (
          <div style={{ gridColumn:'1/-1', textAlign:'center', padding:'60px 0', color:C.muted }}>
            <div style={{ fontSize:48, marginBottom:12 }}>🎯</div>
            <div>No leads found</div>
          </div>
        )}
      </div>

      {/* Add Lead Modal */}
      <Modal open={addModal} onClose={()=>setAddModal(false)} title="New Lead" width={600}>
        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'0 20px' }}>
          <Field label="Client Name" value={newLead.name}    onChange={v=>nl('name',v)} required />
          <Field label="Phone"       value={newLead.phone}   onChange={v=>nl('phone',v)} />
          <Field label="Email"       value={newLead.email}   onChange={v=>nl('email',v)} type="email" />
          <Field label="Address"     value={newLead.address} onChange={v=>nl('address',v)} />
        </div>
        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'0 20px' }}>
          <div><label style={s.label}>Roof Type</label><Chips options={ROOF_TYPES} value={newLead.roofType} onChange={v=>nl('roofType',v)} /><div style={{ height:8 }} /></div>
          <div>
            <label style={s.label}>Job Type</label>
            <div style={{ display:'flex', gap:10, marginBottom:16 }}>
              {['Retail','Insurance'].map(jt=>(
                <button key={jt} onClick={()=>nl('jobType',jt)}
                  style={{ flex:1, background:newLead.jobType===jt?(jt==='Insurance'?'rgba(58,123,213,.15)':'rgba(200,151,58,.12)'):'rgba(255,255,255,.04)',
                           border:`1.5px solid ${newLead.jobType===jt?(jt==='Insurance'?C.blueLt:C.gold):C.border}`,
                           borderRadius:10, padding:12, cursor:'pointer', color:newLead.jobType===jt?(jt==='Insurance'?C.blueLt:C.gold):C.muted, fontWeight:700 }}>
                  {jt==='Insurance'?'🏛':'🛒'} {jt}
                </button>
              ))}
            </div>
          </div>
        </div>
        {newLead.jobType==='Insurance' && (
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'0 20px' }}>
            <Field label="Insurance Co"   value={newLead.insCo}    onChange={v=>nl('insCo',v)} />
            <Field label="Claim #"        value={newLead.claimNum} onChange={v=>nl('claimNum',v)} />
            <Field label="Adjuster Name"  value={newLead.adjName}  onChange={v=>nl('adjName',v)} />
            <Field label="Adjuster Email" value={newLead.adjEmail} onChange={v=>nl('adjEmail',v)} type="email" />
          </div>
        )}
        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'0 20px' }}>
          <Field label="Lead Source" value={newLead.source} onChange={v=>nl('source',v)}
            options={['Referral','Website','Google','Facebook','Door Knock','Sign','Other']} />
        </div>
        <Field label="Notes" value={newLead.notes} onChange={v=>nl('notes',v)} multi />
        <div style={{ display:'flex', gap:12, justifyContent:'flex-end', marginTop:8 }}>
          <Btn label="Cancel" v="ghost" onClick={()=>setAddModal(false)} />
          <Btn label="✓ Add Lead" onClick={submit} />
        </div>
      </Modal>
    </div>
  );
}

// ─────────────────────────────────────────────────────────
// LEAD DETAIL
// ─────────────────────────────────────────────────────────
function LeadDetailScreen({ params, onBack }) {
  const { leads, updateLead, deleteLead, convertLeadToJob } = useApp();
  const lead = leads.find(l => l.id === params?.leadId);
  const [editing, setEditing] = useState(false);
  const [statusM, setStatusM] = useState(false);
  const [showEstimate, setShowEstimate] = useState(false);
  const [f, setF]   = useState(null);
  const [confirm, setConfirm] = useState(null);
  const fu = (k,v) => setF(x=>({...x,[k]:v}));

  if (!lead) return <div style={{ padding:40, color:C.muted }}>Lead not found.</div>;

  const sc = LEAD_STATUS_C[lead.status]||{ bg:C.panel, fg:C.muted };

  function doConvert() {
    setConfirm({
      title:'Convert to Job',
      message:`Move ${lead.name} to active jobs? This removes them from Leads and creates a job with status "Estimate Sent".`,
      onConfirm:() => { convertLeadToJob(lead); setConfirm(null); onBack(); },
    });
  }

  function doDelete() {
    setConfirm({
      title:'Delete Lead',
      message:`Permanently remove ${lead.name}?`,
      danger:true,
      onConfirm:() => { deleteLead(lead.id); setConfirm(null); onBack(); },
    });
  }

  return (
    <div style={{ padding:'28px 0' }}>
      <div style={{ ...s.row, marginBottom:24 }}>
        <div style={{ display:'flex', alignItems:'center', gap:14 }}>
          <button onClick={onBack} style={{ background:'none', border:'none', color:C.gold, fontSize:15, fontWeight:700, cursor:'pointer' }}>‹ Back</button>
          <div>
            <div style={{ fontSize:11, color:C.muted }}>LEAD</div>
            <h1 style={{ ...s.h1, fontSize:22, marginTop:2 }}>{lead.name}</h1>
          </div>
        </div>
        <div style={{ display:'flex', gap:10 }}>
          <Btn label="📋 Status" v="outline" size="sm" onClick={()=>setStatusM(true)} />
          <Btn label="✏️ Edit" v="outline" size="sm" onClick={()=>{ setF({...lead}); setEditing(true); }} />
          <Btn label="📄 Estimate" v="outline" size="sm" onClick={()=>setShowEstimate(true)} />
          <Btn label="✅ Convert to Job" onClick={doConvert} />
          <Btn label="🗑" v="danger" size="sm" onClick={doDelete} />
        </div>
      </div>

      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:20 }}>
        {editing && f ? (
          <Card style={{ gridColumn:'1/-1' }}>
            <h3 style={{ fontWeight:700, color:C.gold, marginBottom:20 }}>Editing Lead</h3>
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'0 20px' }}>
              <Field label="Name"    value={f.name}    onChange={v=>fu('name',v)} required />
              <Field label="Phone"   value={f.phone}   onChange={v=>fu('phone',v)} />
              <Field label="Email"   value={f.email}   onChange={v=>fu('email',v)} type="email" />
              <Field label="Address" value={f.address} onChange={v=>fu('address',v)} />
            </div>
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'0 20px' }}>
              <Field label="Roof Type" value={f.roofType} onChange={v=>fu('roofType',v)} options={ROOF_TYPES} />
              <Field label="Source"    value={f.source}   onChange={v=>fu('source',v)}
                options={['Referral','Website','Google','Facebook','Door Knock','Sign','Other']} />
            </div>
            {f.jobType==='Insurance' && (
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'0 20px' }}>
                <Field label="Insurance Co"   value={f.insCo}    onChange={v=>fu('insCo',v)} />
                <Field label="Claim #"        value={f.claimNum} onChange={v=>fu('claimNum',v)} />
                <Field label="Adjuster Name"  value={f.adjName}  onChange={v=>fu('adjName',v)} />
                <Field label="Adjuster Email" value={f.adjEmail} onChange={v=>fu('adjEmail',v)} type="email" />
                <Field label="Adjuster Phone" value={f.adjPhone} onChange={v=>fu('adjPhone',v)} />
              </div>
            )}
            <Field label="Scope of Work / Notes" value={f.notes} onChange={v=>fu('notes',v)} multi />
            <Toggle label="🔍 Inspection Complete" checked={!!f.inspectionDone} onChange={v=>fu('inspectionDone',v)} />
            <Toggle label="✍️ CCR Agreement Signed" checked={!!f.contractSigned} onChange={v=>fu('contractSigned',v)} />
            <div style={{ display:'flex', gap:10, justifyContent:'flex-end', marginTop:8 }}>
              <Btn label="Cancel" v="ghost" onClick={()=>setEditing(false)} />
              <Btn label="💾 Save Changes" onClick={()=>{ updateLead(f); setEditing(false); }} />
            </div>
          </Card>
        ) : (
          <>
            <Card>
              <h3 style={{ fontWeight:700, marginBottom:16 }}>Contact</h3>
              {[['Name',lead.name],['Phone',lead.phone],['Email',lead.email],['Address',lead.address],['Source',lead.source],['Created',lead.createdAt]].map(([l,v])=>(
                <div key={l} style={{ marginBottom:12 }}>
                  <div style={{ fontSize:11, color:C.muted, textTransform:'uppercase', fontWeight:700, marginBottom:3 }}>{l}</div>
                  <div style={{ fontSize:13, color:C.text }}>{v||'—'}</div>
                </div>
              ))}
            </Card>

            <div style={{ display:'flex', flexDirection:'column', gap:16 }}>
              <Card>
                <h3 style={{ fontWeight:700, marginBottom:16 }}>Job Details</h3>
                <div style={{ display:'flex', gap:10, marginBottom:14, flexWrap:'wrap' }}>
                  <span style={{ background:lead.jobType==='Insurance'?'rgba(58,123,213,.15)':'rgba(200,151,58,.12)',
                                 color:lead.jobType==='Insurance'?C.blueLt:C.gold, borderRadius:999, padding:'4px 14px', fontSize:12, fontWeight:700 }}>
                    {lead.jobType==='Insurance'?'🏛 Insurance':'🛒 Retail'}
                  </span>
                  <span style={{ background:sc.bg, color:sc.fg, borderRadius:999, padding:'4px 14px', fontSize:12, fontWeight:700 }}>{lead.status}</span>
                </div>
                <div style={{ fontSize:11, color:C.muted, textTransform:'uppercase', fontWeight:700, marginBottom:4 }}>Roof Type</div>
                <div style={{ fontSize:13, marginBottom:14 }}>{lead.roofType||'—'}</div>
                {lead.notes && <>
                  <div style={{ fontSize:11, color:C.muted, textTransform:'uppercase', fontWeight:700, marginBottom:4 }}>Scope / Notes</div>
                  <div style={{ fontSize:13, color:C.text, fontStyle:'italic' }}>"{lead.notes}"</div>
                </>}
              </Card>

              <Card>
                <h3 style={{ fontWeight:700, marginBottom:14 }}>Progress</h3>
                <div onClick={()=>updateLead({...lead,inspectionDone:!lead.inspectionDone})}
                  style={{ display:'flex', alignItems:'center', gap:12, padding:'12px 14px', borderRadius:10, cursor:'pointer', marginBottom:10,
                           background:lead.inspectionDone?'rgba(64,200,112,.1)':C.panel,
                           border:`1.5px solid ${lead.inspectionDone?C.green:C.border}` }}>
                  <div style={{ width:22, height:22, borderRadius:6, border:`2px solid ${lead.inspectionDone?C.green:C.border}`,
                                background:lead.inspectionDone?C.green:'transparent', display:'flex', alignItems:'center', justifyContent:'center' }}>
                    {lead.inspectionDone && <span style={{ color:C.night, fontSize:12, fontWeight:900 }}>✓</span>}
                  </div>
                  <span style={{ fontWeight:700, color:lead.inspectionDone?C.green:C.text }}>🔍 Inspection Complete</span>
                </div>
                <div onClick={()=>updateLead({...lead,contractSigned:!lead.contractSigned})}
                  style={{ display:'flex', alignItems:'center', gap:12, padding:'12px 14px', borderRadius:10, cursor:'pointer',
                           background:lead.contractSigned?'rgba(64,200,112,.1)':C.panel,
                           border:`1.5px solid ${lead.contractSigned?C.green:C.border}` }}>
                  <div style={{ width:22, height:22, borderRadius:6, border:`2px solid ${lead.contractSigned?C.green:C.border}`,
                                background:lead.contractSigned?C.green:'transparent', display:'flex', alignItems:'center', justifyContent:'center' }}>
                    {lead.contractSigned && <span style={{ color:C.night, fontSize:12, fontWeight:900 }}>✓</span>}
                  </div>
                  <span style={{ fontWeight:700, color:lead.contractSigned?C.green:C.text }}>✍️ CCR Agreement Signed</span>
                </div>
              </Card>

              {lead.jobType==='Insurance' && (
                <Card>
                  <h3 style={{ fontWeight:700, marginBottom:14 }}>🏛 Insurance</h3>
                  {[['Co',lead.insCo],['Claim',lead.claimNum],['Adjuster',lead.adjName],['Email',lead.adjEmail],['Phone',lead.adjPhone]].map(([l,v])=>(
                    v ? <div key={l} style={{ marginBottom:10 }}>
                      <div style={{ fontSize:11, color:C.muted, textTransform:'uppercase', fontWeight:700, marginBottom:3 }}>{l}</div>
                      <div style={{ fontSize:13 }}>{v}</div>
                    </div> : null
                  ))}
                </Card>
              )}
            </div>
          </>
        )}
      </div>

      {/* Photos & Docs sections */}
      {!editing && <LeadPhotosSection lead={lead} updateLead={updateLead} />}
      {!editing && <LeadDocsSection   lead={lead} updateLead={updateLead} />}

      {/* Status modal */}
      <Modal open={statusM} onClose={()=>setStatusM(false)} title="Update Status" width={420}>
        <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
          {LEAD_STATUSES.map(st => {
            const sc2 = LEAD_STATUS_C[st]||{ bg:C.panel, fg:C.muted };
            const isCur = lead.status===st;
            return (
              <button key={st} onClick={()=>{ updateLead({...lead,status:st}); setStatusM(false); }}
                style={{ background:isCur?sc2.bg:C.panel, border:`1.5px solid ${isCur?sc2.fg:C.border}`,
                         borderRadius:10, padding:'12px 16px', cursor:'pointer', display:'flex', alignItems:'center', gap:12 }}>
                <div style={{ width:10, height:10, borderRadius:5, background:sc2.fg }} />
                <span style={{ fontWeight:700, color:isCur?sc2.fg:C.text, flex:1, textAlign:'left' }}>{st}</span>
                {isCur && <span style={{ fontSize:11, color:sc2.fg }}>● Current</span>}
              </button>
            );
          })}
        </div>
      </Modal>

      <Confirm {...(confirm||{})} open={!!confirm} onCancel={()=>setConfirm(null)} />
      <EstimateModal open={showEstimate} onClose={()=>setShowEstimate(false)} source={lead}
        onSave={d=>updateLead({ ...lead, estimatedAmt:d.total, estimateData:d })} />
    </div>
  );
}

// ─────────────────────────────────────────────────────────
// LEAD PHOTOS SECTION
// ─────────────────────────────────────────────────────────
function LeadPhotosSection({ lead, updateLead }) {
  const fileRef      = React.useRef();
  const [lightbox,   setLightbox]   = useState(null);
  const [delConfirm, setDelConfirm] = useState(null);
  const [dragging,   setDragging]   = useState(false);
  const photos = lead.photos || [];

  function formatSize(bytes) {
    if (!bytes) return '';
    if (bytes < 1048576) return `${(bytes/1024).toFixed(1)} KB`;
    return `${(bytes/1048576).toFixed(1)} MB`;
  }

  async function addPhotos(files) {
    const imageFiles = Array.from(files).filter(f => f.type.startsWith('image/'));
    if (!imageFiles.length) return;
    const previews = imageFiles.map(file => ({
      id: 'lph' + Date.now() + Math.random(),
      uri: URL.createObjectURL(file),
      name: file.name, size: file.size,
      addedAt: new Date().toISOString(), uploading: true,
    }));
    updateLead({ ...lead, photos: [...photos, ...previews] });

    const uploads = await Promise.all(imageFiles.map(async (file, i) => {
      const path = `leads/${lead.id}/photos/${previews[i].id}_${file.name.replace(/[^a-zA-Z0-9._-]/g,'_')}`;
      try {
        const { error } = await supabase.storage.from('job-files').upload(path, file, { upsert:true, contentType:file.type });
        if (!error) {
          const { data: urlData } = supabase.storage.from('job-files').getPublicUrl(path);
          return { ...previews[i], uri: urlData.publicUrl, url: urlData.publicUrl, uploading: false };
        }
      } catch(e) { console.warn('Lead photo upload failed:', e.message); }
      return { ...previews[i], uploading: false };
    }));

    updateLead({ ...lead, photos: [...photos, ...uploads] });
  }

  function deletePhoto(id) {
    updateLead({ ...lead, photos: photos.filter(p => p.id !== id) });
    setDelConfirm(null);
    if (lightbox?.id === id) setLightbox(null);
  }

  function navigate(dir) {
    const idx = photos.findIndex(p => p.id === lightbox.id);
    const next = photos[idx + dir];
    if (next) setLightbox(next);
  }

  return (
    <Card style={{ marginTop:20 }}>
      {/* Header */}
      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:16 }}>
        <div>
          <h3 style={{ fontWeight:700, color:C.text, margin:0 }}>
            📸 Photos {photos.length > 0 && <span style={{ fontSize:13, color:C.muted, fontWeight:400 }}>({photos.length})</span>}
          </h3>
          <div style={{ fontSize:12, color:C.faint, marginTop:3 }}>Site photos, damage, before/after</div>
        </div>
        <div style={{ display:'flex', gap:10 }}>
          <input ref={fileRef} type="file" accept="image/*" multiple style={{ display:'none' }}
            onChange={e => { addPhotos(e.target.files); e.target.value=''; }} />
          <Btn label="📷 Upload" size="sm" onClick={()=>fileRef.current.click()} />
        </div>
      </div>

      {/* Drop zone / grid */}
      <div
        onDragOver={e=>{ e.preventDefault(); setDragging(true); }}
        onDragLeave={()=>setDragging(false)}
        onDrop={e=>{ e.preventDefault(); setDragging(false); addPhotos(e.dataTransfer.files); }}
        style={{ border:`2px dashed ${dragging?C.gold:C.border}`, borderRadius:12,
                 padding: photos.length ? '14px' : '36px 20px',
                 background: dragging?'rgba(200,151,58,.06)':photos.length?'transparent':C.panel,
                 transition:'all .2s' }}>
        {photos.length === 0 ? (
          <div style={{ textAlign:'center', cursor:'pointer' }} onClick={()=>fileRef.current.click()}>
            <div style={{ fontSize:36, marginBottom:10 }}>📸</div>
            <div style={{ fontWeight:600, color:C.muted, marginBottom:4 }}>
              {dragging ? 'Drop photos here' : 'No photos yet'}
            </div>
            <div style={{ fontSize:12, color:C.faint }}>Drag &amp; drop or click Upload · JPG, PNG, HEIC</div>
          </div>
        ) : (
          <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(160px,1fr))', gap:10 }}>
            {photos.map(photo => (
              <div key={photo.id}
                style={{ position:'relative', borderRadius:10, overflow:'hidden', aspectRatio:'4/3',
                         background:C.panel, cursor:'pointer', boxShadow:'0 2px 8px rgba(0,0,0,.3)',
                         transition:'transform .15s', opacity:photo.uploading?.7:1 }}
                onMouseEnter={e=>e.currentTarget.style.transform='scale(1.03)'}
                onMouseLeave={e=>e.currentTarget.style.transform='scale(1)'}>
                <img src={photo.uri} alt={photo.name}
                  style={{ width:'100%', height:'100%', objectFit:'cover', display:'block' }}
                  onClick={()=>setLightbox(photo)} />
                {photo.uploading && (
                  <div style={{ position:'absolute', inset:0, background:'rgba(0,0,0,.5)',
                                display:'flex', alignItems:'center', justifyContent:'center' }}>
                    <div style={{ color:'white', fontSize:11, fontWeight:700 }}>Uploading…</div>
                  </div>
                )}
                <button onClick={e=>{ e.stopPropagation(); setDelConfirm(photo); }}
                  style={{ position:'absolute', top:6, right:6, background:'rgba(232,64,64,.85)',
                           border:'none', borderRadius:6, padding:'3px 7px', color:'white',
                           fontSize:12, cursor:'pointer' }}>🗑</button>
              </div>
            ))}
            {/* Add more tile */}
            <div onClick={()=>fileRef.current.click()}
              style={{ borderRadius:10, aspectRatio:'4/3', background:C.panel,
                       border:`2px dashed ${C.border}`, display:'flex', flexDirection:'column',
                       alignItems:'center', justifyContent:'center', cursor:'pointer',
                       transition:'all .15s', gap:6 }}
              onMouseEnter={e=>{ e.currentTarget.style.borderColor=C.gold; }}
              onMouseLeave={e=>{ e.currentTarget.style.borderColor=C.border; }}>
              <div style={{ fontSize:24, color:C.faint }}>+</div>
              <div style={{ fontSize:11, color:C.faint, fontWeight:600 }}>Add more</div>
            </div>
          </div>
        )}
      </div>

      {/* Lightbox */}
      {lightbox && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,.92)', zIndex:2000,
                      display:'flex', alignItems:'center', justifyContent:'center' }}
          onClick={()=>setLightbox(null)}>
          <div style={{ position:'absolute', top:0, left:0, right:0, padding:'16px 20px',
                        display:'flex', justifyContent:'space-between', alignItems:'center',
                        background:'linear-gradient(to bottom,rgba(0,0,0,.7),transparent)' }}
            onClick={e=>e.stopPropagation()}>
            <div>
              <div style={{ fontWeight:700, color:'white', fontSize:14 }}>{lightbox.name}</div>
              <div style={{ fontSize:11, color:'rgba(255,255,255,.6)', marginTop:2 }}>
                {formatSize(lightbox.size)} · {photos.findIndex(p=>p.id===lightbox.id)+1} of {photos.length}
              </div>
            </div>
            <div style={{ display:'flex', gap:10 }}>
              <a href={lightbox.uri} download={lightbox.name}
                style={{ background:'rgba(200,151,58,.2)', border:`1px solid ${C.gold}`, borderRadius:8,
                         padding:'7px 14px', color:C.gold, fontSize:12, fontWeight:700, textDecoration:'none' }}
                onClick={e=>e.stopPropagation()}>
                ⬇ Download
              </a>
              <button onClick={()=>setLightbox(null)}
                style={{ background:'rgba(255,255,255,.1)', border:'1px solid rgba(255,255,255,.2)',
                         borderRadius:8, padding:'7px 14px', color:'white', fontSize:14, cursor:'pointer' }}>
                ✕
              </button>
            </div>
          </div>

          <img src={lightbox.uri} alt={lightbox.name}
            style={{ maxWidth:'90vw', maxHeight:'82vh', objectFit:'contain', borderRadius:8 }}
            onClick={e=>e.stopPropagation()} />

          {photos.findIndex(p=>p.id===lightbox.id) > 0 && (
            <button onClick={e=>{ e.stopPropagation(); navigate(-1); }}
              style={{ position:'absolute', left:20, top:'50%', transform:'translateY(-50%)',
                       background:'rgba(255,255,255,.1)', border:'1px solid rgba(255,255,255,.2)',
                       borderRadius:10, padding:'12px 16px', color:'white', fontSize:22, cursor:'pointer' }}>
              ‹
            </button>
          )}
          {photos.findIndex(p=>p.id===lightbox.id) < photos.length-1 && (
            <button onClick={e=>{ e.stopPropagation(); navigate(1); }}
              style={{ position:'absolute', right:20, top:'50%', transform:'translateY(-50%)',
                       background:'rgba(255,255,255,.1)', border:'1px solid rgba(255,255,255,.2)',
                       borderRadius:10, padding:'12px 16px', color:'white', fontSize:22, cursor:'pointer' }}>
              ›
            </button>
          )}
        </div>
      )}

      <Confirm open={!!delConfirm}
        title="Delete Photo"
        message={`Remove "${delConfirm?.name}"?`}
        danger
        onConfirm={()=>deletePhoto(delConfirm.id)}
        onCancel={()=>setDelConfirm(null)} />
    </Card>
  );
}

// ─────────────────────────────────────────────────────────
// LEAD DOCS SECTION
// ─────────────────────────────────────────────────────────
function LeadDocsSection({ lead, updateLead }) {
  const fileRef      = React.useRef();
  const [delConfirm, setDelConfirm] = useState(null);
  const [dragging,   setDragging]   = useState(false);
  const docs = lead.uploadedDocs || [];

  function fileIcon(mime='') {
    if (mime.includes('pdf'))   return '📄';
    if (mime.includes('image')) return '🖼️';
    if (mime.includes('word') || mime.includes('document')) return '📝';
    if (mime.includes('sheet') || mime.includes('excel'))   return '📊';
    return '📎';
  }

  function formatSize(bytes) {
    if (!bytes) return '';
    if (bytes < 1048576) return `${(bytes/1024).toFixed(1)} KB`;
    return `${(bytes/1048576).toFixed(1)} MB`;
  }

  async function addDocs(files) {
    if (!files?.length) return;
    const newDocs = await Promise.all(Array.from(files).map(async file => {
      const id   = 'ldoc' + Date.now() + Math.random();
      const path = `leads/${lead.id}/docs/${id}_${file.name.replace(/[^a-zA-Z0-9._-]/g,'_')}`;
      const preview = {
        id, name: file.name, mimeType: file.type, size: file.size,
        addedAt: new Date().toISOString(),
        uri: URL.createObjectURL(file), uploading: true,
      };
      try {
        const { error } = await supabase.storage.from('job-files').upload(path, file, { upsert:true, contentType:file.type });
        if (!error) {
          const { data: urlData } = supabase.storage.from('job-files').getPublicUrl(path);
          return { ...preview, uri: urlData.publicUrl, url: urlData.publicUrl, uploading: false };
        }
      } catch(e) { console.warn('Lead doc upload failed:', e.message); }
      return { ...preview, uploading: false };
    }));
    updateLead({ ...lead, uploadedDocs: [...docs, ...newDocs] });
  }

  function shareDoc(doc) {
    const url = doc.url || doc.uri;
    if (navigator.share && url) {
      if (url.startsWith('http')) {
        navigator.share({ title: doc.name, url }).catch(() => downloadDoc(doc));
      } else {
        fetch(url).then(r=>r.blob()).then(blob => {
          const file = new File([blob], doc.name, { type: doc.mimeType||'application/octet-stream' });
          if (navigator.canShare?.({ files:[file] })) {
            navigator.share({ title: doc.name, files:[file] }).catch(()=>downloadDoc(doc));
          } else {
            navigator.share({ title: doc.name, url }).catch(()=>downloadDoc(doc));
          }
        }).catch(()=>downloadDoc(doc));
      }
    } else { downloadDoc(doc); }
  }

  function downloadDoc(doc) {
    const a = document.createElement('a');
    a.href = doc.url || doc.uri;
    a.download = doc.name;
    a.target = '_blank';
    a.click();
  }

  function deleteDoc(id) {
    updateLead({ ...lead, uploadedDocs: docs.filter(d => d.id !== id) });
    setDelConfirm(null);
  }

  return (
    <Card style={{ marginTop:16 }}>
      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:16 }}>
        <div>
          <h3 style={{ fontWeight:700, color:C.text, margin:0 }}>
            📁 Documents {docs.length > 0 && <span style={{ fontSize:13, color:C.muted, fontWeight:400 }}>({docs.length})</span>}
          </h3>
          <div style={{ fontSize:12, color:C.faint, marginTop:3 }}>Estimates, contracts, quotes, inspection reports</div>
        </div>
        <div>
          <input ref={fileRef} type="file" multiple style={{ display:'none' }}
            onChange={e=>{ addDocs(e.target.files); e.target.value=''; }} />
          <Btn label="📎 Upload" size="sm" onClick={()=>fileRef.current.click()} />
        </div>
      </div>

      {/* Drop zone */}
      <div
        onDragOver={e=>{ e.preventDefault(); setDragging(true); }}
        onDragLeave={()=>setDragging(false)}
        onDrop={e=>{ e.preventDefault(); setDragging(false); addDocs(e.dataTransfer.files); }}
        style={{ border:`2px dashed ${dragging?C.gold:C.border}`, borderRadius:12,
                 padding: docs.length?'12px':'32px 20px',
                 background: dragging?'rgba(200,151,58,.06)':docs.length?'transparent':C.panel,
                 transition:'all .2s' }}>
        {docs.length === 0 ? (
          <div style={{ textAlign:'center', cursor:'pointer' }} onClick={()=>fileRef.current.click()}>
            <div style={{ fontSize:32, marginBottom:8 }}>📁</div>
            <div style={{ fontWeight:600, color:C.muted, marginBottom:4 }}>
              {dragging ? 'Drop files here' : 'No documents yet'}
            </div>
            <div style={{ fontSize:12, color:C.faint }}>Drag &amp; drop or click Upload · PDF, Word, Excel, images</div>
          </div>
        ) : (
          <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
            {docs.map(doc => (
              <div key={doc.id} style={{ background:C.panel, borderRadius:10, padding:'10px 14px',
                                         display:'flex', alignItems:'center', gap:12,
                                         border:`1px solid ${C.border}`,
                                         opacity:doc.uploading?.7:1 }}>
                <div style={{ width:38, height:38, borderRadius:8, background:'rgba(200,151,58,.1)',
                              display:'flex', alignItems:'center', justifyContent:'center',
                              fontSize:20, flexShrink:0, border:`1px solid rgba(200,151,58,.2)` }}>
                  {fileIcon(doc.mimeType)}
                </div>
                <div style={{ flex:1, minWidth:0 }}>
                  <div style={{ fontWeight:600, fontSize:13, color:C.text,
                                overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                    {doc.uploading ? '⏳ ' : ''}{doc.name}
                  </div>
                  <div style={{ fontSize:11, color:C.faint, marginTop:2 }}>
                    {formatSize(doc.size)} · {new Date(doc.addedAt).toLocaleDateString('en-US',{month:'short',day:'numeric',year:'numeric'})}
                  </div>
                </div>
                <div style={{ display:'flex', gap:8, flexShrink:0 }}>
                  <button onClick={()=>shareDoc(doc)}
                    style={{ background:'rgba(58,123,213,.15)', border:`1px solid rgba(58,123,213,.3)`,
                             borderRadius:8, padding:'6px 12px', color:C.blueLt,
                             fontSize:12, fontWeight:700, cursor:'pointer' }}>
                    📤 Share
                  </button>
                  <button onClick={()=>downloadDoc(doc)}
                    style={{ background:'rgba(200,151,58,.1)', border:`1px solid rgba(200,151,58,.25)`,
                             borderRadius:8, padding:'6px 10px', color:C.gold,
                             fontSize:12, fontWeight:700, cursor:'pointer' }}>
                    ⬇
                  </button>
                  <button onClick={()=>setDelConfirm(doc)}
                    style={{ background:C.redDim, border:`1px solid rgba(232,64,64,.3)`,
                             borderRadius:8, padding:'6px 9px', color:C.red,
                             fontSize:12, cursor:'pointer' }}>
                    🗑
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <Confirm open={!!delConfirm}
        title="Delete Document"
        message={`Remove "${delConfirm?.name}"?`}
        danger
        onConfirm={()=>deleteDoc(delConfirm.id)}
        onCancel={()=>setDelConfirm(null)} />
    </Card>
  );
}

// ─────────────────────────────────────────────────────────
// SCHEDULE
// ─────────────────────────────────────────────────────────
function SchedScreen() {
  const { jobs } = useApp();
  const sched = jobs.filter(j => j.start && !j.closed);
  return (
    <div style={{ padding:'32px 0' }}>
      <h1 style={{ ...s.h1, marginBottom:24 }}>Schedule</h1>
      {sched.length===0 && <div style={{ textAlign:'center', padding:'60px 0', color:C.muted }}><div style={{ fontSize:48 }}>📅</div><div style={{ marginTop:12 }}>No scheduled jobs</div></div>}
      <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(380px,1fr))', gap:14 }}>
        {sched.map(j => (
          <Card key={j.id}>
            <div style={{ ...s.row, marginBottom:12 }}>
              <div>
                <div style={s.mono}>{j.num}</div>
                <div style={{ fontWeight:800, fontSize:15, marginTop:3 }}>{j.client.name}</div>
                <div style={{ fontSize:12, color:C.muted, marginTop:2 }}>{j.client.address.split(',')[0]}</div>
              </div>
              <Badge status={j.status} />
            </div>
            <div style={{ height:1, background:C.border, marginBottom:12 }} />
            <div style={{ fontSize:12, color:C.muted }}>📅 {j.start} → {j.end||'TBD'}</div>
            <div style={{ fontSize:12, color:C.muted, marginTop:6 }}>👷 {j.crew.length?j.crew.join(', '):'Unassigned'}</div>
          </Card>
        ))}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────
// PAYMENTS
// ─────────────────────────────────────────────────────────
function PayScreen() {
  const { jobs } = useApp();
  const col = jobs.reduce((a,j)=>a+j.paid,0);
  const out = jobs.reduce((a,j)=>a+j.bal,0);
  return (
    <div style={{ padding:'32px 0' }}>
      <h1 style={{ ...s.h1, marginBottom:24 }}>Payments</h1>
      <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:14, marginBottom:28 }}>
        <Stat label="Collected YTD"   value={`$${col.toLocaleString()}`} color={C.green} />
        <Stat label="Outstanding"     value={`$${out.toLocaleString()}`} color={C.red} />
        <Stat label="Active Jobs"     value={jobs.filter(j=>!j.closed).length} color={C.gold} />
      </div>
      <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
        {jobs.filter(j=>j.paid>0).map(j => (
          <Card key={j.id}>
            <div style={s.row}>
              <div>
                <div style={s.mono}>{j.num}</div>
                <div style={{ fontWeight:700, marginTop:4 }}>{j.client.name}</div>
              </div>
              <div style={{ textAlign:'right' }}>
                <div style={{ fontWeight:800, color:C.green }}>${j.paid.toLocaleString()} collected</div>
                {j.bal>0 && <div style={{ fontSize:12, color:C.red, marginTop:4 }}>${j.bal.toLocaleString()} outstanding</div>}
              </div>
            </div>
          </Card>
        ))}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────
// CREW
// ─────────────────────────────────────────────────────────
function CrewScreen() {
  const { contractors, addContractor, updateContractor, deleteContractor } = useApp();
  const [modal, setModal] = useState(null);
  const [f, setF] = useState({ name:'',phone:'',email:'',ein:'',coi:'',coiExpiry:'',payMethod:'ACH',bankLast4:'',specialty:'',active:true });
  const fv = (k,v) => setF(x=>({...x,[k]:v}));
  const [confirm, setConfirm] = useState(null);

  function save() {
    if (!f.name.trim()) return;
    if (modal==='add') addContractor(f);
    else updateContractor(f);
    setModal(null);
  }

  return (
    <div style={{ padding:'32px 0' }}>
      <div style={{ ...s.row, marginBottom:24 }}>
        <h1 style={s.h1}>Crew / Contractors <span style={{ fontSize:13, color:C.muted, fontWeight:400 }}>1099 · Admin Only</span></h1>
        <Btn label="+ Add Contractor" onClick={()=>{ setF({ name:'',phone:'',email:'',ein:'',coi:'',coiExpiry:'',payMethod:'ACH',bankLast4:'',specialty:'',active:true }); setModal('add'); }} />
      </div>
      <div style={{ background:'rgba(42,184,165,.1)', border:'1px solid rgba(42,184,165,.3)', borderRadius:12, padding:'14px 18px', marginBottom:20, display:'flex', gap:12, alignItems:'center' }}>
        <span style={{ fontSize:22 }}>🏦</span>
        <div>
          <div style={{ fontWeight:700, color:C.teal }}>Bluevine ACH Payments</div>
          <div style={{ fontSize:12, color:C.muted, marginTop:2 }}>Contractor payments processed via ACH from your Bluevine business account.</div>
        </div>
      </div>
      <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(380px,1fr))', gap:14 }}>
        {contractors.map(c => {
          const coiExp = c.coiExpiry && new Date(c.coiExpiry) < new Date();
          return (
            <Card key={c.id}>
              <div style={{ ...s.row, marginBottom:14 }}>
                <div style={{ display:'flex', gap:12, alignItems:'center' }}>
                  <div style={{ width:44, height:44, borderRadius:22, background:c.active?'rgba(200,151,58,.2)':'rgba(255,255,255,.05)',
                                display:'flex', alignItems:'center', justifyContent:'center', fontWeight:900, fontSize:16, color:c.active?C.gold:C.muted, border:`1px solid ${c.active?C.gold:C.border}` }}>
                    {c.name[0]}
                  </div>
                  <div>
                    <div style={{ fontWeight:800, fontSize:15 }}>{c.name}</div>
                    <div style={{ fontSize:12, color:C.muted, marginTop:2 }}>{c.specialty}</div>
                  </div>
                </div>
                <span style={{ background:c.active?'rgba(64,200,112,.15)':'rgba(232,64,64,.15)', color:c.active?C.green:C.red, borderRadius:999, padding:'4px 12px', fontSize:11, fontWeight:700 }}>
                  {c.active?'Active':'Inactive'}
                </span>
              </div>
              <div style={{ fontSize:12, color:C.muted, display:'flex', flexDirection:'column', gap:5, marginBottom:14 }}>
                <div>📞 {c.phone} · 📧 {c.email}</div>
                <div>EIN: <span style={{ color:C.text, fontWeight:600 }}>{c.ein}</span></div>
                <div>COI: <span style={{ color:coiExp?C.red:C.text, fontWeight:600 }}>{c.coi}</span> · Expires: <span style={{ color:coiExp?C.red:C.muted }}>{c.coiExpiry}</span> {coiExp&&'⚠️ EXPIRED'}</div>
                <div>ACH Bank: ····{c.bankLast4}</div>
              </div>
              <div style={{ display:'flex', gap:10 }}>
                <Btn label="💳 ACH Pay" v="teal" size="sm"
                  onClick={()=>alert(`ACH payment queued for ${c.name} via Bluevine (Bank ····${c.bankLast4})`)} />
                <Btn label="✏️ Edit" v="outline" size="sm" onClick={()=>{ setF({...c}); setModal(c); }} />
                <Btn label="🗑" v="danger" size="sm"
                  onClick={()=>setConfirm({ title:'Remove Contractor', message:`Remove ${c.name}?`, danger:true, onConfirm:()=>{ deleteContractor(c.id); setConfirm(null); } })} />
              </div>
            </Card>
          );
        })}
      </div>

      <Modal open={!!modal} onClose={()=>setModal(null)} title={modal==='add'?'Add Contractor':'Edit Contractor'} width={560}>
        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'0 20px' }}>
          <Field label="Full Name *"     value={f.name}      onChange={v=>fv('name',v)} required />
          <Field label="Phone"           value={f.phone}     onChange={v=>fv('phone',v)} />
          <Field label="Email"           value={f.email}     onChange={v=>fv('email',v)} type="email" />
          <Field label="EIN (1099)"      value={f.ein}       onChange={v=>fv('ein',v)} />
          <Field label="COI Number"      value={f.coi}       onChange={v=>fv('coi',v)} />
          <Field label="COI Expiry"      value={f.coiExpiry} onChange={v=>fv('coiExpiry',v)} type="date" />
          <Field label="Specialty"       value={f.specialty} onChange={v=>fv('specialty',v)} />
          <Field label="Bank Last 4"     value={f.bankLast4} onChange={v=>fv('bankLast4',v)} />
        </div>
        <Toggle label="Active Contractor" checked={!!f.active} onChange={v=>fv('active',v)} />
        <div style={{ display:'flex', gap:12, justifyContent:'flex-end', marginTop:8 }}>
          <Btn label="Cancel" v="ghost" onClick={()=>setModal(null)} />
          <Btn label="💾 Save" onClick={save} />
        </div>
      </Modal>

      <Confirm {...(confirm||{})} open={!!confirm} onCancel={()=>setConfirm(null)} />
    </div>
  );
}

// ─────────────────────────────────────────────────────────
// ESTIMATE & ANALYSIS
// ─────────────────────────────────────────────────────────
// ─────────────────────────────────────────────────────────
// PROPERTY ANALYSIS SCREEN
// ─────────────────────────────────────────────────────────
function PropertyAnalysisScreen() {
  const [view,    setView]    = useState('form');
  const [geStep,  setGeStep]  = useState(1);   // Google Earth guided step 1-5
  const [calc,    setCalc]    = useState(null); // computed roof values
  const [report,  setReport]  = useState(null);

  // ── Form state ──
  const [f, setF] = useState({
    // Property
    address:'', ownerName:'', ownerContact:'', yearBuilt:'',
    sqFtLiving:'', bedsBaths:'', lotSize:'', lastSale:'', lastSaleDate:'', avm:'',
    // Google Earth inputs (what you actually measure)
    geFootprint:'',   // polygon area in sqft (flat footprint)
    gePitch:'8/12',   // pitch — measured on site or from listing
    geRidges:'',      // ruler: total ridge length (ft)
    geHips:'',        // ruler: total hip length (ft)
    geValleys:'',     // ruler: total valley length (ft)
    geEaves:'',       // ruler: total eave length (ft)
    geRakes:'',       // ruler: total rake length (ft)
    geFacets:'',      // count: number of visible roof planes
    // Project
    roofMaterial:'Asphalt Shingle',
    roofCondition:'Storm Damage — Full Replacement',
    jobType:'Insurance — Full Replacement',
    insCo:'', claimNum:'', adjName:'',
    retailTotal:'', insTotal:'',
    matCost:'225', labCost:'51',
    // Comps
    c1addr:'', c1price:'', c1det:'',
    c2addr:'', c2price:'', c2det:'',
    c3addr:'', c3price:'', c3det:'',
    // Scope
    scope:'Full tear-off and replacement of existing roofing system. Installation of synthetic underlayment, ice & water shield at valleys and eaves, new pipe boots, drip edge, ridge ventilation, and GAF Timberline HDZ architectural shingles. Permit pulled by CCR.',
    notes:'',
    preparedBy:'Capricorn Capital Roofing LLC',
  });
  const fv = (k,v) => { setF(x=>({...x,[k]:v})); setCalc(null); };

  // ── Pitch multiplier table ──
  const PITCH_MULT = {
    'Flat (1/12)':1.003,'2/12':1.014,'3/12':1.031,'4/12':1.054,
    '5/12':1.083,'6/12':1.118,'7/12':1.158,'8/12':1.202,
    '9/12':1.250,'10/12':1.302,'11/12':1.357,'12/12':1.414,
    'Steep (13/12+)':1.474,
  };
  const WASTE_BY_PITCH = {
    'Flat (1/12)':10,'2/12':10,'3/12':10,'4/12':10,
    '5/12':12,'6/12':12,'7/12':15,'8/12':15,
    '9/12':17,'10/12':20,'11/12':20,'12/12':22,'Steep (13/12+)':22,
  };

  // ── Auto-calculate everything from GE inputs ──
  function computeRoof() {
    const fp      = parseFloat(f.geFootprint) || 0;
    const mult    = PITCH_MULT[f.gePitch] || 1.202;
    const waste   = WASTE_BY_PITCH[f.gePitch] || 15;
    const area    = Math.round(fp * mult);
    const sq      = (area / 100);
    const sqBase  = Math.round(sq * 10) / 10;
    const wasteMult = 1 + waste/100;
    const sqWaste = Math.round(sq * wasteMult * 10) / 10;
    const ridges  = parseFloat(f.geRidges)  || 0;
    const hips    = parseFloat(f.geHips)    || 0;
    const valleys = parseFloat(f.geValleys) || 0;
    const eaves   = parseFloat(f.geEaves)   || 0;
    const rakes   = parseFloat(f.geRakes)   || 0;
    const facets  = parseInt(f.geFacets)    || 0;
    const sqNum   = sq;
    const matC    = parseFloat(f.matCost) || 225;
    const labC    = parseFloat(f.labCost) || 51;
    const totalCost = Math.round((matC + labC) * sqNum);
    const retailN   = parseFloat(f.retailTotal.replace(/[$,]/g,'')) || Math.round(460 * sqNum);
    const insN      = parseFloat(f.insTotal.replace(/[$,]/g,''))    || Math.round(550 * sqNum);
    const retMargin = retailN > 0 ? ((retailN-totalCost)/retailN*100).toFixed(1) : '0';
    const insMargin = insN    > 0 ? ((insN-totalCost)/insN*100).toFixed(1)       : '0';
    // Material quantities
    const bundlesShingle = Math.ceil(sqWaste * 3); // 3 bundles/SQ
    const rollsUnderly   = Math.ceil(sqWaste / 4); // ~4 SQ per roll
    const sqIceWater     = Math.ceil(eaves / 3 + valleys * 2 / 3); // eave rows + valley
    const dripEdgeSticks = Math.ceil((eaves + rakes) / 10);        // 10ft sticks
    const ridgeBundles   = Math.ceil((ridges + hips) / 35);        // 35 lin ft/bundle
    const valleySheets   = Math.ceil(valleys / 8);                  // 8ft valley metal

    setCalc({
      fp, mult, waste, area, sqBase, sqWaste, wasteMult,
      ridges, hips, valleys, eaves, rakes, facets,
      sqNum, matC, labC, totalCost, retailN, insN, retMargin, insMargin,
      bundlesShingle, rollsUnderly, sqIceWater, dripEdgeSticks, ridgeBundles, valleySheets,
      // waste table
      wasteTable: [10,12,15,17,20].map(w => ({
        pct: w,
        sq: Math.round(sq * (1+w/100) * 10)/10,
        sqft: Math.round(area * (1+w/100)),
        recommended: w === waste,
      })),
    });
  }

  function generateReport() {
    if (!calc) computeRoof();
    const c = calc || (() => { computeRoof(); return null; })();
    const today = new Date().toLocaleDateString('en-US',{year:'numeric',month:'long',day:'numeric'});
    setReport({ today, reportNum:'CCR-PA-'+Date.now().toString().slice(-6) });
    setView('report');
  }

  const fmt = n => '$'+parseFloat(n||0).toLocaleString('en-US',{minimumFractionDigits:2,maximumFractionDigits:2});

  function Field2({label, k, placeholder, type='text', note}) {
    return (
      <div style={{ marginBottom:14 }}>
        <label style={s.label}>{label}</label>
        <input type={type} value={f[k]} onChange={e=>fv(k,e.target.value)}
          placeholder={placeholder||''} style={{ ...s.input }} />
        {note && <div style={{ fontSize:11, color:C.faint, marginTop:4 }}>{note}</div>}
      </div>
    );
  }

  function Sel2({label, k, options, note}) {
    return (
      <div style={{ marginBottom:14 }}>
        <label style={s.label}>{label}</label>
        <select value={f[k]} onChange={e=>fv(k,e.target.value)} style={{ ...s.input }}>
          {options.map(o=><option key={o}>{o}</option>)}
        </select>
        {note && <div style={{ fontSize:11, color:C.faint, marginTop:4 }}>{note}</div>}
      </div>
    );
  }

  // ── GOOGLE EARTH GUIDE STEPS ──
  const GE_STEPS = [
    {
      num:1, icon:'🔍', title:'Find the Property',
      desc:'Open Google Earth Pro (free at earth.google.com). Type the property address in the search bar and press Enter.',
      tip:'Press 🅃 to tilt to top-down view. Go to View → Reset → Tilt & Compass if the image looks angled.',
      action: null,
    },
    {
      num:2, icon:'🔲', title:'Uncheck 3D Buildings & Measure Footprint',
      desc:'In the left panel, uncheck "3D Buildings." Then go to Tools → Ruler → Polygon tab. Trace the outer edge of the entire roof by clicking each corner. When done, click the starting point to close the polygon.',
      tip:'The Area field shows square feet automatically. Write it down.',
      action: (
        <div style={{ marginTop:14 }}>
          <label style={s.label}>Footprint Area (sqft) — from GE polygon</label>
          <input type="number" value={f.geFootprint} onChange={e=>fv('geFootprint',e.target.value)}
            placeholder="e.g. 1756" style={{ ...s.input }} />
          <div style={{ fontSize:11, color:C.faint, marginTop:4 }}>This is the flat top-down area. The tool applies your pitch multiplier to get true roof area.</div>
        </div>
      ),
    },
    {
      num:3, icon:'📐', title:'Measure Linear Features',
      desc:'Switch to the Line tab in the Ruler tool. Measure each of these separately by drawing a line along each feature. Write down each length in feet.',
      tip:'Click once to start, once to end. The Length field shows feet. You can switch between imperial and metric in the dropdown.',
      action: (
        <div style={{ marginTop:14 }}>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
            {[
              ['geRidges',  'Ridges (ft)',          'Horizontal peak lines at the top'],
              ['geHips',    'Hips (ft)',             'Diagonal lines where two slopes meet going down'],
              ['geValleys', 'Valleys (ft)',          'Diagonal lines where two slopes meet going up (inside corners)'],
              ['geEaves',   'Eaves (ft)',            'Bottom horizontal edges of the roof'],
              ['geRakes',   'Rakes (ft)',            'Diagonal edges on gable ends'],
              ['geFacets',  'Number of Facets',     'Count each visible roof plane (section)'],
            ].map(([k, lbl, hint]) => (
              <div key={k}>
                <label style={s.label}>{lbl}</label>
                <input type="number" value={f[k]} onChange={e=>fv(k,e.target.value)}
                  placeholder="0" style={{ ...s.input }} />
                <div style={{ fontSize:10, color:C.faint, marginTop:3 }}>{hint}</div>
              </div>
            ))}
          </div>
        </div>
      ),
    },
    {
      num:4, icon:'📏', title:'Enter the Pitch',
      desc:'Pitch cannot be seen from satellite view. Get it from: (1) the property listing on Redfin/Zillow, (2) the homeowner, (3) county permit records, or (4) measure on-site with a pitch gauge or app.',
      tip:'Most residential homes in Georgia are 6/12 to 8/12. Steep or complex roofs tend to be 9/12+.',
      action: (
        <div style={{ marginTop:14 }}>
          <Sel2 label="Roof Pitch" k="gePitch"
            options={Object.keys(PITCH_MULT)}
            note={`Pitch multiplier: ${(PITCH_MULT[f.gePitch]||1.202).toFixed(4)} · Recommended waste: ${WASTE_BY_PITCH[f.gePitch]||15}%`} />
        </div>
      ),
    },
    {
      num:5, icon:'✅', title:'Review Auto-Calculations',
      desc:'The tool will now calculate your true roof area, squares, waste tables, and material quantities — the same output as a Roofr report.',
      tip:'Always double-check linear measurements on steep or complex roofs. GE accuracy is typically within 5–10%.',
      action: calc ? (
        <div style={{ marginTop:14 }}>
          <div style={{ background:'rgba(64,200,112,.08)', border:`1px solid rgba(64,200,112,.3)`,
                        borderRadius:12, padding:'16px 18px' }}>
            <div style={{ fontWeight:800, color:C.green, marginBottom:12 }}>✓ Calculated Results</div>
            <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:10 }}>
              {[
                ['Footprint', Math.round(calc.fp).toLocaleString()+' sqft'],
                ['True Roof Area', calc.area.toLocaleString()+' sqft'],
                ['Base Squares', calc.sqBase+' SQ'],
                ['Pitch Multiplier', calc.mult.toFixed(4)+'x'],
                ['Rec. Waste', calc.waste+'%'],
                ['SQ w/ Waste', calc.sqWaste+' SQ'],
              ].map(([l,v])=>(
                <div key={l} style={{ background:C.panel, borderRadius:8, padding:'10px 12px' }}>
                  <div style={{ fontSize:10, color:C.faint, textTransform:'uppercase', letterSpacing:.7, marginBottom:3 }}>{l}</div>
                  <div style={{ fontWeight:800, color:C.text, fontSize:14 }}>{v}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      ) : (
        <div style={{ marginTop:14 }}>
          <Btn label="⚡ Calculate from My Measurements" onClick={computeRoof} fullWidth />
        </div>
      ),
    },
  ];

  // ── FORM VIEW ──
  if (view === 'form') return (
    <div style={{ padding:'28px 0' }}>
      <h1 style={{ ...s.h1, marginBottom:4 }}>Property Analysis</h1>
      <div style={{ fontSize:13, color:C.muted, marginBottom:28 }}>
        Guided measurement workflow — Google Earth Pro + PropStream + Redfin
      </div>

      {/* ── GOOGLE EARTH GUIDE ── */}
      <Card style={{ marginBottom:20, border:`1.5px solid rgba(200,151,58,.3)` }}>
        <div style={{ display:'flex', alignItems:'center', gap:12, marginBottom:20 }}>
          <div style={{ fontSize:28 }}>🌍</div>
          <div>
            <div style={{ fontWeight:900, fontSize:16, color:C.gold }}>Google Earth Pro Measurement Guide</div>
            <div style={{ fontSize:12, color:C.muted, marginTop:2 }}>Free · Download at earth.google.com · Takes ~10 min per property</div>
          </div>
          <a href="https://earth.google.com" target="_blank" rel="noopener noreferrer"
            style={{ marginLeft:'auto', background:'rgba(200,151,58,.12)', border:`1px solid rgba(200,151,58,.3)`,
                     borderRadius:8, padding:'8px 16px', color:C.gold, fontSize:12,
                     fontWeight:700, textDecoration:'none', flexShrink:0 }}>
            Open Google Earth →
          </a>
        </div>

        {/* Step tabs */}
        <div style={{ display:'flex', gap:6, marginBottom:20, flexWrap:'wrap' }}>
          {GE_STEPS.map(st => (
            <button key={st.num} onClick={()=>setGeStep(st.num)}
              style={{ padding:'7px 14px', borderRadius:8, border:'none', cursor:'pointer',
                       background:geStep===st.num?C.gold:'rgba(255,255,255,.06)',
                       color:geStep===st.num?C.night:C.muted, fontWeight:700, fontSize:12 }}>
              {st.icon} Step {st.num}
            </button>
          ))}
        </div>

        {/* Active step */}
        {GE_STEPS.filter(st=>st.num===geStep).map(st => (
          <div key={st.num}>
            <div style={{ fontWeight:800, fontSize:15, color:C.text, marginBottom:8 }}>
              {st.icon} {st.title}
            </div>
            <div style={{ fontSize:13, color:C.muted, lineHeight:1.7, marginBottom:10 }}>{st.desc}</div>
            <div style={{ background:'rgba(200,151,58,.08)', border:`1px solid rgba(200,151,58,.2)`,
                          borderRadius:8, padding:'10px 14px', fontSize:12, color:C.gold }}>
              💡 {st.tip}
            </div>
            {st.action}
            <div style={{ display:'flex', justifyContent:'space-between', marginTop:16 }}>
              {geStep > 1
                ? <Btn label="‹ Previous" v="ghost" size="sm" onClick={()=>setGeStep(g=>g-1)} />
                : <div />}
              {geStep < 5
                ? <Btn label="Next Step ›" size="sm" onClick={()=>setGeStep(g=>g+1)} />
                : <Btn label="✓ Done — Fill Remaining Fields Below" size="sm"
                    onClick={()=>{ if(!calc)computeRoof(); }} />}
            </div>
          </div>
        ))}
      </Card>

      {/* Live calc preview */}
      {calc && (
        <div style={{ background:C.void, border:`1px solid ${C.border}`, borderRadius:14,
                      padding:'16px 20px', marginBottom:20 }}>
          <div style={{ fontWeight:800, color:C.gold, marginBottom:12, fontSize:13 }}>
            📐 Auto-Calculated Roof Data
          </div>
          <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:10, marginBottom:12 }}>
            {[
              ['True Roof Area', calc.area.toLocaleString()+' sqft', C.gold],
              ['Base Squares', calc.sqBase+' SQ', C.text],
              ['SQ w/ '+calc.waste+'% Waste', calc.sqWaste+' SQ', C.green],
              ['Pitch Mult.', calc.mult.toFixed(3)+'x', C.teal],
            ].map(([l,v,col])=>(
              <div key={l} style={{ background:C.panel, borderRadius:10, padding:'11px 13px', textAlign:'center' }}>
                <div style={{ fontSize:10, color:C.faint, textTransform:'uppercase', marginBottom:3, letterSpacing:.7 }}>{l}</div>
                <div style={{ fontWeight:900, fontSize:15, color:col }}>{v}</div>
              </div>
            ))}
          </div>
          {/* Waste table */}
          <div style={{ display:'grid', gridTemplateColumns:'repeat(5,1fr)', gap:6 }}>
            {calc.wasteTable.map(w=>(
              <div key={w.pct} style={{ borderRadius:8, padding:'8px 10px', textAlign:'center',
                                        border:`1.5px solid ${w.recommended?C.gold:C.border}`,
                                        background:w.recommended?'rgba(200,151,58,.1)':C.panel }}>
                <div style={{ fontSize:10, color:w.recommended?C.gold:C.muted, fontWeight:700 }}>
                  {w.pct}%{w.recommended?' ★':''}
                </div>
                <div style={{ fontSize:13, fontWeight:800, color:w.recommended?C.gold:C.text }}>{w.sq} SQ</div>
                <div style={{ fontSize:10, color:C.faint }}>{w.sqft.toLocaleString()} sqft</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── PROPERTY INFO ── */}
      <Card style={{ marginBottom:16 }}>
        <h3 style={{ fontWeight:800, marginBottom:16, color:C.gold }}>📍 Property Information</h3>
        <div style={{ fontSize:11, color:C.faint, marginBottom:14 }}>Source: PropStream · Redfin · Realtor.com</div>
        <Field2 label="Property Address *" k="address" placeholder="Full property address" />
        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:16 }}>
          <Field2 label="Owner Name" k="ownerName" placeholder="From PropStream" />
          <Field2 label="Owner Phone / Email" k="ownerContact" placeholder="Contact info" />
          <Field2 label="Year Built" k="yearBuilt" placeholder="e.g. 2005" />
          <Field2 label="Living Area (sqft)" k="sqFtLiving" placeholder="e.g. 2,450" />
          <Field2 label="Beds / Baths" k="bedsBaths" placeholder="e.g. 4 bd / 2.5 ba" />
          <Field2 label="Lot Size" k="lotSize" placeholder="e.g. 0.28 acres" />
          <Field2 label="Last Sale Price" k="lastSale" placeholder="e.g. $285,000" />
          <Field2 label="Last Sale Date" k="lastSaleDate" placeholder="e.g. Mar 2021" />
          <Field2 label="Est. Value (AVM)" k="avm" placeholder="From Redfin / PropStream" />
        </div>
      </Card>

      {/* ── PROJECT ── */}
      <Card style={{ marginBottom:16 }}>
        <h3 style={{ fontWeight:800, marginBottom:16, color:C.gold }}>📊 Project Details</h3>
        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:16 }}>
          <Field2 label="Roof Material" k="roofMaterial" placeholder="e.g. Asphalt Shingle" />
          <Sel2 label="Roof Condition" k="roofCondition"
            options={['Storm Damage — Full Replacement','End of Life — Full Replacement','Partial Damage — Repair','Good — Maintenance Only']} />
          <Sel2 label="Job Type" k="jobType"
            options={['Insurance — Full Replacement','Retail — Full Replacement','Retail — Repair']} />
          <Field2 label="Insurance Company" k="insCo" placeholder="e.g. State Farm" />
          <Field2 label="Claim Number" k="claimNum" placeholder="CLM-2025-0000" />
          <Field2 label="Adjuster Name" k="adjName" placeholder="Adjuster full name" />
          <Field2 label="Materials Cost/SQ ($)" k="matCost" placeholder="225" type="number"
            note="Used to calculate margin" />
          <Field2 label="Labor Cost/SQ ($)" k="labCost" placeholder="51" type="number"
            note="Used to calculate margin" />
          <Field2 label="Override Retail Total (optional)" k="retailTotal" placeholder="Auto: $460 × SQ" />
          <Field2 label="Override Insurance Total (optional)" k="insTotal" placeholder="Auto: $550 × SQ" />
        </div>
      </Card>

      {/* ── COMPS ── */}
      <Card style={{ marginBottom:16 }}>
        <h3 style={{ fontWeight:800, marginBottom:16, color:C.gold }}>💰 Market Comparables</h3>
        <div style={{ fontSize:11, color:C.faint, marginBottom:14 }}>Source: Redfin · Realtor.com · PropStream — up to 3 comps</div>
        {[['1','c1addr','c1price','c1det'],['2','c2addr','c2price','c2det'],['3','c3addr','c3price','c3det']].map(([n,ak,pk,dk])=>(
          <div key={n} style={{ background:C.panel, borderRadius:10, padding:'14px 16px', marginBottom:10, border:`1px solid ${C.border}` }}>
            <div style={{ fontWeight:700, fontSize:12, color:C.muted, marginBottom:10 }}>COMP {n}</div>
            <div style={{ display:'grid', gridTemplateColumns:'2fr 1fr 2fr', gap:12 }}>
              <Field2 label="Address" k={ak} placeholder="123 Main St, City, GA" />
              <Field2 label="Sale Price" k={pk} placeholder="$295,000" />
              <Field2 label="Details" k={dk} placeholder="4bd/2ba · 1,850 sqft · 2024" />
            </div>
          </div>
        ))}
      </Card>

      {/* ── SCOPE ── */}
      <Card style={{ marginBottom:24 }}>
        <h3 style={{ fontWeight:800, marginBottom:16, color:C.gold }}>📝 Scope & Notes</h3>
        <div style={{ marginBottom:14 }}>
          <label style={s.label}>Scope of Work</label>
          <textarea value={f.scope} onChange={e=>fv('scope',e.target.value)}
            rows={4} style={{ ...s.input, resize:'vertical' }} />
        </div>
        <div style={{ marginBottom:14 }}>
          <label style={s.label}>Field Notes / Inspection Observations</label>
          <textarea value={f.notes} onChange={e=>fv('notes',e.target.value)}
            rows={3} placeholder="Visible storm damage, hail hits, granule loss, flashing condition..."
            style={{ ...s.input, resize:'vertical' }} />
        </div>
        <Field2 label="Prepared By" k="preparedBy" placeholder="Your name" />
      </Card>

      {!calc && (
        <div style={{ background:'rgba(200,151,58,.08)', border:`1px solid rgba(200,151,58,.2)`,
                      borderRadius:12, padding:'14px 18px', marginBottom:16, fontSize:13, color:C.muted }}>
          ⚡ Enter your Google Earth measurements above and click Calculate before generating the report.
        </div>
      )}

      <div style={{ display:'flex', gap:12 }}>
        {!calc && <Btn label="⚡ Calculate Roof" v="outline" onClick={computeRoof} />}
        <Btn label="📄 Generate Property Analysis Report" onClick={()=>{ if(!calc)computeRoof(); setTimeout(()=>generateReport(),50); }} style={{ flex:1 }} size="lg" />
      </div>
    </div>
  );

  // ── REPORT VIEW ──
  const c = calc;
  const { today, reportNum } = report;

  return (
    <div style={{ padding:'28px 0' }}>
      <style>{`@media print { .no-print { display:none!important; } }`}</style>
      <div className="no-print" style={{ display:'flex', gap:12, marginBottom:24 }}>
        <Btn label="‹ Edit Data" v="outline" onClick={()=>setView('form')} />
        <Btn label="🖨 Print / Save PDF" onClick={()=>window.print()} />
        <span style={{ fontSize:12, color:C.muted, alignSelf:'center' }}>
          Print → Save as PDF → email or AirDrop to client or adjuster
        </span>
      </div>

      {/* HEADER */}
      <div style={{ background:'#0a0c10', padding:'28px 36px 22px', borderRadius:'14px 14px 0 0',
                    display:'flex', justifyContent:'space-between', alignItems:'flex-start' }}>
        <div>
          <img src="/logo.png" alt="CCR" style={{ height:68, width:'auto', marginBottom:8 }} />
          <div style={{ fontSize:11, color:'#6b7280', lineHeight:1.6 }}>
            info@capricorncapitalroofing.com · capricorncapitalroofing.com
          </div>
        </div>
        <div style={{ textAlign:'right' }}>
          <div style={{ fontSize:10, fontWeight:700, letterSpacing:2, textTransform:'uppercase', color:C.gold, opacity:.8, marginBottom:4 }}>Property Analysis Report</div>
          <div style={{ fontFamily:'monospace', fontSize:18, fontWeight:900, color:C.gold }}>{reportNum}</div>
          <div style={{ fontSize:11, color:'#6b7280', marginTop:4, lineHeight:1.7 }}>
            Prepared: {today}<br/>By: {f.preparedBy}
          </div>
        </div>
      </div>
      <div style={{ height:4, background:'linear-gradient(90deg,#c8973a,#e2b054,#c8973a)', marginBottom:24 }} />

      {/* PROPERTY */}
      <div style={{ fontWeight:800, fontSize:10, color:C.muted, textTransform:'uppercase', letterSpacing:1.5, marginBottom:12, display:'flex', alignItems:'center', gap:8 }}>
        Property Information <div style={{ flex:1, height:1, background:C.border }} />
      </div>
      <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:10, marginBottom:24 }}>
        <div style={{ gridColumn:'1/-1', background:'#f8f9fc', borderRadius:10, padding:'13px 15px', border:'1px solid #eaecf4' }}>
          <div style={{ fontSize:10, color:C.muted, textTransform:'uppercase', letterSpacing:.8, fontWeight:700, marginBottom:4 }}>Address</div>
          <div style={{ fontSize:15, fontWeight:800, color:C.text }}>{f.address||'—'}</div>
        </div>
        {[['Owner',f.ownerName],['Contact',f.ownerContact],['Year Built',f.yearBuilt],
          ['Living Area',f.sqFtLiving],['Beds / Baths',f.bedsBaths],['Lot Size',f.lotSize],
          ['Last Sale',f.lastSale,true],['Sale Date',f.lastSaleDate],['Est. Value (AVM)',f.avm,true]
        ].map(([l,v,gold])=>(
          <div key={l} style={{ background:'#f8f9fc', borderRadius:10, padding:'13px 15px', border:'1px solid #eaecf4' }}>
            <div style={{ fontSize:10, color:C.muted, textTransform:'uppercase', letterSpacing:.8, fontWeight:700, marginBottom:4 }}>{l}</div>
            <div style={{ fontSize:13, fontWeight:700, color:gold?'#92400e':C.text }}>{v||'—'}</div>
          </div>
        ))}
      </div>

      {/* ROOF METRICS */}
      <div style={{ fontWeight:800, fontSize:10, color:C.muted, textTransform:'uppercase', letterSpacing:1.5, marginBottom:12, display:'flex', alignItems:'center', gap:8 }}>
        Roof Measurements — Google Earth Pro + {f.gePitch} Pitch <div style={{ flex:1, height:1, background:C.border }} />
      </div>
      <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:10, marginBottom:16 }}>
        {[
          ['Footprint Area', c ? Math.round(c.fp).toLocaleString()+' sqft' : '—'],
          ['True Roof Area', c ? c.area.toLocaleString()+' sqft' : '—'],
          ['Base Squares',   c ? c.sqBase+' SQ' : '—'],
          ['Pitch',          f.gePitch],
          ['Facets',         f.geFacets||'—'],
          ['Pitch Mult.',    c ? c.mult.toFixed(4)+'x' : '—'],
          ['Rec. Waste',     c ? c.waste+'%' : '—'],
          ['SQ w/ Waste',    c ? c.sqWaste+' SQ' : '—'],
        ].map(([l,v])=>(
          <div key={l} style={{ background:C.void, borderRadius:10, padding:'13px 14px', textAlign:'center' }}>
            <div style={{ fontSize:10, color:'#6b7280', textTransform:'uppercase', letterSpacing:.7, marginBottom:4 }}>{l}</div>
            <div style={{ fontSize:16, fontWeight:900, color:C.gold }}>{v}</div>
          </div>
        ))}
      </div>

      {/* LINEAR MEASUREMENTS */}
      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10, marginBottom:20 }}>
        {[
          ['Eaves',         f.geEaves   ? f.geEaves+' ft'   : '—'],
          ['Rakes',         f.geRakes   ? f.geRakes+' ft'   : '—'],
          ['Ridges',        f.geRidges  ? f.geRidges+' ft'  : '—'],
          ['Hips',          f.geHips    ? f.geHips+' ft'    : '—'],
          ['Valleys',       f.geValleys ? f.geValleys+' ft' : '—'],
          ['Data Source',   'Google Earth Pro (free)'],
        ].map(([l,v])=>(
          <div key={l} style={{ display:'flex', justifyContent:'space-between', padding:'9px 14px',
                                background:C.panel, borderRadius:8, border:`1px solid ${C.border}` }}>
            <span style={{ fontSize:12, color:C.muted }}>{l}</span>
            <span style={{ fontSize:13, fontWeight:700, color:C.text }}>{v}</span>
          </div>
        ))}
      </div>

      {/* WASTE TABLE */}
      <div style={{ fontWeight:800, fontSize:10, color:C.muted, textTransform:'uppercase', letterSpacing:1.5, marginBottom:12, display:'flex', alignItems:'center', gap:8 }}>
        Material Quantities by Waste Factor <div style={{ flex:1, height:1, background:C.border }} />
      </div>
      <Card style={{ marginBottom:20 }}>
        <div style={{ fontWeight:700, fontSize:13, marginBottom:12, color:C.text }}>
          Shingle Squares — {f.roofMaterial} · Base: {c ? c.sqBase : '—'} SQ
        </div>
        <div style={{ display:'grid', gridTemplateColumns:'repeat(5,1fr)', gap:8, marginBottom:14 }}>
          {(c ? c.wasteTable : []).map(w=>(
            <div key={w.pct} style={{ borderRadius:10, padding:'12px 12px', textAlign:'center',
                                       border:`1.5px solid ${w.recommended?C.gold:C.border}`,
                                       background:w.recommended?'rgba(200,151,58,.08)':C.panel }}>
              <div style={{ fontSize:10, color:w.recommended?C.gold:C.muted, fontWeight:700, marginBottom:3 }}>
                {w.pct}%{w.recommended?' ★':''}
              </div>
              <div style={{ fontSize:15, fontWeight:900, color:w.recommended?C.gold:C.text }}>{w.sq} SQ</div>
              <div style={{ fontSize:10, color:C.faint, marginTop:2 }}>{w.sqft ? w.sqft.toLocaleString() : '—'} sqft</div>
            </div>
          ))}
        </div>
        {/* Material quantities */}
        {c && (
          <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:8 }}>
            {[
              ['Shingle Bundles', c.bundlesShingle+' bundles', '3 bundles = 1 SQ'],
              ['Underlayment', c.rollsUnderly+' rolls', '~4 SQ per roll'],
              ['Ice & Water', c.sqIceWater+' SQ', 'Eaves + valleys'],
              ['Drip Edge', c.dripEdgeSticks+' pieces', '10 ft sticks'],
              ['Ridge Bundles', c.ridgeBundles+' bundles', '35 lin ft per bundle'],
              ['Valley Metal', c.valleySheets+' sheets', '8 ft valley metal'],
            ].map(([l,v,note])=>(
              <div key={l} style={{ background:C.panel, borderRadius:8, padding:'10px 12px', border:`1px solid ${C.border}` }}>
                <div style={{ fontSize:10, color:C.faint, textTransform:'uppercase', letterSpacing:.7, marginBottom:3 }}>{l}</div>
                <div style={{ fontWeight:800, fontSize:14, color:C.text }}>{v}</div>
                <div style={{ fontSize:10, color:C.faint, marginTop:2 }}>{note}</div>
              </div>
            ))}
          </div>
        )}
      </Card>

      {/* COMPS */}
      {[[f.c1addr,f.c1price,f.c1det],[f.c2addr,f.c2price,f.c2det],[f.c3addr,f.c3price,f.c3det]].filter(([a])=>a).length > 0 && (
        <>
          <div style={{ fontWeight:800, fontSize:10, color:C.muted, textTransform:'uppercase', letterSpacing:1.5, marginBottom:12, display:'flex', alignItems:'center', gap:8 }}>
            Market Comparables <div style={{ flex:1, height:1, background:C.border }} />
          </div>
          <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:12, marginBottom:20 }}>
            {[[f.c1addr,f.c1price,f.c1det],[f.c2addr,f.c2price,f.c2det],[f.c3addr,f.c3price,f.c3det]].filter(([a])=>a).map(([addr,price,det],i)=>(
              <Card key={i}>
                <div style={{ fontWeight:700, fontSize:13, marginBottom:4 }}>{addr}</div>
                <div style={{ fontSize:20, fontWeight:900, color:C.green, marginBottom:8 }}>{price}</div>
                <div style={{ fontSize:12, color:C.muted, lineHeight:1.8 }}>{det}</div>
              </Card>
            ))}
          </div>
        </>
      )}

      {/* ESTIMATE */}
      <div style={{ fontWeight:800, fontSize:10, color:C.muted, textTransform:'uppercase', letterSpacing:1.5, marginBottom:12, display:'flex', alignItems:'center', gap:8 }}>
        Project Estimate <div style={{ flex:1, height:1, background:C.border }} />
      </div>
      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:14, marginBottom:24 }}>
        {[
          { label:'🛒 Retail Estimate', total: c ? c.retailN : 0, margin: c ? c.retMargin : '0',
            sub:`${c?c.sqNum.toFixed(1):'—'} SQ · $${c?(c.retailN/c.sqNum).toFixed(0):'—'}/SQ`,
            rows:[['Materials',c?fmt(c.matC*c.sqNum):'—'],['Labor',c?fmt(c.labC*c.sqNum):'—'],
                  ['Total Cost',c?fmt(c.totalCost):'—'],['Gross Profit',c?fmt(c.retailN-c.totalCost):'—']],
            bg:'#fffbf2', border:'rgba(200,151,58,.3)', color:'#92400e', lColor:'#a16207' },
          { label:'🏛 Insurance Estimate', total: c ? c.insN : 0, margin: c ? c.insMargin : '0',
            sub:`${c?c.sqNum.toFixed(1):'—'} SQ · $${c?(c.insN/c.sqNum).toFixed(0):'—'}/SQ · Includes O&P`,
            rows:[['Claim #',f.claimNum||'—'],['Insurance Co.',f.insCo||'—'],
                  ['Adjuster',f.adjName||'—'],['Gross Profit',c?fmt(c.insN-c.totalCost):'—']],
            bg:'#eff6ff', border:'rgba(58,123,213,.25)', color:'#1e40af', lColor:'#1d4ed8' },
        ].map(({label,total,margin,sub,rows,bg,border,color,lColor})=>(
          <div key={label} style={{ background:bg, border:`1px solid ${border}`, borderRadius:12, padding:'18px 20px' }}>
            <div style={{ fontSize:10, fontWeight:800, textTransform:'uppercase', letterSpacing:1, color, marginBottom:8 }}>{label}</div>
            <div style={{ fontSize:32, fontWeight:900, color, marginBottom:4 }}>{fmt(total)}</div>
            <div style={{ fontSize:12, color:lColor, marginBottom:14 }}>{sub}</div>
            {rows.map(([l,v],i)=>(
              <div key={l} style={{ display:'flex', justifyContent:'space-between', padding:'7px 0',
                                     borderBottom:i<rows.length-1?`1px solid ${border}`:'none', fontSize:13 }}>
                <span style={{ color:C.muted }}>{l}</span><span style={{ fontWeight:700 }}>{v}</span>
              </div>
            ))}
            <div style={{ display:'flex', justifyContent:'space-between', paddingTop:8, fontSize:14, fontWeight:900, color:parseFloat(margin)>=40?C.green:C.red }}>
              <span>Margin</span><span>{margin}%</span>
            </div>
          </div>
        ))}
      </div>

      {/* SCOPE */}
      <div style={{ fontWeight:800, fontSize:10, color:C.muted, textTransform:'uppercase', letterSpacing:1.5, marginBottom:12, display:'flex', alignItems:'center', gap:8 }}>
        Scope of Work <div style={{ flex:1, height:1, background:C.border }} />
      </div>
      <div style={{ background:'#fffbf2', border:'1px solid rgba(200,151,58,.25)', borderRadius:11, padding:'18px 20px', marginBottom:f.notes?16:24 }}>
        <div style={{ fontWeight:800, fontSize:13, color:'#92400e', marginBottom:10 }}>🏗 {f.roofMaterial} — {f.jobType}</div>
        {f.scope.split('\n').filter(Boolean).map((line,i)=>(
          <div key={i} style={{ display:'flex', gap:9, fontSize:13, color:'#374151', lineHeight:1.5, marginBottom:7 }}>
            <div style={{ width:6, height:6, borderRadius:3, background:C.gold, flexShrink:0, marginTop:5 }} />
            {line}
          </div>
        ))}
      </div>

      {/* NOTES */}
      {f.notes && (
        <div style={{ background:C.panel, borderRadius:11, padding:'16px 18px', border:`1px solid ${C.border}`,
                      fontSize:13, color:C.text, lineHeight:1.7, whiteSpace:'pre-wrap', marginBottom:24 }}>
          <div style={{ fontWeight:800, marginBottom:8, color:C.muted, fontSize:11, textTransform:'uppercase', letterSpacing:.8 }}>Field Notes</div>
          {f.notes}
        </div>
      )}

      {/* SIGNATURES */}
      <div style={{ fontWeight:800, fontSize:10, color:C.muted, textTransform:'uppercase', letterSpacing:1.5, marginBottom:16, display:'flex', alignItems:'center', gap:8 }}>
        Authorization <div style={{ flex:1, height:1, background:C.border }} />
      </div>
      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:40, marginBottom:32 }}>
        {[['Property Owner / Client', f.ownerName||'Property Owner'],['CCR Representative', f.preparedBy]].map(([l,n])=>(
          <div key={l}>
            <div style={{ fontSize:10, fontWeight:700, color:C.muted, textTransform:'uppercase', letterSpacing:.8, marginBottom:36 }}>{l}</div>
            <div style={{ borderBottom:'1.5px solid #d1d5db', marginBottom:5 }} />
            <div style={{ fontSize:11, color:C.muted }}>{n} &nbsp;&nbsp; Date: ___________</div>
          </div>
        ))}
      </div>

      {/* FOOTER */}
      <div style={{ background:'#0a0c10', padding:'18px 36px', borderRadius:'0 0 14px 14px',
                    display:'flex', justifyContent:'space-between', alignItems:'center' }}>
        <div>
          <div style={{ fontSize:12, fontWeight:800, color:C.gold }}>Capricorn Capital Roofing LLC</div>
          <div style={{ fontSize:11, color:'#4b5563', marginTop:2, lineHeight:1.6 }}>
            Atlanta, GA Metro Area · info@capricorncapitalroofing.com
          </div>
        </div>
        <div style={{ textAlign:'right', fontSize:11, color:'#4b5563', lineHeight:1.8 }}>
          {reportNum}<br/>Prepared {today}<br/>Google Earth Pro measurements
        </div>
      </div>

      <div className="no-print" style={{ display:'flex', gap:12, marginTop:24 }}>
        <Btn label="‹ Edit Data" v="outline" onClick={()=>setView('form')} />
        <Btn label="🖨 Print / Save PDF" onClick={()=>window.print()} />
      </div>
    </div>
  );
}

function EAScreen({ onBack }) {
  const { leads, updateLead } = useApp();
  const [selLead,      setSelLead]      = useState(null);
  const [jobType,      setJobType]      = useState('Retail');
  const [sq,           setSq]           = useState('20');
  const [pitch,        setPitch]        = useState('');
  const [stories,      setStories]      = useState('1');
  const [matCost,      setMatCost]      = useState('225');
  const [laborCost,    setLaborCost]    = useState('51');
  const [ratePerSq,    setRatePerSq]    = useState('460');
  const [targetMgn,    setTargetMgn]    = useState('40');
  const [estimate,     setEstimate]     = useState(null);
  const [showEstimate, setShowEstimate] = useState(false);

  function selectLead(l) {
    setSelLead(l);
    setJobType(l.jobType || 'Retail');
    setSq(l.sq || '20');
    const mp = MARKET_PRICES[l.roofType] || MARKET_PRICES['Asphalt Shingle'];
    setRatePerSq(String(l.jobType==='Insurance' ? mp.insurance : mp.retail));
    setEstimate(null);
  }

  function toggleJobType(jt) {
    setJobType(jt);
    const mp = selLead
      ? (MARKET_PRICES[selLead.roofType] || MARKET_PRICES['Asphalt Shingle'])
      : MARKET_PRICES['Asphalt Shingle'];
    setRatePerSq(String(jt==='Insurance' ? mp.insurance : mp.retail));
    setEstimate(null);
  }

  const minRate = () => {
    const cost = parseFloat(matCost||0) + parseFloat(laborCost||0);
    const tgt  = parseFloat(targetMgn||40) / 100;
    return tgt < 1 ? (cost / (1 - tgt)).toFixed(2) : '—';
  };

  function calculate() {
    const sqNum     = parseFloat(sq) || 20;
    const matNum    = parseFloat(matCost) || 0;
    const labNum    = parseFloat(laborCost) || 0;
    const rateNum   = parseFloat(ratePerSq) || 0;
    const tgtMargin = parseFloat(targetMgn) || 40;
    const pitchMult = pitch==='Steep (9/12+)' ? 1.15 : pitch==='Complex' ? 1.25 : 1.0;
    const storMult  = parseInt(stories) > 1 ? 1.08 : 1.0;
    const adjCostSq = (matNum + labNum) * pitchMult * storMult;
    const adjRateSq = rateNum * pitchMult * storMult;
    const totalCost = Math.round(adjCostSq * sqNum);
    const totalRev  = Math.round(adjRateSq * sqNum);
    const profit    = totalRev - totalCost;
    const margin    = totalRev > 0 ? (profit / totalRev) * 100 : 0;
    const meetsTarget = margin >= tgtMargin;
    setEstimate({ total:totalRev, cost:totalCost, profit, margin, meetsTarget, tgtMargin, sqNum, adjCostSq, adjRateSq });
    if (!meetsTarget && jobType==='Retail' && selLead) {
      if (window.confirm(`⚠️ Margin is ${margin.toFixed(1)}% — below the ${tgtMargin}% target.\n\nMark this lead as Non-Profitable?`)) {
        updateLead({ ...selLead, status:'Non-Profitable' });
      }
    }
  }

  const fmtC = v => `$${parseFloat(v||0).toLocaleString('en-US',{minimumFractionDigits:2,maximumFractionDigits:2})}`;

  return (
    <div style={{ padding:'28px 0' }}>
      <div style={{ display:'flex', alignItems:'center', gap:14, marginBottom:28 }}>
        <button onClick={onBack} style={{ background:'none', border:'none', color:C.gold, fontSize:15, fontWeight:700, cursor:'pointer' }}>‹ Back</button>
        <h1 style={{ ...s.h1, fontSize:24 }}>Estimate &amp; Analysis</h1>
      </div>

      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:20 }}>
        <div>
          {/* Lead selector */}
          <Card style={{ marginBottom:16 }}>
            <h3 style={{ fontWeight:700, marginBottom:14 }}>Select Lead</h3>
            <div style={{ display:'flex', flexDirection:'column', gap:8, maxHeight:260, overflowY:'auto' }}>
              {leads.map(l => (
                <button key={l.id} onClick={()=>selectLead(l)}
                  style={{ background:selLead?.id===l.id?'rgba(200,151,58,.15)':C.panel,
                           border:`1.5px solid ${selLead?.id===l.id?C.gold:C.border}`,
                           borderRadius:10, padding:'10px 14px', cursor:'pointer', textAlign:'left' }}>
                  <div style={{ fontWeight:700, color:selLead?.id===l.id?C.gold:C.text }}>{l.name}</div>
                  <div style={{ fontSize:11, color:C.muted, marginTop:3 }}>{l.roofType} · {l.jobType}</div>
                </button>
              ))}
            </div>
          </Card>

          {/* Job Type Toggle */}
          <Card style={{ marginBottom:16 }}>
            <h3 style={{ fontWeight:700, marginBottom:14 }}>Job Type</h3>
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10 }}>
              {['Retail','Insurance'].map(jt => (
                <button key={jt} onClick={()=>toggleJobType(jt)}
                  style={{ padding:'14px 0', borderRadius:12,
                           border:`2px solid ${jobType===jt?(jt==='Insurance'?C.blueLt:C.gold):C.border}`,
                           background:jobType===jt?(jt==='Insurance'?'rgba(58,123,213,.15)':'rgba(200,151,58,.15)'):'rgba(255,255,255,.04)',
                           cursor:'pointer', textAlign:'center', transition:'all .15s' }}>
                  <div style={{ fontSize:24, marginBottom:6 }}>{jt==='Insurance'?'🏛':'🛒'}</div>
                  <div style={{ fontWeight:800, fontSize:14, color:jobType===jt?(jt==='Insurance'?C.blueLt:C.gold):C.muted }}>{jt}</div>
                  {selLead && (
                    <div style={{ fontSize:11, color:C.faint, marginTop:4 }}>
                      ${jt==='Insurance'
                        ? (MARKET_PRICES[selLead.roofType]||MARKET_PRICES['Asphalt Shingle']).insurance
                        : (MARKET_PRICES[selLead.roofType]||MARKET_PRICES['Asphalt Shingle']).retail}/SQ market
                    </div>
                  )}
                </button>
              ))}
            </div>
          </Card>

          {/* Market reference */}
          {selLead && (
            <Card>
              <h3 style={{ fontWeight:700, marginBottom:14 }}>📊 Market Reference — {selLead.roofType}</h3>
              {[
                ['Material Cost/SQ', `$${(MARKET_PRICES[selLead.roofType]||MARKET_PRICES['Asphalt Shingle']).matCost}`, C.muted],
                ['Labor Rate/SQ',    `$${(MARKET_PRICES[selLead.roofType]||MARKET_PRICES['Asphalt Shingle']).laborRate}`, C.muted],
                ['Retail Market/SQ', `$${(MARKET_PRICES[selLead.roofType]||MARKET_PRICES['Asphalt Shingle']).retail}`, C.gold],
                ['Insurance Mkt/SQ', `$${(MARKET_PRICES[selLead.roofType]||MARKET_PRICES['Asphalt Shingle']).insurance}`, C.blueLt],
              ].map(([l,v,col])=>(
                <div key={l} style={{ display:'flex', justifyContent:'space-between', padding:'8px 0', borderBottom:`1px solid ${C.border}` }}>
                  <span style={{ fontSize:13, color:C.muted }}>{l}</span>
                  <span style={{ fontWeight:700, color:col }}>{v}</span>
                </div>
              ))}
            </Card>
          )}
        </div>

        <div>
          <Card style={{ marginBottom:16 }}>
            <h3 style={{ fontWeight:700, marginBottom:16 }}>Parameters</h3>
            <Field label="Squares (SQ)" value={sq} onChange={setSq} type="number" placeholder="20" />
            <label style={s.label}>Pitch</label>
            <Chips options={['Flat','4/12','5/12','6/12','7/12','Steep (9/12+)','Complex']} value={pitch} onChange={setPitch} />
            <label style={s.label}>Stories</label>
            <Chips options={['1','2','3+']} value={stories} onChange={setStories} />

            <div style={{ height:1, background:C.border, margin:'16px 0' }} />
            <div style={{ fontSize:11, fontWeight:800, color:C.muted, textTransform:'uppercase', letterSpacing:1, marginBottom:14 }}>
              Cost Inputs — {jobType}
            </div>
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
              <div>
                <label style={s.label}>Materials ($/SQ)</label>
                <input type="number" value={matCost} onChange={e=>setMatCost(e.target.value)}
                  placeholder="225" style={{ ...s.input }} />
              </div>
              <div>
                <label style={s.label}>Labor ($/SQ)</label>
                <input type="number" value={laborCost} onChange={e=>setLaborCost(e.target.value)}
                  placeholder="51" style={{ ...s.input }} />
              </div>
            </div>
            <div style={{ background:C.panel, borderRadius:8, padding:'10px 14px', marginBottom:14,
                          display:'flex', justifyContent:'space-between',
                          border:`1px solid ${C.border}` }}>
              <span style={{ fontSize:12, color:C.muted }}>Total Cost/SQ</span>
              <span style={{ fontWeight:800, color:C.text }}>${(parseFloat(matCost||0)+parseFloat(laborCost||0)).toFixed(2)}</span>
            </div>

            <div style={{ height:1, background:C.border, margin:'4px 0 16px' }} />
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
              <div>
                <label style={s.label}>Charge Rate ($/SQ)</label>
                <input type="number" value={ratePerSq} onChange={e=>{ setRatePerSq(e.target.value); setEstimate(null); }}
                  placeholder="460" style={{ ...s.input }} />
              </div>
              <div>
                <label style={s.label}>Target Margin (%)</label>
                <input type="number" value={targetMgn} onChange={e=>{ setTargetMgn(e.target.value); setEstimate(null); }}
                  placeholder="40" style={{ ...s.input }} />
              </div>
            </div>
            <div style={{ background:'rgba(200,151,58,.08)', borderRadius:8, padding:'10px 14px', marginBottom:16,
                          display:'flex', justifyContent:'space-between',
                          border:`1px solid rgba(200,151,58,.2)` }}>
              <span style={{ fontSize:12, color:C.muted }}>Min rate for {targetMgn}% margin</span>
              <span style={{ fontWeight:800, color:C.gold }}>${minRate()}/SQ</span>
            </div>

            <Btn label="🧮 Calculate Estimate" onClick={calculate} fullWidth size="lg" />
          </Card>

          {estimate && (
            <Card style={{ border:`2px solid ${estimate.meetsTarget?C.green:C.red}` }}>
              <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:16 }}>
                <h3 style={{ fontWeight:900, color:C.gold, margin:0 }}>📈 Results</h3>
                <span style={{ background:estimate.meetsTarget?'rgba(64,200,112,.15)':'rgba(232,64,64,.15)',
                               color:estimate.meetsTarget?C.green:C.red,
                               borderRadius:999, padding:'4px 12px', fontSize:11, fontWeight:800 }}>
                  {estimate.meetsTarget?`✓ ${targetMgn}% Target Met`:`✗ Below ${targetMgn}%`}
                </span>
              </div>
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12, marginBottom:12 }}>
                {[
                  ['Total Estimate', fmtC(estimate.total),  C.gold],
                  ['Gross Profit',   fmtC(estimate.profit), C.green],
                  ['Total Cost',     fmtC(estimate.cost),   C.text],
                  ['Margin',         `${estimate.margin.toFixed(1)}%`, estimate.meetsTarget?C.green:C.red],
                ].map(([l,v,col])=>(
                  <div key={l} style={{ background:C.panel, borderRadius:10, padding:'14px 16px' }}>
                    <div style={{ fontSize:11, color:C.muted, textTransform:'uppercase', fontWeight:700, marginBottom:6 }}>{l}</div>
                    <div style={{ fontSize:22, fontWeight:900, color:col }}>{v}</div>
                  </div>
                ))}
              </div>
              <div style={{ background:C.panel, borderRadius:10, padding:'12px 16px', marginBottom:12,
                            display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:8, textAlign:'center' }}>
                {[
                  ['Cost/SQ',   fmtC(estimate.adjCostSq), C.text],
                  ['Rate/SQ',   fmtC(estimate.adjRateSq), C.gold],
                  ['Profit/SQ', fmtC(estimate.adjRateSq - estimate.adjCostSq), estimate.meetsTarget?C.green:C.red],
                ].map(([l,v,col])=>(
                  <div key={l}>
                    <div style={{ fontSize:10, color:C.faint, textTransform:'uppercase', fontWeight:700, marginBottom:4 }}>{l}</div>
                    <div style={{ fontWeight:800, fontSize:14, color:col }}>{v}</div>
                  </div>
                ))}
              </div>
              <div style={{ fontSize:11, color:C.faint, textAlign:'center', marginBottom:14 }}>
                {estimate.sqNum} SQ · {jobType}
                {pitch ? ` · ${pitch}` : ''}{parseInt(stories)>1 ? ` · ${stories} stories` : ''}
              </div>
              <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
                <Btn label="📄 Generate & Send Estimate" onClick={()=>setShowEstimate(true)} fullWidth />
                {selLead && <Btn label="💾 Save Amount to Lead" v="outline" onClick={()=>{ updateLead({...selLead, estimatedAmt:estimate.total, jobType}); alert(`Estimate of ${fmtC(estimate.total)} saved to ${selLead.name}`); }} fullWidth />}
                {!estimate.meetsTarget && selLead && <Btn label="🚩 Mark Non-Profitable" v="danger" fullWidth onClick={()=>updateLead({...selLead,status:'Non-Profitable'})} />}
              </div>
            </Card>
          )}
        </div>
      </div>

      {/* Estimate modal — build a source object from current E&A data */}
      <EstimateModal
        open={showEstimate}
        onClose={()=>setShowEstimate(false)}
        source={selLead ? {
          ...selLead,
          sq,
          pitch,
          stories,
          estimatedAmt: estimate?.total,
        } : null}
        onSave={selLead ? d=>updateLead({...selLead, estimatedAmt:d.total, estimateData:d}) : null}
      />
    </div>
  );
}

function UsersScreen() {
  const { users, addUser, updateUser, deleteUser, user:me } = useApp();
  const [modal, setModal]   = useState(null); // 'add' | user object
  const [confirm, setConfirm] = useState(null);
  const [f, setF] = useState({ name:'',email:'',password:'',role:'member',title:'',active:true });
  const fv = (k,v) => setF(x=>({...x,[k]:v}));
  const [showPw, setShowPw] = useState(false);

  function openAdd() {
    setF({ name:'',email:'',password:'Welcome2025!',role:'member',title:'',active:true });
    setModal('add');
  }
  function openEdit(u) { setF({...u}); setModal(u); }

  async function save() {
    if (!f.name.trim() || !f.email.trim()) return;
    if (modal==='add') await addUser(f);
    else await updateUser(f);
    setModal(null);
  }

  const roleColor = r => r==='admin' ? C.gold : C.blueLt;

  return (
    <div style={{ padding:'32px 0' }}>
      <div style={{ ...s.row, marginBottom:24 }}>
        <div>
          <h1 style={s.h1}>Users</h1>
          <div style={{ fontSize:13,color:C.muted,marginTop:4 }}>{users.filter(u=>u.active!==false).length} active · Admin only</div>
        </div>
        <Btn label="+ Add User" onClick={openAdd} />
      </div>

      <div style={{ background:'rgba(200,151,58,.08)', border:`1px solid rgba(200,151,58,.25)`, borderRadius:12, padding:'14px 18px', marginBottom:20, fontSize:13, color:C.muted }}>
        🔐 New users receive a temporary password and should change it on first login. Credentials work on both the web portal and mobile app.
      </div>

      <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(360px,1fr))', gap:14 }}>
        {users.map(u => (
          <Card key={u.id} style={{ border:`1px solid ${u.active===false?C.redDim:C.border}` }}>
            <div style={{ display:'flex', gap:14, alignItems:'center', marginBottom:14 }}>
              <div style={{ width:48, height:48, borderRadius:24,
                            background:`linear-gradient(135deg,${roleColor(u.role)},${u.role==='admin'?C.amber:C.blue})`,
                            display:'flex', alignItems:'center', justifyContent:'center',
                            fontWeight:900, fontSize:16, color:C.night, flexShrink:0 }}>
                {u.avatar||u.name[0]}
              </div>
              <div style={{ flex:1 }}>
                <div style={{ fontWeight:800, fontSize:15 }}>{u.name} {u.id===me?.id&&<span style={{ fontSize:11,color:C.muted }}>(you)</span>}</div>
                <div style={{ fontSize:12, color:C.muted, marginTop:2 }}>{u.title||'—'}</div>
                <div style={{ fontSize:12, color:C.faint, marginTop:2 }}>{u.email}</div>
              </div>
              <div style={{ display:'flex', flexDirection:'column', alignItems:'flex-end', gap:6 }}>
                <span style={{ background:u.role==='admin'?'rgba(200,151,58,.15)':'rgba(58,123,213,.15)',
                               color:roleColor(u.role), borderRadius:999, padding:'3px 10px', fontSize:11, fontWeight:700, textTransform:'uppercase' }}>
                  {u.role}
                </span>
                <span style={{ background:u.active===false?'rgba(232,64,64,.15)':'rgba(64,200,112,.15)',
                               color:u.active===false?C.red:C.green, borderRadius:999, padding:'3px 10px', fontSize:10, fontWeight:700 }}>
                  {u.active===false?'Inactive':'Active'}
                </span>
              </div>
            </div>
            <div style={{ display:'flex', gap:8 }}>
              <Btn label="✏️ Edit" v="outline" size="sm" onClick={()=>openEdit(u)} />
              {u.active!==false
                ? <Btn label="Deactivate" v="danger" size="sm"
                    onClick={()=>setConfirm({ title:'Deactivate User', message:`Remove ${u.name}'s access? They won't be able to log in.`, danger:true, onConfirm:()=>{ updateUser({...u,active:false}); setConfirm(null); } })} />
                : <Btn label="Reactivate" v="teal" size="sm"
                    onClick={()=>updateUser({...u,active:true})} />
              }
              {u.id!==me?.id && (
                <Btn label="🗑" v="danger" size="sm"
                  onClick={()=>setConfirm({ title:'Delete User', message:`Permanently delete ${u.name}?`, danger:true, onConfirm:()=>{ deleteUser(u.id); setConfirm(null); } })} />
              )}
            </div>
          </Card>
        ))}
      </div>

      {/* Add/Edit Modal */}
      <Modal open={!!modal} onClose={()=>setModal(null)} title={modal==='add'?'Add New User':'Edit User'} width={500}>
        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'0 20px' }}>
          <Field label="Full Name *"  value={f.name}  onChange={v=>fv('name',v)}  required />
          <Field label="Job Title"    value={f.title} onChange={v=>fv('title',v)} placeholder="e.g. Project Manager" />
        </div>
        <Field label="Email *" value={f.email} onChange={v=>fv('email',v)} type="email" required />

        {/* Password field */}
        <div style={{ marginBottom:16 }}>
          <label style={s.label}>
            {modal==='add' ? 'Temporary Password *' : 'Password (leave unchanged to keep current)'}
          </label>
          <div style={{ position:'relative' }}>
            <input type={showPw?'text':'password'} value={f.password||''}
              onChange={e=>fv('password',e.target.value)}
              style={{ ...s.input, paddingRight:52 }} />
            <button onClick={()=>setShowPw(!showPw)}
              style={{ position:'absolute', right:12, top:'50%', transform:'translateY(-50%)',
                       background:'none', border:'none', color:C.muted, cursor:'pointer', fontSize:14 }}>
              {showPw?'🙈':'👁️'}
            </button>
          </div>
          {modal==='add' && <div style={{ fontSize:11, color:C.faint, marginTop:6 }}>User should change this on first login.</div>}
        </div>

        {/* Role */}
        <div style={{ marginBottom:16 }}>
          <label style={s.label}>Role</label>
          <div style={{ display:'flex', gap:10 }}>
            {['admin','member'].map(r => (
              <button key={r} onClick={()=>fv('role',r)}
                style={{ flex:1, background:f.role===r?(r==='admin'?'rgba(200,151,58,.15)':'rgba(58,123,213,.15)'):'rgba(255,255,255,.04)',
                         border:`1.5px solid ${f.role===r?roleColor(r):C.border}`,
                         borderRadius:10, padding:'12px 0', cursor:'pointer',
                         color:f.role===r?roleColor(r):C.muted, fontWeight:700, fontSize:13 }}>
                {r==='admin'?'🔐 Admin':'👷 Team Member'}
              </button>
            ))}
          </div>
          <div style={{ fontSize:11, color:C.faint, marginTop:8 }}>
            {f.role==='admin' ? 'Full access: jobs, leads, financials, payments, crew, users.' : 'Field access: jobs, leads, schedule, documents. No financials.'}
          </div>
        </div>

        <Toggle label="Active account" checked={f.active!==false} onChange={v=>fv('active',v)} />

        <div style={{ display:'flex', gap:12, justifyContent:'flex-end', marginTop:16 }}>
          <Btn label="Cancel" v="ghost" onClick={()=>setModal(null)} />
          <Btn label={modal==='add'?'✓ Add User':'💾 Save Changes'} onClick={save} />
        </div>
      </Modal>

      <Confirm {...(confirm||{})} open={!!confirm} onCancel={()=>setConfirm(null)} />
    </div>
  );
}


function ProfScreen() {
  const { user, logout } = useApp();
  return (
    <div style={{ padding:'32px 0', maxWidth:480 }}>
      <h1 style={{ ...s.h1, marginBottom:24 }}>My Profile</h1>
      <Card style={{ marginBottom:16 }}>
        <div style={{ display:'flex', gap:16, alignItems:'center', marginBottom:20 }}>
          <div style={{ width:64, height:64, borderRadius:32, background:`linear-gradient(135deg,${C.gold},${C.amber})`,
                        display:'flex', alignItems:'center', justifyContent:'center', fontWeight:900, fontSize:22, color:C.night }}>
            {user?.avatar}
          </div>
          <div>
            <div style={{ fontWeight:900, fontSize:20 }}>{user?.name}</div>
            <div style={{ color:C.muted, marginTop:4 }}>{user?.title}</div>
            <div style={{ fontSize:12, color:user?.role==='admin'?C.gold:C.blueLt, fontWeight:700, marginTop:6, textTransform:'uppercase' }}>{user?.role}</div>
          </div>
        </div>
        <div style={{ height:1, background:C.border, marginBottom:16 }} />
        <div style={{ fontSize:13, color:C.muted }}>{user?.email}</div>
      </Card>
      <Btn label="Sign Out" v="danger" onClick={logout} fullWidth size="lg" />
    </div>
  );
}

// ─────────────────────────────────────────────────────────
// LOGIN
// ─────────────────────────────────────────────────────────
function LoginScreen() {
  const { login } = useApp();
  const [email, setEmail] = useState('');
  const [pass,  setPass]  = useState('');
  const [err,   setErr]   = useState('');
  const [busy,  setBusy]  = useState(false);

  async function go(e) {
    e.preventDefault();
    setBusy(true); setErr('');
    try { login(email, pass); }
    catch(ex) { setErr(ex.message); }
    setBusy(false);
  }

  return (
    <div style={{ minHeight:'100vh', background:`linear-gradient(150deg,${C.night} 0%,#111828 100%)`,
                  display:'flex', alignItems:'center', justifyContent:'center', padding:20 }}>
      <div style={{ width:'100%', maxWidth:420 }}>
        {/* Logo */}
        <div style={{ textAlign:'center', marginBottom:36 }}>
          <img src="/logo.png" alt="Capricorn Capital Roofing"
            style={{ width:140, height:'auto', margin:'0 auto 16px', display:'block', filter:'drop-shadow(0 4px 20px rgba(200,151,58,.3))' }} />
          <div style={{ fontSize:13, color:C.muted, marginTop:6 }}>Internal Portal</div>
        </div>

        {/* Form */}
        <div style={{ background:C.surface, borderRadius:20, padding:'32px 36px', border:`1px solid ${C.borderB}`,
                      boxShadow:'0 32px 80px rgba(0,0,0,.5)' }}>
          <form onSubmit={go}>
            <div style={{ marginBottom:20 }}>
              <label style={s.label}>Email</label>
              <input type="email" value={email} onChange={e=>setEmail(e.target.value)}
                placeholder="your@email.com" autoComplete="email"
                style={{ ...s.input, borderColor:err?C.red:C.border }} required />
            </div>
            <div style={{ marginBottom:24 }}>
              <label style={s.label}>Password</label>
              <input type="password" value={pass} onChange={e=>setPass(e.target.value)}
                placeholder="••••••••" autoComplete="current-password"
                style={{ ...s.input, borderColor:err?C.red:C.border }} required />
            </div>
            {err && <div style={{ background:C.redDim, color:C.red, borderRadius:8, padding:'10px 14px', fontSize:13, marginBottom:20, border:`1px solid rgba(232,64,64,.3)` }}>{err}</div>}
            <button type="submit" disabled={busy}
              style={{ width:'100%', background:C.gold, color:C.night, border:'none', borderRadius:10, padding:'13px 0',
                       fontSize:15, fontWeight:800, cursor:busy?'not-allowed':'pointer', opacity:busy?.7:1, transition:'all .15s' }}>
              {busy ? 'Signing in…' : 'Sign In →'}
            </button>
          </form>
          <div style={{ marginTop:20, textAlign:'center', borderTop:`1px solid ${C.border}`, paddingTop:20 }}>
            <div style={{ fontSize:11, color:C.faint, marginBottom:10 }}>Internal access only · Invite required</div>
            <div style={{ fontSize:11, color:C.faint }}>
              Contact <a href="mailto:info@capricorncapitalroofing.com" style={{ color:C.gold }}>info@capricorncapitalroofing.com</a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────
// MAIN APP SHELL
// ─────────────────────────────────────────────────────────
function MainApp() {
  const { user, loading, toast } = useApp();
  const [active, setActive] = useState('dash');
  const [stack,  setStack]  = useState([]);

  if (loading) return (
    <div style={{ minHeight:'100vh', background:C.night, display:'flex', alignItems:'center', justifyContent:'center' }}>
      <div style={{ textAlign:'center' }}>
        <div style={{ fontSize:48, marginBottom:16 }}>🏠</div>
        <div style={{ color:C.gold, fontWeight:700 }}>Loading CCRoofing…</div>
      </div>
    </div>
  );

  if (!user) return <LoginScreen />;

  function push(screen, params={}) {
    if (screen==='dash'||screen==='jobs'||screen==='leads'||screen==='sched'||screen==='pay'||screen==='crew'||screen==='users'||screen==='prof'||screen==='pa') {
      setActive(screen); setStack([]); return;
    }
    setStack(s => [...s, { screen, params }]);
  }
  function pop() { setStack(s => s.slice(0,-1)); }

  // Render stacked screen
  if (stack.length > 0) {
    const { screen, params } = stack[stack.length-1];
    const screenEl =
      screen==='jobDetail'   ? <JobDetailScreen  params={params} onBack={pop} push={push} /> :
      screen==='leadDetail'  ? <LeadDetailScreen params={params} onBack={pop} /> :
      screen==='ea'          ? <EAScreen onBack={pop} /> :
      null;
    if (screenEl) return (
      <div style={{ ...s.page, display:'flex' }}>
        <Sidebar active={active} setActive={(k)=>{ setActive(k); setStack([]); }} push={push} />
        <div style={{ flex:1, minHeight:'100vh', overflowY:'auto' }}>
          <div style={{ maxWidth:1100, margin:'0 auto', padding:'0 32px' }}>
            {screenEl}
          </div>
        </div>
        <Toast toast={toast} />
      </div>
    );
  }

  const screens = {
    dash:  <DashScreen  push={push} />,
    jobs:  <JobsScreen  push={push} />,
    leads: <LeadsScreen push={push} />,
    sched: <SchedScreen />,
    pa:    <PropertyAnalysisScreen />,
    pay:   <PayScreen />,
    crew:  <CrewScreen />,
    users: <UsersScreen />,
    prof:  <ProfScreen />,
  };

  return (
    <div style={{ ...s.page, display:'flex' }}>
      <Sidebar active={active} setActive={setActive} push={push} />
      <div style={{ flex:1, minHeight:'100vh', overflowY:'auto' }}>
        <div style={{ maxWidth:1100, margin:'0 auto', padding:'0 32px' }}>
          {screens[active] || screens.dash}
        </div>
      </div>
      <Toast toast={toast} />
      <style>{`
        @keyframes slideUp { from { transform:translateY(20px); opacity:0; } to { transform:translateY(0); opacity:1; } }
        * { box-sizing: border-box; }
        input:focus, textarea:focus, select:focus { border-color: #c8973a !important; outline: none; }
        ::-webkit-scrollbar { width: 6px; height: 6px; }
        ::-webkit-scrollbar-track { background: #111318; }
        ::-webkit-scrollbar-thumb { background: #2a2f3d; border-radius: 3px; }
        ::-webkit-scrollbar-thumb:hover { background: #363d52; }
      `}</style>
    </div>
  );
}

export default function App() {
  return <AppProvider><MainApp /></AppProvider>;
}
